import * as i0 from '@angular/core';
import { InjectionToken, Injectable, APP_INITIALIZER, Injector, NgModule } from '@angular/core';
import { map, distinctUntilChanged, filter, catchError, switchMap, mapTo, takeUntil, tap } from 'rxjs/operators';
import compare from 'just-compare';
import { BehaviorSubject, Subject, throwError, of, timer } from 'rxjs';
import * as i1 from '@abp/ng.core';
import { mapEnumToOptions as mapEnumToOptions$1 } from '@abp/ng.core';
import * as i2 from '@angular/common/http';

const CORE_OPTIONS = new InjectionToken('CORE_OPTIONS');
function coreOptionsFactory({ ...options }) {
    return {
        ...options,
    };
}

const LOADER_DELAY = new InjectionToken('LOADER_DELAY');

function noop() {
    // tslint:disable-next-line: only-arrow-functions
    const fn = function () { };
    return fn;
}
function isUndefinedOrEmptyString(value) {
    return value === undefined || value === '';
}
function isNullOrUndefined(obj) {
    return obj === null || obj === undefined;
}
function exists(obj) {
    return !isNullOrUndefined(obj);
}
function isObject(obj) {
    return obj instanceof Object;
}
function isArray(obj) {
    return Array.isArray(obj);
}
function isObjectAndNotArray(obj) {
    return isObject(obj) && !isArray(obj);
}
function isNode(obj) {
    return obj instanceof Node;
}
function isObjectAndNotArrayNotNode(obj) {
    return isObjectAndNotArray(obj) && !isNode(obj);
}

function deepMerge(target, source) {
    if (isObjectAndNotArrayNotNode(target) && isObjectAndNotArrayNotNode(source)) {
        return deepMergeRecursively(target, source);
    }
    else if (isNullOrUndefined(target) && isNullOrUndefined(source)) {
        return {};
    }
    else {
        return exists(source) ? source : target;
    }
}
function deepMergeRecursively(target, source) {
    const shouldNotRecurse = isNullOrUndefined(target) ||
        isNullOrUndefined(source) || // at least one not defined
        isArray(target) ||
        isArray(source) || // at least one array
        !isObject(target) ||
        !isObject(source) || // at least one not an object
        isNode(target) ||
        isNode(source); // at least one node
    /**
     * if we will not recurse any further,
     * we will prioritize source if it is a defined value.
     */
    if (shouldNotRecurse) {
        return exists(source) ? source : target;
    }
    const keysOfTarget = Object.keys(target);
    const keysOfSource = Object.keys(source);
    const uniqueKeys = new Set(keysOfTarget.concat(keysOfSource));
    return [...uniqueKeys].reduce((retVal, key) => {
        retVal[key] = deepMergeRecursively(target[key], source[key]);
        return retVal;
    }, {});
}

class InternalStore {
    get state() {
        return this.state$.value;
    }
    constructor(initialState) {
        this.initialState = initialState;
        this.state$ = new BehaviorSubject(this.initialState);
        this.update$ = new Subject();
        this.sliceState = (selector, compareFn = compare) => this.state$.pipe(map(selector), distinctUntilChanged(compareFn));
        this.sliceUpdate = (selector, filterFn = (x) => x !== undefined) => this.update$.pipe(map(selector), filter(filterFn));
    }
    patch(state) {
        let patchedState = state;
        if (typeof state === 'object' && !Array.isArray(state)) {
            patchedState = { ...this.state, ...state };
        }
        this.state$.next(patchedState);
        this.update$.next(patchedState);
    }
    deepPatch(state) {
        this.state$.next(deepMerge(this.state, state));
        this.update$.next(state);
    }
    set(state) {
        this.state$.next(state);
        this.update$.next(state);
    }
    reset() {
        this.set(this.initialState);
    }
}

const mapToApiUrl = (key) => (apis) => ((key && apis[key]) || apis.default).url || apis.default.url;
const mapToIssuer = (issuer) => {
    if (!issuer) {
        return issuer;
    }
    return issuer.endsWith('/') ? issuer : issuer + '/';
};
class EnvironmentService {
    constructor() {
        this.store = new InternalStore({});
    }
    get createOnUpdateStream() {
        return this.store.sliceUpdate;
    }
    getEnvironment$() {
        return this.store.sliceState(state => state);
    }
    getEnvironment() {
        return this.store.state;
    }
    getApiUrl(key) {
        return mapToApiUrl(key)(this.store.state?.apis);
    }
    getApiUrl$(key) {
        return this.store.sliceState(state => state.apis).pipe(map(mapToApiUrl(key)));
    }
    setState(environment) {
        this.store.set(environment);
    }
    getIssuer() {
        const issuer = this.store.state?.oAuthConfig?.issuer;
        return mapToIssuer(issuer);
    }
    getIssuer$() {
        return this.store.sliceState(state => state?.oAuthConfig?.issuer).pipe(map(mapToIssuer));
    }
}
EnvironmentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: EnvironmentService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
EnvironmentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: EnvironmentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: EnvironmentService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

function getInitialData(injector) {
    const fn = async () => {
        const environmentService = injector.get(EnvironmentService);
        const options = injector.get(CORE_OPTIONS);
        environmentService.setState(options.environment);
    };
    return fn;
}

class TajerApiModule {
    static forRoot(options = {}) {
        return {
            ngModule: TajerApiModule,
            providers: [
                {
                    provide: 'CORE_OPTIONS',
                    useValue: options,
                },
                {
                    provide: CORE_OPTIONS,
                    useFactory: coreOptionsFactory,
                    deps: ['CORE_OPTIONS'],
                },
                {
                    provide: APP_INITIALIZER,
                    multi: true,
                    deps: [Injector],
                    useFactory: getInitialData,
                },
            ]
        };
    }
}
TajerApiModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TajerApiModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TajerApiModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "15.2.10", ngImport: i0, type: TajerApiModule });
TajerApiModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TajerApiModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TajerApiModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [],
                    imports: [],
                    exports: []
                }]
        }] });

class AccountService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.getAvailableTenants = (email, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/account/available-tenants',
            params: { email },
        }, { apiName: this.apiName, ...config });
        this.postCompleteRegisterTenant = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/account/complete-register-tenant',
        }, { apiName: this.apiName, ...config });
        this.postConfirmUserEmail = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/account/confirm-user-email',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.postRegisterTenant = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/account/register-tenant',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.resetPassword = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/account/reset-password',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.sendPasswordResetCode = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/account/send-password-reset-code',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.sendUserConfirmationEmail = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/account/send-user-confirmation-email',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
AccountService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AccountService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
AccountService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AccountService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AccountService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class BranchService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/branch',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/branch/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/branch/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/branch/all',
            params: { onlyChildren: input.onlyChildren, disabled: input.disabled },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/branch',
            params: { onlyChildren: input.onlyChildren, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getSubBranchesToMainBranchByBranchId = (branchId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/branch/sub-branches-to-main-branch/${branchId}`,
        }, { apiName: this.apiName, ...config });
        this.putDefault = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/branch/${id}/default`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/branch/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/branch/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/branch/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
BranchService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: BranchService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
BranchService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: BranchService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: BranchService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class BrandService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.checkSafeSlug = (slug, id, config) => this.restService.request({
            method: 'POST',
            responseType: 'text',
            url: `/api/app/brand/${id}/check-safe-slug`,
            params: { slug },
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/brand',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/brand/${id}`,
        }, { apiName: this.apiName, ...config });
        this.deleteTranslation = (id, language, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/brand/${id}/translation`,
            params: { language },
        }, { apiName: this.apiName, ...config });
        this.enableBranchInAllBrands = (branchId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/brand/enable-branch-in-all-brands/${branchId}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/brand/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/brand/all',
            params: { sorting: input.sorting, disabled: input.disabled, outletId: input.outletId, branchId: input.branchId, ids: input.ids },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/brand',
            params: { disabled: input.disabled, ids: input.ids, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMarketplace = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/brand/marketplace',
        }, { apiName: this.apiName, ...config });
        this.getTranslations = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/brand/${id}/translations`,
        }, { apiName: this.apiName, ...config });
        this.migrateData = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/brand/migrate-data',
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, branchId, outletId, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/brand/${id}/disable`,
            params: { branchId, outletId },
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, branchId, outletId, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/brand/${id}/enable`,
            params: { branchId, outletId },
        }, { apiName: this.apiName, ...config });
        this.putReorderBrandByInput = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/brand/reorder-brand',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putTranslations = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/brand/${id}/translations`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/brand/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
BrandService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: BrandService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
BrandService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: BrandService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: BrandService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class CategoryService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.checkSafeSlug = (slug, id, config) => this.restService.request({
            method: 'POST',
            responseType: 'text',
            url: `/api/app/category/${id}/check-safe-slug`,
            params: { slug },
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/category',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/category/${id}`,
        }, { apiName: this.apiName, ...config });
        this.deleteTranslation = (id, language, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/category/${id}/translation`,
            params: { language },
        }, { apiName: this.apiName, ...config });
        this.enableBranchInAllCategories = (branchId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/category/enable-branch-in-all-categories/${branchId}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/category/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/category/all',
            params: { sorting: input.sorting, parentId: input.parentId, disabled: input.disabled, outletId: input.outletId, branchId: input.branchId, ids: input.ids },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/category',
            params: { parentId: input.parentId, disabled: input.disabled, ids: input.ids, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMarketplace = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/category/marketplace',
        }, { apiName: this.apiName, ...config });
        this.getTranslations = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/category/${id}/translations`,
        }, { apiName: this.apiName, ...config });
        this.migrateData = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/category/migrate-data',
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, branchId, outletId, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/category/${id}/disable`,
            params: { branchId, outletId },
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, branchId, outletId, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/category/${id}/enable`,
            params: { branchId, outletId },
        }, { apiName: this.apiName, ...config });
        this.putReorderCategory = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/category/reorder-category',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putTranslations = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/category/${id}/translations`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/category/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
CategoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CategoryService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CategoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CategoryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CategoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var InventoryType;
(function (InventoryType) {
    InventoryType[InventoryType["NonInventory"] = 0] = "NonInventory";
    InventoryType[InventoryType["InStock"] = 1] = "InStock";
    InventoryType[InventoryType["NoStock"] = 2] = "NoStock";
    InventoryType[InventoryType["LowStock"] = 3] = "LowStock";
})(InventoryType || (InventoryType = {}));
const inventoryTypeOptions = mapEnumToOptions$1(InventoryType);

class ItemPosService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item-pos',
            params: { branchId: input.branchId, warehouseId: input.warehouseId, currencyId: input.currencyId, search: input.search, barcode: input.barcode, categoryId: input.categoryId, brandId: input.brandId, priceListId: input.priceListId, salesChannel: input.salesChannel, customerId: input.customerId, hideOutOfStock: input.hideOutOfStock, belongToMarketplaceCategory: input.belongToMarketplaceCategory, belongToMarketplaceBrand: input.belongToMarketplaceBrand, languagePosition: input.languagePosition, fromPrice: input.fromPrice, toPrice: input.toPrice, latestOnly: input.latestOnly, featuredOnly: input.featuredOnly, offersOnly: input.offersOnly, ids: input.ids, linkedWithMarketplace: input.linkedWithMarketplace, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
    }
}
ItemPosService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemPosService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ItemPosService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemPosService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemPosService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ItemRedisService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.getRunInBackgroundJob = (config) => this.restService.request({
            method: 'GET',
            responseType: 'text',
            url: '/api/app/item-redis/run-in-background-job',
        }, { apiName: this.apiName, ...config });
        this.postReindex = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/item-redis/reindex',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putRunInBackgroundJob = (enable, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/item-redis/run-in-background-job',
            params: { enable },
        }, { apiName: this.apiName, ...config });
    }
}
ItemRedisService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemRedisService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ItemRedisService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemRedisService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemRedisService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ItemReviewHelpfulnessService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/item-review-helpfulness/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item-review-helpfulness',
            params: { itemReviewId: input.itemReviewId, customerId: input.customerId, marketplaceId: input.marketplaceId, wasHelpful: input.wasHelpful, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.put = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/item-review-helpfulness',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ItemReviewHelpfulnessService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemReviewHelpfulnessService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ItemReviewHelpfulnessService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemReviewHelpfulnessService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemReviewHelpfulnessService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ItemReviewService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/item-review/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/item-review/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item-review',
            params: { filter: input.filter, isPublished: input.isPublished, orderId: input.orderId, customerId: input.customerId, itemDetailId: input.itemDetailId, itemId: input.itemId, minRating: input.minRating, maxRating: input.maxRating, fromDate: input.fromDate, toDate: input.toDate, marketplaceId: input.marketplaceId, isMarketplacePublished: input.isMarketplacePublished, relatedStoreIds: input.relatedStoreIds, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.publish = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/item-review/publish',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.reply = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/item-review/reply',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ItemReviewService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemReviewService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ItemReviewService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemReviewService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemReviewService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ItemStoreReviewService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/item-store-review',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createMarketplace = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/item-store-review/marketplace',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createMarketplaceOrder = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/item-store-review/marketplace-order',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/item-store-review/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/item-store-review/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAverage = (itemId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/item-store-review/average/${itemId}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item-store-review',
            params: { filter: input.filter, itemDetailId: input.itemDetailId, orderId: input.orderId, customerId: input.customerId, itemId: input.itemId, marketplaceId: input.marketplaceId, minRating: input.minRating, maxRating: input.maxRating, fromDate: input.fromDate, toDate: input.toDate, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getListMarketplace = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item-store-review/marketplace',
            params: { filter: input.filter, itemDetailId: input.itemDetailId, orderId: input.orderId, customerId: input.customerId, itemId: input.itemId, marketplaceId: input.marketplaceId, minRating: input.minRating, maxRating: input.maxRating, fromDate: input.fromDate, toDate: input.toDate, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMarketplace = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/item-store-review/${id}/marketplace`,
        }, { apiName: this.apiName, ...config });
        this.order = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/item-store-review/order',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/item-store-review/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateMarketplace = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/item-store-review/${id}/marketplace`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ItemStoreReviewService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemStoreReviewService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ItemStoreReviewService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemStoreReviewService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemStoreReviewService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ItemStoreService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.getDetails = (id, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/item-store/${id}/details`,
            params: { currencyId: input.currencyId, languagePosition: input.languagePosition, warehouseId: input.warehouseId, priceListId: input.priceListId, customerId: input.customerId, branchId: input.branchId, hideOutOfStock: input.hideOutOfStock, salesChannel: input.salesChannel, outletId: input.outletId },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item-store',
            params: { branchId: input.branchId, warehouseId: input.warehouseId, currencyId: input.currencyId, search: input.search, barcode: input.barcode, categoryId: input.categoryId, brandId: input.brandId, priceListId: input.priceListId, salesChannel: input.salesChannel, customerId: input.customerId, hideOutOfStock: input.hideOutOfStock, belongToMarketplaceCategory: input.belongToMarketplaceCategory, belongToMarketplaceBrand: input.belongToMarketplaceBrand, languagePosition: input.languagePosition, fromPrice: input.fromPrice, toPrice: input.toPrice, latestOnly: input.latestOnly, featuredOnly: input.featuredOnly, offersOnly: input.offersOnly, ids: input.ids, linkedWithMarketplace: input.linkedWithMarketplace, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMarketplace = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item-store/marketplace',
            params: { storesIds: input.storesIds, branchId: input.branchId, warehouseId: input.warehouseId, currencyId: input.currencyId, search: input.search, barcode: input.barcode, categoryId: input.categoryId, brandId: input.brandId, priceListId: input.priceListId, salesChannel: input.salesChannel, customerId: input.customerId, hideOutOfStock: input.hideOutOfStock, belongToMarketplaceCategory: input.belongToMarketplaceCategory, belongToMarketplaceBrand: input.belongToMarketplaceBrand, languagePosition: input.languagePosition, fromPrice: input.fromPrice, toPrice: input.toPrice, latestOnly: input.latestOnly, featuredOnly: input.featuredOnly, offersOnly: input.offersOnly, ids: input.ids, linkedWithMarketplace: input.linkedWithMarketplace, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMarketplaceDetails = (id, tenantId, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/item-store-details/${id}/marketplace/${tenantId}`,
            params: { currencyId: input.currencyId, languagePosition: input.languagePosition, warehouseId: input.warehouseId, priceListId: input.priceListId, customerId: input.customerId, branchId: input.branchId, hideOutOfStock: input.hideOutOfStock, salesChannel: input.salesChannel, outletId: input.outletId },
        }, { apiName: this.apiName, ...config });
        this.getMarketplaceFilters = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item-store/marketplace-filters',
            params: { storesIds: input.storesIds, search: input.search, categoryId: input.categoryId, currencyId: input.currencyId, brandId: input.brandId, outletId: input.outletId, minItemPrice: input.minItemPrice, maxItemPrice: input.maxItemPrice },
        }, { apiName: this.apiName, ...config });
        this.getMarketplaceMostOrdered = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item-store/marketplace-most-ordered',
            params: { storesIds: input.storesIds, outletId: input.outletId, currencyId: input.currencyId, languagePosition: input.languagePosition, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMarketplaceSlug = (slug, tenantId, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/item-store/${slug}/marketplace/${tenantId}`,
            params: { currencyId: input.currencyId, languagePosition: input.languagePosition, warehouseId: input.warehouseId, priceListId: input.priceListId, customerId: input.customerId, branchId: input.branchId, hideOutOfStock: input.hideOutOfStock, salesChannel: input.salesChannel, outletId: input.outletId },
        }, { apiName: this.apiName, ...config });
        this.getMostOrdered = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item-store/most-ordered',
            params: { outletId: input.outletId, currencyId: input.currencyId, languagePosition: input.languagePosition, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getOldStorefront = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item/item-store',
            params: { branchId: input.branchId, warehouseId: input.warehouseId, currencyId: input.currencyId, search: input.search, barcode: input.barcode, categoryId: input.categoryId, brandId: input.brandId, priceListId: input.priceListId, salesChannel: input.salesChannel, customerId: input.customerId, hideOutOfStock: input.hideOutOfStock, belongToMarketplaceCategory: input.belongToMarketplaceCategory, belongToMarketplaceBrand: input.belongToMarketplaceBrand, languagePosition: input.languagePosition, fromPrice: input.fromPrice, toPrice: input.toPrice, latestOnly: input.latestOnly, featuredOnly: input.featuredOnly, offersOnly: input.offersOnly, ids: input.ids, linkedWithMarketplace: input.linkedWithMarketplace, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getSlug = (slug, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/item-store/${slug}`,
            params: { currencyId: input.currencyId, languagePosition: input.languagePosition, warehouseId: input.warehouseId, priceListId: input.priceListId, customerId: input.customerId, branchId: input.branchId, hideOutOfStock: input.hideOutOfStock, salesChannel: input.salesChannel, outletId: input.outletId },
        }, { apiName: this.apiName, ...config });
    }
}
ItemStoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemStoreService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ItemStoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemStoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemStoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ItemService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.checkItemHasOrder = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/item/${id}/check-item-has-order`,
        }, { apiName: this.apiName, ...config });
        this.checkItemsCount = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/item/check-items-count',
        }, { apiName: this.apiName, ...config });
        this.checkSafeSlug = (slug, id, config) => this.restService.request({
            method: 'POST',
            responseType: 'text',
            url: `/api/app/item/${id}/check-safe-slug`,
            params: { slug },
        }, { apiName: this.apiName, ...config });
        this.copy = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/item/${id}/copy`,
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/item',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/item/${id}`,
        }, { apiName: this.apiName, ...config });
        this.enableBranchInAllItems = (branchId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/item/enable-branch-in-all-items/${branchId}`,
        }, { apiName: this.apiName, ...config });
        this.enableWarehouseInAllItems = (warehouseId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/item/enable-warehouse-in-all-items/${warehouseId}`,
        }, { apiName: this.apiName, ...config });
        this.getCheckItemDetail = (sku, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item/check-item-detail',
            params: { sku },
        }, { apiName: this.apiName, ...config });
        this.getExport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item/export',
            params: { isTemplateOnly: input.isTemplateOnly, stockOnly: input.stockOnly, priceOnly: input.priceOnly, search: input.search, barcode: input.barcode, itemId: input.itemId, categoryId: input.categoryId, brandId: input.brandId, outletId: input.outletId, branchId: input.branchId, warehouseId: input.warehouseId, hasImages: input.hasImages, hasBarcode: input.hasBarcode, hasMaxOrderQuantity: input.hasMaxOrderQuantity, hasMinOrderQuantity: input.hasMinOrderQuantity, disabled: input.disabled, latestOnly: input.latestOnly, featuredOnly: input.featuredOnly, offersOnly: input.offersOnly, filtersArray: input.filtersArray, filters: input.filters, fromPrice: input.fromPrice, toPrice: input.toPrice, stockTracked: input.stockTracked, noFilterDetail: input.noFilterDetail, salesChannel: input.salesChannel, taxId: input.taxId, hasNoTax: input.hasNoTax, inventoryType: input.inventoryType, deliveryMethodId: input.deliveryMethodId, mainSupplierId: input.mainSupplierId, alternativeSupplierId: input.alternativeSupplierId, hideOutOfStock: input.hideOutOfStock, ids: input.ids, linkedWithMarketplace: input.linkedWithMarketplace, verified: input.verified, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getFilters = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item/filters',
            params: { storesIds: input.storesIds, search: input.search, categoryId: input.categoryId, currencyId: input.currencyId, brandId: input.brandId, outletId: input.outletId, minItemPrice: input.minItemPrice, maxItemPrice: input.maxItemPrice },
        }, { apiName: this.apiName, ...config });
        this.getItemPriceHistory = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item/item-price-history',
            params: { itemId: input.itemId, warehouseId: input.warehouseId, fromDate: input.fromDate, toDate: input.toDate, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item',
            params: { search: input.search, barcode: input.barcode, itemId: input.itemId, categoryId: input.categoryId, brandId: input.brandId, outletId: input.outletId, branchId: input.branchId, warehouseId: input.warehouseId, hasImages: input.hasImages, hasBarcode: input.hasBarcode, hasMaxOrderQuantity: input.hasMaxOrderQuantity, hasMinOrderQuantity: input.hasMinOrderQuantity, disabled: input.disabled, latestOnly: input.latestOnly, featuredOnly: input.featuredOnly, offersOnly: input.offersOnly, filtersArray: input.filtersArray, filters: input.filters, fromPrice: input.fromPrice, toPrice: input.toPrice, stockTracked: input.stockTracked, noFilterDetail: input.noFilterDetail, salesChannel: input.salesChannel, taxId: input.taxId, hasNoTax: input.hasNoTax, inventoryType: input.inventoryType, deliveryMethodId: input.deliveryMethodId, mainSupplierId: input.mainSupplierId, alternativeSupplierId: input.alternativeSupplierId, hideOutOfStock: input.hideOutOfStock, ids: input.ids, linkedWithMarketplace: input.linkedWithMarketplace, verified: input.verified, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getListV2 = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item/v2',
            params: { search: input.search, barcode: input.barcode, itemId: input.itemId, categoryId: input.categoryId, brandId: input.brandId, outletId: input.outletId, branchId: input.branchId, warehouseId: input.warehouseId, hasImages: input.hasImages, hasBarcode: input.hasBarcode, hasMaxOrderQuantity: input.hasMaxOrderQuantity, hasMinOrderQuantity: input.hasMinOrderQuantity, disabled: input.disabled, latestOnly: input.latestOnly, featuredOnly: input.featuredOnly, offersOnly: input.offersOnly, filtersArray: input.filtersArray, filters: input.filters, fromPrice: input.fromPrice, toPrice: input.toPrice, stockTracked: input.stockTracked, noFilterDetail: input.noFilterDetail, salesChannel: input.salesChannel, taxId: input.taxId, hasNoTax: input.hasNoTax, inventoryType: input.inventoryType, deliveryMethodId: input.deliveryMethodId, mainSupplierId: input.mainSupplierId, alternativeSupplierId: input.alternativeSupplierId, hideOutOfStock: input.hideOutOfStock, ids: input.ids, linkedWithMarketplace: input.linkedWithMarketplace, verified: input.verified, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMarketplace = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item/marketplace',
            params: { storesIds: input.storesIds, search: input.search, barcode: input.barcode, itemId: input.itemId, categoryId: input.categoryId, brandId: input.brandId, outletId: input.outletId, branchId: input.branchId, warehouseId: input.warehouseId, hasImages: input.hasImages, hasBarcode: input.hasBarcode, hasMaxOrderQuantity: input.hasMaxOrderQuantity, hasMinOrderQuantity: input.hasMinOrderQuantity, disabled: input.disabled, latestOnly: input.latestOnly, featuredOnly: input.featuredOnly, offersOnly: input.offersOnly, filtersArray: input.filtersArray, filters: input.filters, fromPrice: input.fromPrice, toPrice: input.toPrice, stockTracked: input.stockTracked, noFilterDetail: input.noFilterDetail, salesChannel: input.salesChannel, taxId: input.taxId, hasNoTax: input.hasNoTax, inventoryType: input.inventoryType, deliveryMethodId: input.deliveryMethodId, mainSupplierId: input.mainSupplierId, alternativeSupplierId: input.alternativeSupplierId, hideOutOfStock: input.hideOutOfStock, ids: input.ids, linkedWithMarketplace: input.linkedWithMarketplace, verified: input.verified, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getPriceListPrices = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item/price-list-prices',
            params: { itemId: input.itemId, priceListId: input.priceListId },
        }, { apiName: this.apiName, ...config });
        this.getPriceListStorePrices = (itemId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/item/price-list-store-prices/${itemId}`,
        }, { apiName: this.apiName, ...config });
        this.getSupplierItemsStoreList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/item/supplier-items-store-list',
            params: { mainSupplierId: input.mainSupplierId, alternativeSupplierId: input.alternativeSupplierId, warehouseId: input.warehouseId, branchId: input.branchId, inventoryType: input.inventoryType },
        }, { apiName: this.apiName, ...config });
        this.getV2 = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/item/${id}`,
        }, { apiName: this.apiName, ...config });
        this.newSku = (config) => this.restService.request({
            method: 'GET',
            responseType: 'text',
            url: '/api/app/item/new-sku',
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, branchId, outletId, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/item/${id}/disable`,
            params: { branchId, outletId },
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, branchId, outletId, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/item/${id}/enable`,
            params: { branchId, outletId },
        }, { apiName: this.apiName, ...config });
        this.putUnverified = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/item/${id}/unverified`,
        }, { apiName: this.apiName, ...config });
        this.putVerified = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/item/${id}/verified`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/item/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateBulkPrices = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/item/bulk-prices',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updatePrice = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/item/price',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updatePriceListPrices = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/item/price-list-prices',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ItemService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ItemService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ItemService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ModifierListService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/modifier-list',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/modifier-list/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/modifier-list/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/modifier-list/all',
            params: { required: input.required },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/modifier-list',
            params: { search: input.search, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getListByItem = (itemId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/modifier-list/${itemId}/item-modifier-lists`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/modifier-list/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateItemModifierLists = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/modifier-list/item-modifier-lists',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateModifiers = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/modifier-list/modifiers',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ModifierListService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ModifierListService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ModifierListService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ModifierListService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ModifierListService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ModifierService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/modifier',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/modifier/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/modifier/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/modifier/all',
            params: { disabled: input.disabled, modifierListId: input.modifierListId },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/modifier',
            params: { search: input.search, disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/modifier/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/modifier/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/modifier/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ModifierService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ModifierService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ModifierService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ModifierService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ModifierService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var NoStockStatus;
(function (NoStockStatus) {
    NoStockStatus[NoStockStatus["Unavailable"] = 0] = "Unavailable";
    NoStockStatus[NoStockStatus["Available"] = 1] = "Available";
})(NoStockStatus || (NoStockStatus = {}));
const noStockStatusOptions = mapEnumToOptions$1(NoStockStatus);

class PriceListService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/price-list',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/price-list/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/price-list/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getExport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/price-list/export',
            params: { isTemplateOnly: input.isTemplateOnly, priceListId: input.priceListId, warehouseId: input.warehouseId, itemDetailId: input.itemDetailId },
        }, { apiName: this.apiName, ...config });
        this.getItemPrices = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/price-list/item-prices',
            params: { priceListId: input.priceListId, itemDetailId: input.itemDetailId, warehouseId: input.warehouseId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/price-list',
            params: { disabled: input.disabled, priceListId: input.priceListId, priceFrom: input.priceFrom, priceTo: input.priceTo, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/price-list/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/price-list/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/price-list/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
PriceListService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PriceListService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
PriceListService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PriceListService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PriceListService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var SalesChannels;
(function (SalesChannels) {
    SalesChannels[SalesChannels["None"] = 0] = "None";
    SalesChannels[SalesChannels["WebStore"] = 1] = "WebStore";
    SalesChannels[SalesChannels["AndroidStore"] = 2] = "AndroidStore";
    SalesChannels[SalesChannels["IosStore"] = 3] = "IosStore";
    SalesChannels[SalesChannels["POS"] = 4] = "POS";
    SalesChannels[SalesChannels["ControlPanel"] = 5] = "ControlPanel";
})(SalesChannels || (SalesChannels = {}));
const salesChannelsOptions = mapEnumToOptions$1(SalesChannels);

var TaxAddOption;
(function (TaxAddOption) {
    TaxAddOption[TaxAddOption["ApplyToNewOnly"] = 1] = "ApplyToNewOnly";
})(TaxAddOption || (TaxAddOption = {}));
const taxAddOptionOptions = mapEnumToOptions$1(TaxAddOption);

var TaxType;
(function (TaxType) {
    TaxType[TaxType["PriceIncluded"] = 0] = "PriceIncluded";
    TaxType[TaxType["PriceExcluded"] = 1] = "PriceExcluded";
})(TaxType || (TaxType = {}));
const taxTypeOptions = mapEnumToOptions$1(TaxType);

class TaxService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/tax',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/tax/${id}`,
        }, { apiName: this.apiName, ...config });
        this.enableBranchInAllTaxes = (branchId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/tax/enable-branch-in-all-taxes/${branchId}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/tax/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/tax/all',
            params: { disabled: input.disabled, branchId: input.branchId },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/tax',
            params: { sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getStoreList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/tax/store-list',
            params: { branchId: input.branchId },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/tax/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/tax/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/tax/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
TaxService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TaxService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
TaxService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TaxService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TaxService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class UnitService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/unit',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/unit/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/unit/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/unit/all',
            params: { disabled: input.disabled },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/unit',
            params: { disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/unit/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/unit/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/unit/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
UnitService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: UnitService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
UnitService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: UnitService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: UnitService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class AuditLogService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.delete = (dateTime, config) => this.restService.request({
            method: 'DELETE',
            url: '/api/app/audit-log',
            params: { dateTime },
        }, { apiName: this.apiName, ...config });
        this.getActions = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/audit-log/${id}/actions`,
        }, { apiName: this.apiName, ...config });
        this.getAverageExecutionDurationPerDay = (startTime, endTime, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/audit-log/average-execution-duration-per-day',
            params: { startTime, endTime },
        }, { apiName: this.apiName, ...config });
        this.getCount = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/audit-log/count',
            params: { top: input.top, tenantId: input.tenantId, tenantName: input.tenantName, startTime: input.startTime, endTime: input.endTime, httpMethod: input.httpMethod, url: input.url, userId: input.userId, userName: input.userName, applicationName: input.applicationName, clientIpAddress: input.clientIpAddress, correlationId: input.correlationId, maxExecutionDuration: input.maxExecutionDuration, minExecutionDuration: input.minExecutionDuration, hasException: input.hasException, httpStatusCode: input.httpStatusCode, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getCustomList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/audit-log/custom-list',
            params: { top: input.top, tenantId: input.tenantId, tenantName: input.tenantName, startTime: input.startTime, endTime: input.endTime, httpMethod: input.httpMethod, url: input.url, userId: input.userId, userName: input.userName, applicationName: input.applicationName, clientIpAddress: input.clientIpAddress, correlationId: input.correlationId, maxExecutionDuration: input.maxExecutionDuration, minExecutionDuration: input.minExecutionDuration, hasException: input.hasException, httpStatusCode: input.httpStatusCode, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/audit-log',
            params: { top: input.top, tenantId: input.tenantId, tenantName: input.tenantName, startTime: input.startTime, endTime: input.endTime, httpMethod: input.httpMethod, url: input.url, userId: input.userId, userName: input.userName, applicationName: input.applicationName, clientIpAddress: input.clientIpAddress, correlationId: input.correlationId, maxExecutionDuration: input.maxExecutionDuration, minExecutionDuration: input.minExecutionDuration, hasException: input.hasException, httpStatusCode: input.httpStatusCode, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
    }
}
AuditLogService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuditLogService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
AuditLogService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuditLogService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AuditLogService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ConfigService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.deleteRemoveOrigin = (origin, config) => this.restService.request({
            method: 'DELETE',
            url: '/api/app/config/remove-origin',
            params: { origin },
        }, { apiName: this.apiName, ...config });
        this.getDateFormats = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/config/date-formats',
        }, { apiName: this.apiName, ...config });
        this.getLanguages = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/config/languages',
        }, { apiName: this.apiName, ...config });
        this.getOrigins = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/config/origins',
        }, { apiName: this.apiName, ...config });
        this.getTimeZones = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/config/time-zones',
        }, { apiName: this.apiName, ...config });
        this.postAddOrigin = (origin, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/config/add-origin',
            params: { origin },
        }, { apiName: this.apiName, ...config });
    }
}
ConfigService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ConfigService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ConfigService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ConfigService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ConfigService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class SettingsService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.clearTenantCache = (tenantId, config) => this.restService.request({
            method: 'POST',
            url: '/api​/app​/settings​/clear-tenant-cache',
            params: { tenantId },
        }, { apiName: this.apiName, ...config });
        this.getGeneralSettings = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/settings/general-settings',
        }, { apiName: this.apiName, ...config });
        this.getUploadFilesToFtpServer = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/settings/upload-files-to-ftp-server',
        }, { apiName: this.apiName, ...config });
        this.postMarkSetupBasicSettingsDone = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/settings/mark-setup-basic-settings-done',
        }, { apiName: this.apiName, ...config });
        this.postMarkSetupDeliveryMethodsDone = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/settings/mark-setup-delivery-methods-done',
        }, { apiName: this.apiName, ...config });
        this.postMarkSetupItemsDone = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/settings/mark-setup-items-done',
        }, { apiName: this.apiName, ...config });
        this.postMarkSetupMainOutletDone = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/settings/mark-setup-main-outlet-done',
        }, { apiName: this.apiName, ...config });
        this.postMarkSetupPaymentMethodsDone = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/settings/mark-setup-payment-methods-done',
        }, { apiName: this.apiName, ...config });
        this.putCurrency = (currencyId, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/settings/currency/${currencyId}`,
        }, { apiName: this.apiName, ...config });
        this.putGeneralSettings = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/settings/general-settings',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putItemSettings = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/settings/item-settings',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putOrderSettings = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/settings/order-settings',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putReviews = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/settings/reviews',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putTransferOrderSettings = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/settings/transfer-order-settings',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putUploadFilesToFtpServer = (tenantsIds, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/settings/upload-files-to-ftp-server',
            body: tenantsIds,
        }, { apiName: this.apiName, ...config });
        this.updateUserLanguageByLanguage = (language, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/settings/user-language/${language}`,
        }, { apiName: this.apiName, ...config });
    }
}
SettingsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SettingsService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SettingsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SettingsService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SettingsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var AddressTarget;
(function (AddressTarget) {
    AddressTarget[AddressTarget["Customer"] = 1] = "Customer";
    AddressTarget[AddressTarget["Branch"] = 2] = "Branch";
    AddressTarget[AddressTarget["Order"] = 3] = "Order";
    AddressTarget[AddressTarget["Outlet"] = 4] = "Outlet";
    AddressTarget[AddressTarget["Warehouse"] = 5] = "Warehouse";
    AddressTarget[AddressTarget["Invoice"] = 6] = "Invoice";
})(AddressTarget || (AddressTarget = {}));
const addressTargetOptions = mapEnumToOptions$1(AddressTarget);

class AddressService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/address',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/address/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/address/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/address',
            params: { customerId: input.customerId, target: input.target, stateProvinceId: input.stateProvinceId, countryId: input.countryId, cityId: input.cityId, districtId: input.districtId, search: input.search, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/address/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
AddressService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AddressService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
AddressService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AddressService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: AddressService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var CashRoundingMethod;
(function (CashRoundingMethod) {
    CashRoundingMethod[CashRoundingMethod["Round"] = 0] = "Round";
    CashRoundingMethod[CashRoundingMethod["Floor"] = 1] = "Floor";
    CashRoundingMethod[CashRoundingMethod["Ceil"] = 2] = "Ceil";
})(CashRoundingMethod || (CashRoundingMethod = {}));
const cashRoundingMethodOptions = mapEnumToOptions$1(CashRoundingMethod);

var CountryIso2Code;
(function (CountryIso2Code) {
    CountryIso2Code[CountryIso2Code["AF"] = 0] = "AF";
    CountryIso2Code[CountryIso2Code["AX"] = 1] = "AX";
    CountryIso2Code[CountryIso2Code["AL"] = 2] = "AL";
    CountryIso2Code[CountryIso2Code["DZ"] = 3] = "DZ";
    CountryIso2Code[CountryIso2Code["AS"] = 4] = "AS";
    CountryIso2Code[CountryIso2Code["AD"] = 5] = "AD";
    CountryIso2Code[CountryIso2Code["AO"] = 6] = "AO";
    CountryIso2Code[CountryIso2Code["AI"] = 7] = "AI";
    CountryIso2Code[CountryIso2Code["AQ"] = 8] = "AQ";
    CountryIso2Code[CountryIso2Code["AG"] = 9] = "AG";
    CountryIso2Code[CountryIso2Code["AR"] = 10] = "AR";
    CountryIso2Code[CountryIso2Code["AM"] = 11] = "AM";
    CountryIso2Code[CountryIso2Code["AW"] = 12] = "AW";
    CountryIso2Code[CountryIso2Code["AU"] = 13] = "AU";
    CountryIso2Code[CountryIso2Code["AT"] = 14] = "AT";
    CountryIso2Code[CountryIso2Code["AZ"] = 15] = "AZ";
    CountryIso2Code[CountryIso2Code["BH"] = 16] = "BH";
    CountryIso2Code[CountryIso2Code["BS"] = 17] = "BS";
    CountryIso2Code[CountryIso2Code["BD"] = 18] = "BD";
    CountryIso2Code[CountryIso2Code["BB"] = 19] = "BB";
    CountryIso2Code[CountryIso2Code["BY"] = 20] = "BY";
    CountryIso2Code[CountryIso2Code["BE"] = 21] = "BE";
    CountryIso2Code[CountryIso2Code["BZ"] = 22] = "BZ";
    CountryIso2Code[CountryIso2Code["BJ"] = 23] = "BJ";
    CountryIso2Code[CountryIso2Code["BM"] = 24] = "BM";
    CountryIso2Code[CountryIso2Code["BT"] = 25] = "BT";
    CountryIso2Code[CountryIso2Code["BO"] = 26] = "BO";
    CountryIso2Code[CountryIso2Code["BQ"] = 27] = "BQ";
    CountryIso2Code[CountryIso2Code["BA"] = 28] = "BA";
    CountryIso2Code[CountryIso2Code["BW"] = 29] = "BW";
    CountryIso2Code[CountryIso2Code["BV"] = 30] = "BV";
    CountryIso2Code[CountryIso2Code["BR"] = 31] = "BR";
    CountryIso2Code[CountryIso2Code["IO"] = 32] = "IO";
    CountryIso2Code[CountryIso2Code["BN"] = 33] = "BN";
    CountryIso2Code[CountryIso2Code["BG"] = 34] = "BG";
    CountryIso2Code[CountryIso2Code["BF"] = 35] = "BF";
    CountryIso2Code[CountryIso2Code["BI"] = 36] = "BI";
    CountryIso2Code[CountryIso2Code["KH"] = 37] = "KH";
    CountryIso2Code[CountryIso2Code["CM"] = 38] = "CM";
    CountryIso2Code[CountryIso2Code["CA"] = 39] = "CA";
    CountryIso2Code[CountryIso2Code["CV"] = 40] = "CV";
    CountryIso2Code[CountryIso2Code["KY"] = 41] = "KY";
    CountryIso2Code[CountryIso2Code["CF"] = 42] = "CF";
    CountryIso2Code[CountryIso2Code["TD"] = 43] = "TD";
    CountryIso2Code[CountryIso2Code["CL"] = 44] = "CL";
    CountryIso2Code[CountryIso2Code["CN"] = 45] = "CN";
    CountryIso2Code[CountryIso2Code["CX"] = 46] = "CX";
    CountryIso2Code[CountryIso2Code["CC"] = 47] = "CC";
    CountryIso2Code[CountryIso2Code["CO"] = 48] = "CO";
    CountryIso2Code[CountryIso2Code["KM"] = 49] = "KM";
    CountryIso2Code[CountryIso2Code["CG"] = 50] = "CG";
    CountryIso2Code[CountryIso2Code["CD"] = 51] = "CD";
    CountryIso2Code[CountryIso2Code["CK"] = 52] = "CK";
    CountryIso2Code[CountryIso2Code["CR"] = 53] = "CR";
    CountryIso2Code[CountryIso2Code["CI"] = 54] = "CI";
    CountryIso2Code[CountryIso2Code["HR"] = 55] = "HR";
    CountryIso2Code[CountryIso2Code["CU"] = 56] = "CU";
    CountryIso2Code[CountryIso2Code["CW"] = 57] = "CW";
    CountryIso2Code[CountryIso2Code["CY"] = 58] = "CY";
    CountryIso2Code[CountryIso2Code["CZ"] = 59] = "CZ";
    CountryIso2Code[CountryIso2Code["DK"] = 60] = "DK";
    CountryIso2Code[CountryIso2Code["DJ"] = 61] = "DJ";
    CountryIso2Code[CountryIso2Code["DM"] = 62] = "DM";
    CountryIso2Code[CountryIso2Code["DO"] = 63] = "DO";
    CountryIso2Code[CountryIso2Code["EC"] = 64] = "EC";
    CountryIso2Code[CountryIso2Code["EG"] = 65] = "EG";
    CountryIso2Code[CountryIso2Code["SV"] = 66] = "SV";
    CountryIso2Code[CountryIso2Code["GQ"] = 67] = "GQ";
    CountryIso2Code[CountryIso2Code["ER"] = 68] = "ER";
    CountryIso2Code[CountryIso2Code["EE"] = 69] = "EE";
    CountryIso2Code[CountryIso2Code["ET"] = 70] = "ET";
    CountryIso2Code[CountryIso2Code["FK"] = 71] = "FK";
    CountryIso2Code[CountryIso2Code["FO"] = 72] = "FO";
    CountryIso2Code[CountryIso2Code["FJ"] = 73] = "FJ";
    CountryIso2Code[CountryIso2Code["FI"] = 74] = "FI";
    CountryIso2Code[CountryIso2Code["FR"] = 75] = "FR";
    CountryIso2Code[CountryIso2Code["GF"] = 76] = "GF";
    CountryIso2Code[CountryIso2Code["PF"] = 77] = "PF";
    CountryIso2Code[CountryIso2Code["TF"] = 78] = "TF";
    CountryIso2Code[CountryIso2Code["GA"] = 79] = "GA";
    CountryIso2Code[CountryIso2Code["GM"] = 80] = "GM";
    CountryIso2Code[CountryIso2Code["GE"] = 81] = "GE";
    CountryIso2Code[CountryIso2Code["DE"] = 82] = "DE";
    CountryIso2Code[CountryIso2Code["GH"] = 83] = "GH";
    CountryIso2Code[CountryIso2Code["GI"] = 84] = "GI";
    CountryIso2Code[CountryIso2Code["GR"] = 85] = "GR";
    CountryIso2Code[CountryIso2Code["GL"] = 86] = "GL";
    CountryIso2Code[CountryIso2Code["GD"] = 87] = "GD";
    CountryIso2Code[CountryIso2Code["GP"] = 88] = "GP";
    CountryIso2Code[CountryIso2Code["GU"] = 89] = "GU";
    CountryIso2Code[CountryIso2Code["GT"] = 90] = "GT";
    CountryIso2Code[CountryIso2Code["GG"] = 91] = "GG";
    CountryIso2Code[CountryIso2Code["GN"] = 92] = "GN";
    CountryIso2Code[CountryIso2Code["GW"] = 93] = "GW";
    CountryIso2Code[CountryIso2Code["GY"] = 94] = "GY";
    CountryIso2Code[CountryIso2Code["HT"] = 95] = "HT";
    CountryIso2Code[CountryIso2Code["HM"] = 96] = "HM";
    CountryIso2Code[CountryIso2Code["VA"] = 97] = "VA";
    CountryIso2Code[CountryIso2Code["HN"] = 98] = "HN";
    CountryIso2Code[CountryIso2Code["HK"] = 99] = "HK";
    CountryIso2Code[CountryIso2Code["HU"] = 100] = "HU";
    CountryIso2Code[CountryIso2Code["IS"] = 101] = "IS";
    CountryIso2Code[CountryIso2Code["IN"] = 102] = "IN";
    CountryIso2Code[CountryIso2Code["ID"] = 103] = "ID";
    CountryIso2Code[CountryIso2Code["IR"] = 104] = "IR";
    CountryIso2Code[CountryIso2Code["IQ"] = 105] = "IQ";
    CountryIso2Code[CountryIso2Code["IE"] = 106] = "IE";
    CountryIso2Code[CountryIso2Code["IM"] = 107] = "IM";
    CountryIso2Code[CountryIso2Code["IT"] = 108] = "IT";
    CountryIso2Code[CountryIso2Code["JM"] = 109] = "JM";
    CountryIso2Code[CountryIso2Code["JP"] = 110] = "JP";
    CountryIso2Code[CountryIso2Code["JE"] = 111] = "JE";
    CountryIso2Code[CountryIso2Code["JO"] = 112] = "JO";
    CountryIso2Code[CountryIso2Code["KZ"] = 113] = "KZ";
    CountryIso2Code[CountryIso2Code["KE"] = 114] = "KE";
    CountryIso2Code[CountryIso2Code["KI"] = 115] = "KI";
    CountryIso2Code[CountryIso2Code["KP"] = 116] = "KP";
    CountryIso2Code[CountryIso2Code["KR"] = 117] = "KR";
    CountryIso2Code[CountryIso2Code["KW"] = 118] = "KW";
    CountryIso2Code[CountryIso2Code["KG"] = 119] = "KG";
    CountryIso2Code[CountryIso2Code["LA"] = 120] = "LA";
    CountryIso2Code[CountryIso2Code["LV"] = 121] = "LV";
    CountryIso2Code[CountryIso2Code["LB"] = 122] = "LB";
    CountryIso2Code[CountryIso2Code["LS"] = 123] = "LS";
    CountryIso2Code[CountryIso2Code["LR"] = 124] = "LR";
    CountryIso2Code[CountryIso2Code["LY"] = 125] = "LY";
    CountryIso2Code[CountryIso2Code["LI"] = 126] = "LI";
    CountryIso2Code[CountryIso2Code["LT"] = 127] = "LT";
    CountryIso2Code[CountryIso2Code["LU"] = 128] = "LU";
    CountryIso2Code[CountryIso2Code["MO"] = 129] = "MO";
    CountryIso2Code[CountryIso2Code["MK"] = 130] = "MK";
    CountryIso2Code[CountryIso2Code["MG"] = 131] = "MG";
    CountryIso2Code[CountryIso2Code["MW"] = 132] = "MW";
    CountryIso2Code[CountryIso2Code["MY"] = 133] = "MY";
    CountryIso2Code[CountryIso2Code["MV"] = 134] = "MV";
    CountryIso2Code[CountryIso2Code["ML"] = 135] = "ML";
    CountryIso2Code[CountryIso2Code["MT"] = 136] = "MT";
    CountryIso2Code[CountryIso2Code["MH"] = 137] = "MH";
    CountryIso2Code[CountryIso2Code["MQ"] = 138] = "MQ";
    CountryIso2Code[CountryIso2Code["MR"] = 139] = "MR";
    CountryIso2Code[CountryIso2Code["MU"] = 140] = "MU";
    CountryIso2Code[CountryIso2Code["YT"] = 141] = "YT";
    CountryIso2Code[CountryIso2Code["MX"] = 142] = "MX";
    CountryIso2Code[CountryIso2Code["FM"] = 143] = "FM";
    CountryIso2Code[CountryIso2Code["MD"] = 144] = "MD";
    CountryIso2Code[CountryIso2Code["MC"] = 145] = "MC";
    CountryIso2Code[CountryIso2Code["MN"] = 146] = "MN";
    CountryIso2Code[CountryIso2Code["ME"] = 147] = "ME";
    CountryIso2Code[CountryIso2Code["MS"] = 148] = "MS";
    CountryIso2Code[CountryIso2Code["MA"] = 149] = "MA";
    CountryIso2Code[CountryIso2Code["MZ"] = 150] = "MZ";
    CountryIso2Code[CountryIso2Code["MM"] = 151] = "MM";
    CountryIso2Code[CountryIso2Code["NA"] = 152] = "NA";
    CountryIso2Code[CountryIso2Code["NR"] = 153] = "NR";
    CountryIso2Code[CountryIso2Code["NP"] = 154] = "NP";
    CountryIso2Code[CountryIso2Code["NL"] = 155] = "NL";
    CountryIso2Code[CountryIso2Code["NC"] = 156] = "NC";
    CountryIso2Code[CountryIso2Code["NZ"] = 157] = "NZ";
    CountryIso2Code[CountryIso2Code["NI"] = 158] = "NI";
    CountryIso2Code[CountryIso2Code["NE"] = 159] = "NE";
    CountryIso2Code[CountryIso2Code["NG"] = 160] = "NG";
    CountryIso2Code[CountryIso2Code["NU"] = 161] = "NU";
    CountryIso2Code[CountryIso2Code["NF"] = 162] = "NF";
    CountryIso2Code[CountryIso2Code["MP"] = 163] = "MP";
    CountryIso2Code[CountryIso2Code["NO"] = 164] = "NO";
    CountryIso2Code[CountryIso2Code["OM"] = 165] = "OM";
    CountryIso2Code[CountryIso2Code["PK"] = 166] = "PK";
    CountryIso2Code[CountryIso2Code["PW"] = 167] = "PW";
    CountryIso2Code[CountryIso2Code["PS"] = 168] = "PS";
    CountryIso2Code[CountryIso2Code["PA"] = 169] = "PA";
    CountryIso2Code[CountryIso2Code["PG"] = 170] = "PG";
    CountryIso2Code[CountryIso2Code["PY"] = 171] = "PY";
    CountryIso2Code[CountryIso2Code["PE"] = 172] = "PE";
    CountryIso2Code[CountryIso2Code["PH"] = 173] = "PH";
    CountryIso2Code[CountryIso2Code["PN"] = 174] = "PN";
    CountryIso2Code[CountryIso2Code["PL"] = 175] = "PL";
    CountryIso2Code[CountryIso2Code["PT"] = 176] = "PT";
    CountryIso2Code[CountryIso2Code["PR"] = 177] = "PR";
    CountryIso2Code[CountryIso2Code["QA"] = 178] = "QA";
    CountryIso2Code[CountryIso2Code["RE"] = 179] = "RE";
    CountryIso2Code[CountryIso2Code["RO"] = 180] = "RO";
    CountryIso2Code[CountryIso2Code["RU"] = 181] = "RU";
    CountryIso2Code[CountryIso2Code["RW"] = 182] = "RW";
    CountryIso2Code[CountryIso2Code["BL"] = 183] = "BL";
    CountryIso2Code[CountryIso2Code["SH"] = 184] = "SH";
    CountryIso2Code[CountryIso2Code["KN"] = 185] = "KN";
    CountryIso2Code[CountryIso2Code["LC"] = 186] = "LC";
    CountryIso2Code[CountryIso2Code["MF"] = 187] = "MF";
    CountryIso2Code[CountryIso2Code["PM"] = 188] = "PM";
    CountryIso2Code[CountryIso2Code["VC"] = 189] = "VC";
    CountryIso2Code[CountryIso2Code["WS"] = 190] = "WS";
    CountryIso2Code[CountryIso2Code["SM"] = 191] = "SM";
    CountryIso2Code[CountryIso2Code["ST"] = 192] = "ST";
    CountryIso2Code[CountryIso2Code["SA"] = 193] = "SA";
    CountryIso2Code[CountryIso2Code["SN"] = 194] = "SN";
    CountryIso2Code[CountryIso2Code["RS"] = 195] = "RS";
    CountryIso2Code[CountryIso2Code["SC"] = 196] = "SC";
    CountryIso2Code[CountryIso2Code["SL"] = 197] = "SL";
    CountryIso2Code[CountryIso2Code["SG"] = 198] = "SG";
    CountryIso2Code[CountryIso2Code["SX"] = 199] = "SX";
    CountryIso2Code[CountryIso2Code["SK"] = 200] = "SK";
    CountryIso2Code[CountryIso2Code["SI"] = 201] = "SI";
    CountryIso2Code[CountryIso2Code["SB"] = 202] = "SB";
    CountryIso2Code[CountryIso2Code["SO"] = 203] = "SO";
    CountryIso2Code[CountryIso2Code["ZA"] = 204] = "ZA";
    CountryIso2Code[CountryIso2Code["GS"] = 205] = "GS";
    CountryIso2Code[CountryIso2Code["SS"] = 206] = "SS";
    CountryIso2Code[CountryIso2Code["ES"] = 207] = "ES";
    CountryIso2Code[CountryIso2Code["LK"] = 208] = "LK";
    CountryIso2Code[CountryIso2Code["SD"] = 209] = "SD";
    CountryIso2Code[CountryIso2Code["SR"] = 210] = "SR";
    CountryIso2Code[CountryIso2Code["SJ"] = 211] = "SJ";
    CountryIso2Code[CountryIso2Code["SZ"] = 212] = "SZ";
    CountryIso2Code[CountryIso2Code["SE"] = 213] = "SE";
    CountryIso2Code[CountryIso2Code["CH"] = 214] = "CH";
    CountryIso2Code[CountryIso2Code["SY"] = 215] = "SY";
    CountryIso2Code[CountryIso2Code["TW"] = 216] = "TW";
    CountryIso2Code[CountryIso2Code["TJ"] = 217] = "TJ";
    CountryIso2Code[CountryIso2Code["TZ"] = 218] = "TZ";
    CountryIso2Code[CountryIso2Code["TH"] = 219] = "TH";
    CountryIso2Code[CountryIso2Code["TL"] = 220] = "TL";
    CountryIso2Code[CountryIso2Code["TG"] = 221] = "TG";
    CountryIso2Code[CountryIso2Code["TK"] = 222] = "TK";
    CountryIso2Code[CountryIso2Code["TO"] = 223] = "TO";
    CountryIso2Code[CountryIso2Code["TT"] = 224] = "TT";
    CountryIso2Code[CountryIso2Code["TN"] = 225] = "TN";
    CountryIso2Code[CountryIso2Code["TR"] = 226] = "TR";
    CountryIso2Code[CountryIso2Code["TM"] = 227] = "TM";
    CountryIso2Code[CountryIso2Code["TC"] = 228] = "TC";
    CountryIso2Code[CountryIso2Code["TV"] = 229] = "TV";
    CountryIso2Code[CountryIso2Code["UG"] = 230] = "UG";
    CountryIso2Code[CountryIso2Code["UA"] = 231] = "UA";
    CountryIso2Code[CountryIso2Code["AE"] = 232] = "AE";
    CountryIso2Code[CountryIso2Code["GB"] = 233] = "GB";
    CountryIso2Code[CountryIso2Code["US"] = 234] = "US";
    CountryIso2Code[CountryIso2Code["UM"] = 235] = "UM";
    CountryIso2Code[CountryIso2Code["UY"] = 236] = "UY";
    CountryIso2Code[CountryIso2Code["UZ"] = 237] = "UZ";
    CountryIso2Code[CountryIso2Code["VU"] = 238] = "VU";
    CountryIso2Code[CountryIso2Code["VE"] = 239] = "VE";
    CountryIso2Code[CountryIso2Code["VN"] = 240] = "VN";
    CountryIso2Code[CountryIso2Code["VG"] = 241] = "VG";
    CountryIso2Code[CountryIso2Code["VI"] = 242] = "VI";
    CountryIso2Code[CountryIso2Code["WF"] = 243] = "WF";
    CountryIso2Code[CountryIso2Code["EH"] = 244] = "EH";
    CountryIso2Code[CountryIso2Code["YE"] = 245] = "YE";
    CountryIso2Code[CountryIso2Code["ZM"] = 246] = "ZM";
    CountryIso2Code[CountryIso2Code["ZW"] = 247] = "ZW";
})(CountryIso2Code || (CountryIso2Code = {}));
const countryIso2CodeOptions = mapEnumToOptions$1(CountryIso2Code);

var CountryIso3Code;
(function (CountryIso3Code) {
    CountryIso3Code[CountryIso3Code["AFG"] = 0] = "AFG";
    CountryIso3Code[CountryIso3Code["ALA"] = 1] = "ALA";
    CountryIso3Code[CountryIso3Code["ALB"] = 2] = "ALB";
    CountryIso3Code[CountryIso3Code["DZA"] = 3] = "DZA";
    CountryIso3Code[CountryIso3Code["ASM"] = 4] = "ASM";
    CountryIso3Code[CountryIso3Code["AND"] = 5] = "AND";
    CountryIso3Code[CountryIso3Code["AGO"] = 6] = "AGO";
    CountryIso3Code[CountryIso3Code["AIA"] = 7] = "AIA";
    CountryIso3Code[CountryIso3Code["ATA"] = 8] = "ATA";
    CountryIso3Code[CountryIso3Code["ATG"] = 9] = "ATG";
    CountryIso3Code[CountryIso3Code["ARG"] = 10] = "ARG";
    CountryIso3Code[CountryIso3Code["ARM"] = 11] = "ARM";
    CountryIso3Code[CountryIso3Code["ABW"] = 12] = "ABW";
    CountryIso3Code[CountryIso3Code["AUS"] = 13] = "AUS";
    CountryIso3Code[CountryIso3Code["AUT"] = 14] = "AUT";
    CountryIso3Code[CountryIso3Code["AZE"] = 15] = "AZE";
    CountryIso3Code[CountryIso3Code["BHS"] = 16] = "BHS";
    CountryIso3Code[CountryIso3Code["BHR"] = 17] = "BHR";
    CountryIso3Code[CountryIso3Code["BGD"] = 18] = "BGD";
    CountryIso3Code[CountryIso3Code["BRB"] = 19] = "BRB";
    CountryIso3Code[CountryIso3Code["BLR"] = 20] = "BLR";
    CountryIso3Code[CountryIso3Code["BEL"] = 21] = "BEL";
    CountryIso3Code[CountryIso3Code["BLZ"] = 22] = "BLZ";
    CountryIso3Code[CountryIso3Code["BEN"] = 23] = "BEN";
    CountryIso3Code[CountryIso3Code["BES"] = 24] = "BES";
    CountryIso3Code[CountryIso3Code["BMU"] = 25] = "BMU";
    CountryIso3Code[CountryIso3Code["BTN"] = 26] = "BTN";
    CountryIso3Code[CountryIso3Code["BOL"] = 27] = "BOL";
    CountryIso3Code[CountryIso3Code["BIH"] = 28] = "BIH";
    CountryIso3Code[CountryIso3Code["BWA"] = 29] = "BWA";
    CountryIso3Code[CountryIso3Code["BVT"] = 30] = "BVT";
    CountryIso3Code[CountryIso3Code["BRA"] = 31] = "BRA";
    CountryIso3Code[CountryIso3Code["IOT"] = 32] = "IOT";
    CountryIso3Code[CountryIso3Code["BRN"] = 33] = "BRN";
    CountryIso3Code[CountryIso3Code["BGR"] = 34] = "BGR";
    CountryIso3Code[CountryIso3Code["BFA"] = 35] = "BFA";
    CountryIso3Code[CountryIso3Code["BDI"] = 36] = "BDI";
    CountryIso3Code[CountryIso3Code["KHM"] = 37] = "KHM";
    CountryIso3Code[CountryIso3Code["CMR"] = 38] = "CMR";
    CountryIso3Code[CountryIso3Code["CAN"] = 39] = "CAN";
    CountryIso3Code[CountryIso3Code["CPV"] = 40] = "CPV";
    CountryIso3Code[CountryIso3Code["CYM"] = 41] = "CYM";
    CountryIso3Code[CountryIso3Code["CAF"] = 42] = "CAF";
    CountryIso3Code[CountryIso3Code["TCD"] = 43] = "TCD";
    CountryIso3Code[CountryIso3Code["CHL"] = 44] = "CHL";
    CountryIso3Code[CountryIso3Code["CHN"] = 45] = "CHN";
    CountryIso3Code[CountryIso3Code["CXR"] = 46] = "CXR";
    CountryIso3Code[CountryIso3Code["CCK"] = 47] = "CCK";
    CountryIso3Code[CountryIso3Code["COL"] = 48] = "COL";
    CountryIso3Code[CountryIso3Code["COM"] = 49] = "COM";
    CountryIso3Code[CountryIso3Code["COG"] = 50] = "COG";
    CountryIso3Code[CountryIso3Code["COD"] = 51] = "COD";
    CountryIso3Code[CountryIso3Code["COK"] = 52] = "COK";
    CountryIso3Code[CountryIso3Code["CRI"] = 53] = "CRI";
    CountryIso3Code[CountryIso3Code["CIV"] = 54] = "CIV";
    CountryIso3Code[CountryIso3Code["HRV"] = 55] = "HRV";
    CountryIso3Code[CountryIso3Code["CUB"] = 56] = "CUB";
    CountryIso3Code[CountryIso3Code["CUW"] = 57] = "CUW";
    CountryIso3Code[CountryIso3Code["CYP"] = 58] = "CYP";
    CountryIso3Code[CountryIso3Code["CZE"] = 59] = "CZE";
    CountryIso3Code[CountryIso3Code["DNK"] = 60] = "DNK";
    CountryIso3Code[CountryIso3Code["DJI"] = 61] = "DJI";
    CountryIso3Code[CountryIso3Code["DMA"] = 62] = "DMA";
    CountryIso3Code[CountryIso3Code["DOM"] = 63] = "DOM";
    CountryIso3Code[CountryIso3Code["ECU"] = 64] = "ECU";
    CountryIso3Code[CountryIso3Code["EGY"] = 65] = "EGY";
    CountryIso3Code[CountryIso3Code["SLV"] = 66] = "SLV";
    CountryIso3Code[CountryIso3Code["GNQ"] = 67] = "GNQ";
    CountryIso3Code[CountryIso3Code["ERI"] = 68] = "ERI";
    CountryIso3Code[CountryIso3Code["EST"] = 69] = "EST";
    CountryIso3Code[CountryIso3Code["ETH"] = 70] = "ETH";
    CountryIso3Code[CountryIso3Code["FLK"] = 71] = "FLK";
    CountryIso3Code[CountryIso3Code["FRO"] = 72] = "FRO";
    CountryIso3Code[CountryIso3Code["FJI"] = 73] = "FJI";
    CountryIso3Code[CountryIso3Code["FIN"] = 74] = "FIN";
    CountryIso3Code[CountryIso3Code["FRA"] = 75] = "FRA";
    CountryIso3Code[CountryIso3Code["GUF"] = 76] = "GUF";
    CountryIso3Code[CountryIso3Code["PYF"] = 77] = "PYF";
    CountryIso3Code[CountryIso3Code["ATF"] = 78] = "ATF";
    CountryIso3Code[CountryIso3Code["GAB"] = 79] = "GAB";
    CountryIso3Code[CountryIso3Code["GMB"] = 80] = "GMB";
    CountryIso3Code[CountryIso3Code["GEO"] = 81] = "GEO";
    CountryIso3Code[CountryIso3Code["DEU"] = 82] = "DEU";
    CountryIso3Code[CountryIso3Code["GHA"] = 83] = "GHA";
    CountryIso3Code[CountryIso3Code["GIB"] = 84] = "GIB";
    CountryIso3Code[CountryIso3Code["GRC"] = 85] = "GRC";
    CountryIso3Code[CountryIso3Code["GRL"] = 86] = "GRL";
    CountryIso3Code[CountryIso3Code["GRD"] = 87] = "GRD";
    CountryIso3Code[CountryIso3Code["GLP"] = 88] = "GLP";
    CountryIso3Code[CountryIso3Code["GUM"] = 89] = "GUM";
    CountryIso3Code[CountryIso3Code["GTM"] = 90] = "GTM";
    CountryIso3Code[CountryIso3Code["GGY"] = 91] = "GGY";
    CountryIso3Code[CountryIso3Code["GIN"] = 92] = "GIN";
    CountryIso3Code[CountryIso3Code["GNB"] = 93] = "GNB";
    CountryIso3Code[CountryIso3Code["GUY"] = 94] = "GUY";
    CountryIso3Code[CountryIso3Code["HTI"] = 95] = "HTI";
    CountryIso3Code[CountryIso3Code["HMD"] = 96] = "HMD";
    CountryIso3Code[CountryIso3Code["VAT"] = 97] = "VAT";
    CountryIso3Code[CountryIso3Code["HND"] = 98] = "HND";
    CountryIso3Code[CountryIso3Code["HKG"] = 99] = "HKG";
    CountryIso3Code[CountryIso3Code["HUN"] = 100] = "HUN";
    CountryIso3Code[CountryIso3Code["ISL"] = 101] = "ISL";
    CountryIso3Code[CountryIso3Code["IND"] = 102] = "IND";
    CountryIso3Code[CountryIso3Code["IDN"] = 103] = "IDN";
    CountryIso3Code[CountryIso3Code["IRN"] = 104] = "IRN";
    CountryIso3Code[CountryIso3Code["IRQ"] = 105] = "IRQ";
    CountryIso3Code[CountryIso3Code["IRL"] = 106] = "IRL";
    CountryIso3Code[CountryIso3Code["IMN"] = 107] = "IMN";
    CountryIso3Code[CountryIso3Code["ITA"] = 108] = "ITA";
    CountryIso3Code[CountryIso3Code["JAM"] = 109] = "JAM";
    CountryIso3Code[CountryIso3Code["JPN"] = 110] = "JPN";
    CountryIso3Code[CountryIso3Code["JEY"] = 111] = "JEY";
    CountryIso3Code[CountryIso3Code["JOR"] = 112] = "JOR";
    CountryIso3Code[CountryIso3Code["KAZ"] = 113] = "KAZ";
    CountryIso3Code[CountryIso3Code["KEN"] = 114] = "KEN";
    CountryIso3Code[CountryIso3Code["KIR"] = 115] = "KIR";
    CountryIso3Code[CountryIso3Code["KOR"] = 116] = "KOR";
    CountryIso3Code[CountryIso3Code["PRK"] = 117] = "PRK";
    CountryIso3Code[CountryIso3Code["KWT"] = 118] = "KWT";
    CountryIso3Code[CountryIso3Code["KGZ"] = 119] = "KGZ";
    CountryIso3Code[CountryIso3Code["LAO"] = 120] = "LAO";
    CountryIso3Code[CountryIso3Code["LVA"] = 121] = "LVA";
    CountryIso3Code[CountryIso3Code["LBN"] = 122] = "LBN";
    CountryIso3Code[CountryIso3Code["LSO"] = 123] = "LSO";
    CountryIso3Code[CountryIso3Code["LBR"] = 124] = "LBR";
    CountryIso3Code[CountryIso3Code["LBY"] = 125] = "LBY";
    CountryIso3Code[CountryIso3Code["LIE"] = 126] = "LIE";
    CountryIso3Code[CountryIso3Code["LTU"] = 127] = "LTU";
    CountryIso3Code[CountryIso3Code["LUX"] = 128] = "LUX";
    CountryIso3Code[CountryIso3Code["MAC"] = 129] = "MAC";
    CountryIso3Code[CountryIso3Code["MDG"] = 130] = "MDG";
    CountryIso3Code[CountryIso3Code["MWI"] = 131] = "MWI";
    CountryIso3Code[CountryIso3Code["MYS"] = 132] = "MYS";
    CountryIso3Code[CountryIso3Code["MDV"] = 133] = "MDV";
    CountryIso3Code[CountryIso3Code["MLI"] = 134] = "MLI";
    CountryIso3Code[CountryIso3Code["MLT"] = 135] = "MLT";
    CountryIso3Code[CountryIso3Code["MHL"] = 136] = "MHL";
    CountryIso3Code[CountryIso3Code["MTQ"] = 137] = "MTQ";
    CountryIso3Code[CountryIso3Code["MRT"] = 138] = "MRT";
    CountryIso3Code[CountryIso3Code["MUS"] = 139] = "MUS";
    CountryIso3Code[CountryIso3Code["MYT"] = 140] = "MYT";
    CountryIso3Code[CountryIso3Code["MEX"] = 141] = "MEX";
    CountryIso3Code[CountryIso3Code["FSM"] = 142] = "FSM";
    CountryIso3Code[CountryIso3Code["MDA"] = 143] = "MDA";
    CountryIso3Code[CountryIso3Code["MCO"] = 144] = "MCO";
    CountryIso3Code[CountryIso3Code["MNG"] = 145] = "MNG";
    CountryIso3Code[CountryIso3Code["MNE"] = 146] = "MNE";
    CountryIso3Code[CountryIso3Code["MSR"] = 147] = "MSR";
    CountryIso3Code[CountryIso3Code["MAR"] = 148] = "MAR";
    CountryIso3Code[CountryIso3Code["MOZ"] = 149] = "MOZ";
    CountryIso3Code[CountryIso3Code["MMR"] = 150] = "MMR";
    CountryIso3Code[CountryIso3Code["NAM"] = 151] = "NAM";
    CountryIso3Code[CountryIso3Code["NRU"] = 152] = "NRU";
    CountryIso3Code[CountryIso3Code["NPL"] = 153] = "NPL";
    CountryIso3Code[CountryIso3Code["NLD"] = 154] = "NLD";
    CountryIso3Code[CountryIso3Code["NCL"] = 155] = "NCL";
    CountryIso3Code[CountryIso3Code["NZL"] = 156] = "NZL";
    CountryIso3Code[CountryIso3Code["NIC"] = 157] = "NIC";
    CountryIso3Code[CountryIso3Code["NER"] = 158] = "NER";
    CountryIso3Code[CountryIso3Code["NGA"] = 159] = "NGA";
    CountryIso3Code[CountryIso3Code["NIU"] = 160] = "NIU";
    CountryIso3Code[CountryIso3Code["NFK"] = 161] = "NFK";
    CountryIso3Code[CountryIso3Code["MNP"] = 162] = "MNP";
    CountryIso3Code[CountryIso3Code["NOR"] = 163] = "NOR";
    CountryIso3Code[CountryIso3Code["OMN"] = 164] = "OMN";
    CountryIso3Code[CountryIso3Code["PAK"] = 165] = "PAK";
    CountryIso3Code[CountryIso3Code["PLW"] = 166] = "PLW";
    CountryIso3Code[CountryIso3Code["PSE"] = 167] = "PSE";
    CountryIso3Code[CountryIso3Code["PAN"] = 168] = "PAN";
    CountryIso3Code[CountryIso3Code["PNG"] = 169] = "PNG";
    CountryIso3Code[CountryIso3Code["PRY"] = 170] = "PRY";
    CountryIso3Code[CountryIso3Code["PER"] = 171] = "PER";
    CountryIso3Code[CountryIso3Code["PHL"] = 172] = "PHL";
    CountryIso3Code[CountryIso3Code["PCN"] = 173] = "PCN";
    CountryIso3Code[CountryIso3Code["POL"] = 174] = "POL";
    CountryIso3Code[CountryIso3Code["PRT"] = 175] = "PRT";
    CountryIso3Code[CountryIso3Code["PRI"] = 176] = "PRI";
    CountryIso3Code[CountryIso3Code["QAT"] = 177] = "QAT";
    CountryIso3Code[CountryIso3Code["MKD"] = 178] = "MKD";
    CountryIso3Code[CountryIso3Code["REU"] = 179] = "REU";
    CountryIso3Code[CountryIso3Code["ROU"] = 180] = "ROU";
    CountryIso3Code[CountryIso3Code["RUS"] = 181] = "RUS";
    CountryIso3Code[CountryIso3Code["RWA"] = 182] = "RWA";
    CountryIso3Code[CountryIso3Code["BLM"] = 183] = "BLM";
    CountryIso3Code[CountryIso3Code["SHN"] = 184] = "SHN";
    CountryIso3Code[CountryIso3Code["KNA"] = 185] = "KNA";
    CountryIso3Code[CountryIso3Code["LCA"] = 186] = "LCA";
    CountryIso3Code[CountryIso3Code["MAF"] = 187] = "MAF";
    CountryIso3Code[CountryIso3Code["SPM"] = 188] = "SPM";
    CountryIso3Code[CountryIso3Code["VCT"] = 189] = "VCT";
    CountryIso3Code[CountryIso3Code["WSM"] = 190] = "WSM";
    CountryIso3Code[CountryIso3Code["SMR"] = 191] = "SMR";
    CountryIso3Code[CountryIso3Code["STP"] = 192] = "STP";
    CountryIso3Code[CountryIso3Code["SAU"] = 193] = "SAU";
    CountryIso3Code[CountryIso3Code["SEN"] = 194] = "SEN";
    CountryIso3Code[CountryIso3Code["SRB"] = 195] = "SRB";
    CountryIso3Code[CountryIso3Code["SYC"] = 196] = "SYC";
    CountryIso3Code[CountryIso3Code["SLE"] = 197] = "SLE";
    CountryIso3Code[CountryIso3Code["SGP"] = 198] = "SGP";
    CountryIso3Code[CountryIso3Code["SXM"] = 199] = "SXM";
    CountryIso3Code[CountryIso3Code["SVK"] = 200] = "SVK";
    CountryIso3Code[CountryIso3Code["SVN"] = 201] = "SVN";
    CountryIso3Code[CountryIso3Code["SLB"] = 202] = "SLB";
    CountryIso3Code[CountryIso3Code["SOM"] = 203] = "SOM";
    CountryIso3Code[CountryIso3Code["ZAF"] = 204] = "ZAF";
    CountryIso3Code[CountryIso3Code["SGS"] = 205] = "SGS";
    CountryIso3Code[CountryIso3Code["SSD"] = 206] = "SSD";
    CountryIso3Code[CountryIso3Code["ESP"] = 207] = "ESP";
    CountryIso3Code[CountryIso3Code["LKA"] = 208] = "LKA";
    CountryIso3Code[CountryIso3Code["SDN"] = 209] = "SDN";
    CountryIso3Code[CountryIso3Code["SUR"] = 210] = "SUR";
    CountryIso3Code[CountryIso3Code["SJM"] = 211] = "SJM";
    CountryIso3Code[CountryIso3Code["SWZ"] = 212] = "SWZ";
    CountryIso3Code[CountryIso3Code["SWE"] = 213] = "SWE";
    CountryIso3Code[CountryIso3Code["CHE"] = 214] = "CHE";
    CountryIso3Code[CountryIso3Code["SYR"] = 215] = "SYR";
    CountryIso3Code[CountryIso3Code["TWN"] = 216] = "TWN";
    CountryIso3Code[CountryIso3Code["TJK"] = 217] = "TJK";
    CountryIso3Code[CountryIso3Code["TZA"] = 218] = "TZA";
    CountryIso3Code[CountryIso3Code["THA"] = 219] = "THA";
    CountryIso3Code[CountryIso3Code["TLS"] = 220] = "TLS";
    CountryIso3Code[CountryIso3Code["TGO"] = 221] = "TGO";
    CountryIso3Code[CountryIso3Code["TKL"] = 222] = "TKL";
    CountryIso3Code[CountryIso3Code["TON"] = 223] = "TON";
    CountryIso3Code[CountryIso3Code["TTO"] = 224] = "TTO";
    CountryIso3Code[CountryIso3Code["TUN"] = 225] = "TUN";
    CountryIso3Code[CountryIso3Code["TUR"] = 226] = "TUR";
    CountryIso3Code[CountryIso3Code["TKM"] = 227] = "TKM";
    CountryIso3Code[CountryIso3Code["TCA"] = 228] = "TCA";
    CountryIso3Code[CountryIso3Code["TUV"] = 229] = "TUV";
    CountryIso3Code[CountryIso3Code["UGA"] = 230] = "UGA";
    CountryIso3Code[CountryIso3Code["UKR"] = 231] = "UKR";
    CountryIso3Code[CountryIso3Code["ARE"] = 232] = "ARE";
    CountryIso3Code[CountryIso3Code["GBR"] = 233] = "GBR";
    CountryIso3Code[CountryIso3Code["UMI"] = 234] = "UMI";
    CountryIso3Code[CountryIso3Code["USA"] = 235] = "USA";
    CountryIso3Code[CountryIso3Code["URY"] = 236] = "URY";
    CountryIso3Code[CountryIso3Code["UZB"] = 237] = "UZB";
    CountryIso3Code[CountryIso3Code["VUT"] = 238] = "VUT";
    CountryIso3Code[CountryIso3Code["VEN"] = 239] = "VEN";
    CountryIso3Code[CountryIso3Code["VNM"] = 240] = "VNM";
    CountryIso3Code[CountryIso3Code["VGB"] = 241] = "VGB";
    CountryIso3Code[CountryIso3Code["VIR"] = 242] = "VIR";
    CountryIso3Code[CountryIso3Code["WLF"] = 243] = "WLF";
    CountryIso3Code[CountryIso3Code["ESH"] = 244] = "ESH";
    CountryIso3Code[CountryIso3Code["YEM"] = 245] = "YEM";
    CountryIso3Code[CountryIso3Code["ZMB"] = 246] = "ZMB";
    CountryIso3Code[CountryIso3Code["ZWE"] = 247] = "ZWE";
})(CountryIso3Code || (CountryIso3Code = {}));
const countryIso3CodeOptions = mapEnumToOptions$1(CountryIso3Code);

class CountryService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/country',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/country/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/country/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/country/all',
            params: { disabled: input.disabled },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/country',
            params: { disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getOptions = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/country/${id}/options`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/country/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/country/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/country/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (options, id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/country/${id}/options`,
            body: options,
        }, { apiName: this.apiName, ...config });
    }
}
CountryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CountryService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CountryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CountryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CountryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var CurrencyIsoCode;
(function (CurrencyIsoCode) {
    CurrencyIsoCode[CurrencyIsoCode["AED"] = 0] = "AED";
    CurrencyIsoCode[CurrencyIsoCode["AFN"] = 1] = "AFN";
    CurrencyIsoCode[CurrencyIsoCode["ALL"] = 2] = "ALL";
    CurrencyIsoCode[CurrencyIsoCode["AMD"] = 3] = "AMD";
    CurrencyIsoCode[CurrencyIsoCode["ANG"] = 4] = "ANG";
    CurrencyIsoCode[CurrencyIsoCode["AOA"] = 5] = "AOA";
    CurrencyIsoCode[CurrencyIsoCode["ARS"] = 6] = "ARS";
    CurrencyIsoCode[CurrencyIsoCode["AUD"] = 7] = "AUD";
    CurrencyIsoCode[CurrencyIsoCode["AWG"] = 8] = "AWG";
    CurrencyIsoCode[CurrencyIsoCode["AZN"] = 9] = "AZN";
    CurrencyIsoCode[CurrencyIsoCode["BAM"] = 10] = "BAM";
    CurrencyIsoCode[CurrencyIsoCode["BBD"] = 11] = "BBD";
    CurrencyIsoCode[CurrencyIsoCode["BDT"] = 12] = "BDT";
    CurrencyIsoCode[CurrencyIsoCode["BGN"] = 13] = "BGN";
    CurrencyIsoCode[CurrencyIsoCode["BHD"] = 14] = "BHD";
    CurrencyIsoCode[CurrencyIsoCode["BIF"] = 15] = "BIF";
    CurrencyIsoCode[CurrencyIsoCode["BMD"] = 16] = "BMD";
    CurrencyIsoCode[CurrencyIsoCode["BND"] = 17] = "BND";
    CurrencyIsoCode[CurrencyIsoCode["BOB"] = 18] = "BOB";
    CurrencyIsoCode[CurrencyIsoCode["BRL"] = 19] = "BRL";
    CurrencyIsoCode[CurrencyIsoCode["BSD"] = 20] = "BSD";
    CurrencyIsoCode[CurrencyIsoCode["BTN"] = 21] = "BTN";
    CurrencyIsoCode[CurrencyIsoCode["BWP"] = 22] = "BWP";
    CurrencyIsoCode[CurrencyIsoCode["BYN"] = 23] = "BYN";
    CurrencyIsoCode[CurrencyIsoCode["BYR"] = 24] = "BYR";
    CurrencyIsoCode[CurrencyIsoCode["BZD"] = 25] = "BZD";
    CurrencyIsoCode[CurrencyIsoCode["CAD"] = 26] = "CAD";
    CurrencyIsoCode[CurrencyIsoCode["CDF"] = 27] = "CDF";
    CurrencyIsoCode[CurrencyIsoCode["CHF"] = 28] = "CHF";
    CurrencyIsoCode[CurrencyIsoCode["CLP"] = 29] = "CLP";
    CurrencyIsoCode[CurrencyIsoCode["CNY"] = 30] = "CNY";
    CurrencyIsoCode[CurrencyIsoCode["COP"] = 31] = "COP";
    CurrencyIsoCode[CurrencyIsoCode["CRC"] = 32] = "CRC";
    CurrencyIsoCode[CurrencyIsoCode["CUC"] = 33] = "CUC";
    CurrencyIsoCode[CurrencyIsoCode["CUP"] = 34] = "CUP";
    CurrencyIsoCode[CurrencyIsoCode["CVE"] = 35] = "CVE";
    CurrencyIsoCode[CurrencyIsoCode["CZK"] = 36] = "CZK";
    CurrencyIsoCode[CurrencyIsoCode["DJF"] = 37] = "DJF";
    CurrencyIsoCode[CurrencyIsoCode["DKK"] = 38] = "DKK";
    CurrencyIsoCode[CurrencyIsoCode["DOP"] = 39] = "DOP";
    CurrencyIsoCode[CurrencyIsoCode["DZD"] = 40] = "DZD";
    CurrencyIsoCode[CurrencyIsoCode["EGP"] = 41] = "EGP";
    CurrencyIsoCode[CurrencyIsoCode["ERN"] = 42] = "ERN";
    CurrencyIsoCode[CurrencyIsoCode["ETB"] = 43] = "ETB";
    CurrencyIsoCode[CurrencyIsoCode["EUR"] = 44] = "EUR";
    CurrencyIsoCode[CurrencyIsoCode["FJD"] = 45] = "FJD";
    CurrencyIsoCode[CurrencyIsoCode["FKP"] = 46] = "FKP";
    CurrencyIsoCode[CurrencyIsoCode["GBP"] = 47] = "GBP";
    CurrencyIsoCode[CurrencyIsoCode["GEL"] = 48] = "GEL";
    CurrencyIsoCode[CurrencyIsoCode["GGP"] = 49] = "GGP";
    CurrencyIsoCode[CurrencyIsoCode["GHS"] = 50] = "GHS";
    CurrencyIsoCode[CurrencyIsoCode["GIP"] = 51] = "GIP";
    CurrencyIsoCode[CurrencyIsoCode["GMD"] = 52] = "GMD";
    CurrencyIsoCode[CurrencyIsoCode["GNF"] = 53] = "GNF";
    CurrencyIsoCode[CurrencyIsoCode["GTQ"] = 54] = "GTQ";
    CurrencyIsoCode[CurrencyIsoCode["GYD"] = 55] = "GYD";
    CurrencyIsoCode[CurrencyIsoCode["HKD"] = 56] = "HKD";
    CurrencyIsoCode[CurrencyIsoCode["HNL"] = 57] = "HNL";
    CurrencyIsoCode[CurrencyIsoCode["HRK"] = 58] = "HRK";
    CurrencyIsoCode[CurrencyIsoCode["HTG"] = 59] = "HTG";
    CurrencyIsoCode[CurrencyIsoCode["HUF"] = 60] = "HUF";
    CurrencyIsoCode[CurrencyIsoCode["IDR"] = 61] = "IDR";
    CurrencyIsoCode[CurrencyIsoCode["IMP"] = 62] = "IMP";
    CurrencyIsoCode[CurrencyIsoCode["INR"] = 63] = "INR";
    CurrencyIsoCode[CurrencyIsoCode["IQD"] = 64] = "IQD";
    CurrencyIsoCode[CurrencyIsoCode["IRR"] = 65] = "IRR";
    CurrencyIsoCode[CurrencyIsoCode["ISK"] = 66] = "ISK";
    CurrencyIsoCode[CurrencyIsoCode["JEP"] = 67] = "JEP";
    CurrencyIsoCode[CurrencyIsoCode["JMD"] = 68] = "JMD";
    CurrencyIsoCode[CurrencyIsoCode["JOD"] = 69] = "JOD";
    CurrencyIsoCode[CurrencyIsoCode["JPY"] = 70] = "JPY";
    CurrencyIsoCode[CurrencyIsoCode["KES"] = 71] = "KES";
    CurrencyIsoCode[CurrencyIsoCode["KGS"] = 72] = "KGS";
    CurrencyIsoCode[CurrencyIsoCode["KHR"] = 73] = "KHR";
    CurrencyIsoCode[CurrencyIsoCode["KMF"] = 74] = "KMF";
    CurrencyIsoCode[CurrencyIsoCode["KPW"] = 75] = "KPW";
    CurrencyIsoCode[CurrencyIsoCode["KRW"] = 76] = "KRW";
    CurrencyIsoCode[CurrencyIsoCode["KWD"] = 77] = "KWD";
    CurrencyIsoCode[CurrencyIsoCode["KYD"] = 78] = "KYD";
    CurrencyIsoCode[CurrencyIsoCode["KZT"] = 79] = "KZT";
    CurrencyIsoCode[CurrencyIsoCode["LAK"] = 80] = "LAK";
    CurrencyIsoCode[CurrencyIsoCode["LBP"] = 81] = "LBP";
    CurrencyIsoCode[CurrencyIsoCode["LKR"] = 82] = "LKR";
    CurrencyIsoCode[CurrencyIsoCode["LRD"] = 83] = "LRD";
    CurrencyIsoCode[CurrencyIsoCode["LSL"] = 84] = "LSL";
    CurrencyIsoCode[CurrencyIsoCode["LYD"] = 85] = "LYD";
    CurrencyIsoCode[CurrencyIsoCode["MAD"] = 86] = "MAD";
    CurrencyIsoCode[CurrencyIsoCode["MDL"] = 87] = "MDL";
    CurrencyIsoCode[CurrencyIsoCode["MGA"] = 88] = "MGA";
    CurrencyIsoCode[CurrencyIsoCode["MKD"] = 89] = "MKD";
    CurrencyIsoCode[CurrencyIsoCode["MMK"] = 90] = "MMK";
    CurrencyIsoCode[CurrencyIsoCode["MNT"] = 91] = "MNT";
    CurrencyIsoCode[CurrencyIsoCode["MOP"] = 92] = "MOP";
    CurrencyIsoCode[CurrencyIsoCode["MRO"] = 93] = "MRO";
    CurrencyIsoCode[CurrencyIsoCode["MUR"] = 94] = "MUR";
    CurrencyIsoCode[CurrencyIsoCode["MVR"] = 95] = "MVR";
    CurrencyIsoCode[CurrencyIsoCode["MWK"] = 96] = "MWK";
    CurrencyIsoCode[CurrencyIsoCode["MXN"] = 97] = "MXN";
    CurrencyIsoCode[CurrencyIsoCode["MYR"] = 98] = "MYR";
    CurrencyIsoCode[CurrencyIsoCode["MZN"] = 99] = "MZN";
    CurrencyIsoCode[CurrencyIsoCode["NAD"] = 100] = "NAD";
    CurrencyIsoCode[CurrencyIsoCode["NGN"] = 101] = "NGN";
    CurrencyIsoCode[CurrencyIsoCode["NIO"] = 102] = "NIO";
    CurrencyIsoCode[CurrencyIsoCode["NOK"] = 103] = "NOK";
    CurrencyIsoCode[CurrencyIsoCode["NPR"] = 104] = "NPR";
    CurrencyIsoCode[CurrencyIsoCode["NZD"] = 105] = "NZD";
    CurrencyIsoCode[CurrencyIsoCode["OMR"] = 106] = "OMR";
    CurrencyIsoCode[CurrencyIsoCode["PAB"] = 107] = "PAB";
    CurrencyIsoCode[CurrencyIsoCode["PEN"] = 108] = "PEN";
    CurrencyIsoCode[CurrencyIsoCode["PGK"] = 109] = "PGK";
    CurrencyIsoCode[CurrencyIsoCode["PHP"] = 110] = "PHP";
    CurrencyIsoCode[CurrencyIsoCode["PKR"] = 111] = "PKR";
    CurrencyIsoCode[CurrencyIsoCode["PLN"] = 112] = "PLN";
    CurrencyIsoCode[CurrencyIsoCode["PYG"] = 113] = "PYG";
    CurrencyIsoCode[CurrencyIsoCode["QAR"] = 114] = "QAR";
    CurrencyIsoCode[CurrencyIsoCode["RON"] = 115] = "RON";
    CurrencyIsoCode[CurrencyIsoCode["RSD"] = 116] = "RSD";
    CurrencyIsoCode[CurrencyIsoCode["RUB"] = 117] = "RUB";
    CurrencyIsoCode[CurrencyIsoCode["RWF"] = 118] = "RWF";
    CurrencyIsoCode[CurrencyIsoCode["SAR"] = 119] = "SAR";
    CurrencyIsoCode[CurrencyIsoCode["SBD"] = 120] = "SBD";
    CurrencyIsoCode[CurrencyIsoCode["SCR"] = 121] = "SCR";
    CurrencyIsoCode[CurrencyIsoCode["SDG"] = 122] = "SDG";
    CurrencyIsoCode[CurrencyIsoCode["SEK"] = 123] = "SEK";
    CurrencyIsoCode[CurrencyIsoCode["SGD"] = 124] = "SGD";
    CurrencyIsoCode[CurrencyIsoCode["SHP"] = 125] = "SHP";
    CurrencyIsoCode[CurrencyIsoCode["SLL"] = 126] = "SLL";
    CurrencyIsoCode[CurrencyIsoCode["SOS"] = 127] = "SOS";
    CurrencyIsoCode[CurrencyIsoCode["SPL"] = 128] = "SPL";
    CurrencyIsoCode[CurrencyIsoCode["SRD"] = 129] = "SRD";
    CurrencyIsoCode[CurrencyIsoCode["STD"] = 130] = "STD";
    CurrencyIsoCode[CurrencyIsoCode["SVC"] = 131] = "SVC";
    CurrencyIsoCode[CurrencyIsoCode["SYP"] = 132] = "SYP";
    CurrencyIsoCode[CurrencyIsoCode["SZL"] = 133] = "SZL";
    CurrencyIsoCode[CurrencyIsoCode["THB"] = 134] = "THB";
    CurrencyIsoCode[CurrencyIsoCode["TJS"] = 135] = "TJS";
    CurrencyIsoCode[CurrencyIsoCode["TMT"] = 136] = "TMT";
    CurrencyIsoCode[CurrencyIsoCode["TND"] = 137] = "TND";
    CurrencyIsoCode[CurrencyIsoCode["TOP"] = 138] = "TOP";
    CurrencyIsoCode[CurrencyIsoCode["TRY"] = 139] = "TRY";
    CurrencyIsoCode[CurrencyIsoCode["TTD"] = 140] = "TTD";
    CurrencyIsoCode[CurrencyIsoCode["TVD"] = 141] = "TVD";
    CurrencyIsoCode[CurrencyIsoCode["TWD"] = 142] = "TWD";
    CurrencyIsoCode[CurrencyIsoCode["TZS"] = 143] = "TZS";
    CurrencyIsoCode[CurrencyIsoCode["UAH"] = 144] = "UAH";
    CurrencyIsoCode[CurrencyIsoCode["UGX"] = 145] = "UGX";
    CurrencyIsoCode[CurrencyIsoCode["USD"] = 146] = "USD";
    CurrencyIsoCode[CurrencyIsoCode["UYU"] = 147] = "UYU";
    CurrencyIsoCode[CurrencyIsoCode["UZS"] = 148] = "UZS";
    CurrencyIsoCode[CurrencyIsoCode["VEF"] = 149] = "VEF";
    CurrencyIsoCode[CurrencyIsoCode["VND"] = 150] = "VND";
    CurrencyIsoCode[CurrencyIsoCode["VUV"] = 151] = "VUV";
    CurrencyIsoCode[CurrencyIsoCode["WST"] = 152] = "WST";
    CurrencyIsoCode[CurrencyIsoCode["XAF"] = 153] = "XAF";
    CurrencyIsoCode[CurrencyIsoCode["XCD"] = 154] = "XCD";
    CurrencyIsoCode[CurrencyIsoCode["XDR"] = 155] = "XDR";
    CurrencyIsoCode[CurrencyIsoCode["XOF"] = 156] = "XOF";
    CurrencyIsoCode[CurrencyIsoCode["XPF"] = 157] = "XPF";
    CurrencyIsoCode[CurrencyIsoCode["YER"] = 158] = "YER";
    CurrencyIsoCode[CurrencyIsoCode["ZAR"] = 159] = "ZAR";
    CurrencyIsoCode[CurrencyIsoCode["ZMW"] = 160] = "ZMW";
    CurrencyIsoCode[CurrencyIsoCode["ZWD"] = 161] = "ZWD";
})(CurrencyIsoCode || (CurrencyIsoCode = {}));
const currencyIsoCodeOptions = mapEnumToOptions$1(CurrencyIsoCode);

var CurrencyPosition;
(function (CurrencyPosition) {
    CurrencyPosition[CurrencyPosition["Before"] = 0] = "Before";
    CurrencyPosition[CurrencyPosition["After"] = 1] = "After";
})(CurrencyPosition || (CurrencyPosition = {}));
const currencyPositionOptions = mapEnumToOptions$1(CurrencyPosition);

class CurrencyService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/currency',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/currency/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/currency/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/currency/all',
            params: { disabled: input.disabled },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/currency',
            params: { disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/currency/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/currency/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/currency/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
CurrencyService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CurrencyService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CurrencyService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CurrencyService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CurrencyService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var GeoAreaFieldType;
(function (GeoAreaFieldType) {
    GeoAreaFieldType[GeoAreaFieldType["Dropdown"] = 0] = "Dropdown";
    GeoAreaFieldType[GeoAreaFieldType["Text"] = 1] = "Text";
    GeoAreaFieldType[GeoAreaFieldType["Number"] = 2] = "Number";
})(GeoAreaFieldType || (GeoAreaFieldType = {}));
const geoAreaFieldTypeOptions = mapEnumToOptions$1(GeoAreaFieldType);

var GeoAreaType;
(function (GeoAreaType) {
    GeoAreaType[GeoAreaType["StateOrProvince"] = 0] = "StateOrProvince";
    GeoAreaType[GeoAreaType["City"] = 1] = "City";
    GeoAreaType[GeoAreaType["District"] = 2] = "District";
})(GeoAreaType || (GeoAreaType = {}));
const geoAreaTypeOptions = mapEnumToOptions$1(GeoAreaType);

class GeoAreaService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/geo-area',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/geo-area/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/geo-area/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/geo-area',
            params: { parentId: input.parentId, disabled: input.disabled, countryId: input.countryId, type: input.type, search: input.search, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/geo-area/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/geo-area/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.putReorderGeoAreaByInput = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/geo-area/reorder-geo-area',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/geo-area/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
GeoAreaService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: GeoAreaService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
GeoAreaService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: GeoAreaService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: GeoAreaService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var ImageTarget;
(function (ImageTarget) {
    ImageTarget[ImageTarget["Category"] = 1] = "Category";
    ImageTarget[ImageTarget["ItemThumbnail"] = 2] = "ItemThumbnail";
    ImageTarget[ImageTarget["Item"] = 3] = "Item";
    ImageTarget[ImageTarget["Banner"] = 4] = "Banner";
    ImageTarget[ImageTarget["Notification"] = 5] = "Notification";
    ImageTarget[ImageTarget["Others"] = 6] = "Others";
    ImageTarget[ImageTarget["Icon"] = 7] = "Icon";
    ImageTarget[ImageTarget["Outlet"] = 8] = "Outlet";
    ImageTarget[ImageTarget["Review"] = 9] = "Review";
})(ImageTarget || (ImageTarget = {}));
const imageTargetOptions = mapEnumToOptions$1(ImageTarget);

class ImageService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/image',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/image/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/image/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/image',
            params: { sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/image/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ImageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ImageService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ImageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ImageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ImageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class IndustryService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/industry',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/industry/${id}`,
        }, { apiName: this.apiName, ...config });
        this.deleteTranslation = (id, language, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/industry/${id}/translation`,
            params: { language },
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/industry/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/industry/all',
            params: { parentId: input.parentId, disabled: input.disabled },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/industry',
            params: { parentId: input.parentId, disabled: input.disabled, defaultThemeId: input.defaultThemeId, defaultMobileThemeId: input.defaultMobileThemeId, defaultStoreId: input.defaultStoreId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getStoreOptions = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/industry/${id}/store-options`,
        }, { apiName: this.apiName, ...config });
        this.getTranslations = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/industry/${id}/translations`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/industry/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/industry/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.putTranslations = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/industry/${id}/translations`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/industry/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateStoreOptions = (options, industryId, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/industry/store-options/${industryId}`,
            body: options,
        }, { apiName: this.apiName, ...config });
    }
}
IndustryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: IndustryService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
IndustryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: IndustryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: IndustryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var CouponIncludedDetailType;
(function (CouponIncludedDetailType) {
    CouponIncludedDetailType[CouponIncludedDetailType["Product"] = 1] = "Product";
    CouponIncludedDetailType[CouponIncludedDetailType["Category"] = 2] = "Category";
    CouponIncludedDetailType[CouponIncludedDetailType["User"] = 3] = "User";
})(CouponIncludedDetailType || (CouponIncludedDetailType = {}));
const couponIncludedDetailTypeOptions = mapEnumToOptions$1(CouponIncludedDetailType);

var CouponType;
(function (CouponType) {
    CouponType[CouponType["Percentage"] = 1] = "Percentage";
    CouponType[CouponType["FixedAmount"] = 2] = "FixedAmount";
})(CouponType || (CouponType = {}));
const couponTypeOptions = mapEnumToOptions$1(CouponType);

class CouponService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.attach = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/coupon/${id}/attach`,
        }, { apiName: this.apiName, ...config });
        this.checkEligibility = (code, subTotalPrice, relatedStoreId, currencyId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/coupon/${code}`,
            params: { subTotalPrice, relatedStoreId, currencyId },
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/coupon',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/coupon/${id}`,
        }, { apiName: this.apiName, ...config });
        this.detach = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/coupon/${id}/detach`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/coupon/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/coupon',
            params: { search: input.search, isPublished: input.isPublished, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMutual = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/coupon/mutual',
            params: { search: input.search, isPublished: input.isPublished, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.publish = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/coupon/${id}/publish`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/coupon/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/coupon/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.unpublish = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/coupon/${id}/unpublish`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/coupon/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
CouponService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CouponService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CouponService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CouponService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CouponService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class CustomerBusinessTypeService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customer-business-type',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/customer-business-type/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/customer-business-type/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customer-business-type',
            params: { sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer-business-type/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer-business-type/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer-business-type/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
CustomerBusinessTypeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerBusinessTypeService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CustomerBusinessTypeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerBusinessTypeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerBusinessTypeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class CustomerFavoriteItemService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.checkExist = (itemId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/customer-favorite-item/check-exist/${itemId}`,
        }, { apiName: this.apiName, ...config });
        this.checkExistMarketplace = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customer-favorite-item/marketplace/check-exist',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customer-favorite-item',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createMarketplace = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customer-favorite-item/marketplace',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (itemId, config) => this.restService.request({
            method: 'DELETE',
            url: '/api/app/customer-favorite-item',
            params: { itemId },
        }, { apiName: this.apiName, ...config });
        this.deleteMarketplace = (input, config) => this.restService.request({
            method: 'DELETE',
            url: '/api/app/customer-favorite-item/marketplace',
            params: { tenantId: input.tenantId, itemId: input.itemId },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customer-favorite-item',
            params: { outletId: input.outletId, currencyId: input.currencyId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMarketplace = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customer-favorite-item/marketplace',
            params: { outletId: input.outletId, currencyId: input.currencyId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
    }
}
CustomerFavoriteItemService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerFavoriteItemService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CustomerFavoriteItemService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerFavoriteItemService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerFavoriteItemService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class CustomerGroupService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customer-group',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/customer-group/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/customer-group/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customer-group',
            params: { disabled: input.disabled, priceListId: input.priceListId, defaultOutletId: input.defaultOutletId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer-group/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer-group/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer-group/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
CustomerGroupService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerGroupService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CustomerGroupService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerGroupService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerGroupService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var CustomerType;
(function (CustomerType) {
    CustomerType[CustomerType["Individual"] = 0] = "Individual";
    CustomerType[CustomerType["Business"] = 1] = "Business";
})(CustomerType || (CustomerType = {}));
const customerTypeOptions = mapEnumToOptions$1(CustomerType);

class CustomerService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.addNumberAndInvitationCodeToCustomers = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customer/number-and-invitation-code-to-customers',
        }, { apiName: this.apiName, ...config });
        this.changeLanguage = (lang, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/customer/change-language/${lang}`,
        }, { apiName: this.apiName, ...config });
        this.checkInvitationCode = (code, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customer/check-invitation-code',
            params: { code },
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customer',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/customer/${id}`,
        }, { apiName: this.apiName, ...config });
        this.deleteAccount = (config) => this.restService.request({
            method: 'DELETE',
            url: '/api/app/customer/account',
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/customer/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getExport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customer/export',
            params: { isTemplateOnly: input.isTemplateOnly, ids: input.ids, priceListId: input.priceListId, customerGroupId: input.customerGroupId, customerBusinessTypeId: input.customerBusinessTypeId, branchId: input.branchId, countryId: input.countryId, stateProvinceId: input.stateProvinceId, stateProvinceName: input.stateProvinceName, cityId: input.cityId, cityName: input.cityName, districtId: input.districtId, zipPostalCode: input.zipPostalCode, filter: input.filter, address: input.address, isClient: input.isClient, isSupplier: input.isSupplier, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customer',
            params: { ids: input.ids, priceListId: input.priceListId, customerGroupId: input.customerGroupId, customerBusinessTypeId: input.customerBusinessTypeId, branchId: input.branchId, countryId: input.countryId, stateProvinceId: input.stateProvinceId, stateProvinceName: input.stateProvinceName, cityId: input.cityId, cityName: input.cityName, districtId: input.districtId, zipPostalCode: input.zipPostalCode, filter: input.filter, address: input.address, isClient: input.isClient, isSupplier: input.isSupplier, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMyCustomerProfile = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customer/my-customer-profile',
        }, { apiName: this.apiName, ...config });
        this.getMyCustomerSummary = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customer/my-customer-summary',
        }, { apiName: this.apiName, ...config });
        this.getSummaryReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customer/summary-report',
            params: { customerId: input.customerId, fromDate: input.fromDate, toDate: input.toDate },
        }, { apiName: this.apiName, ...config });
        this.putCustomerDefaultAddress = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer/${id}/customer-default-address`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putCustomerDefaultDeliveryMethodZoneRate = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer/${id}/customer-default-delivery-method-zone-rate`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putCustomerDefaultPaymentMethod = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer/${id}/customer-default-payment-method`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putCustomerEmail = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer/${id}/customer-email`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putCustomerPhoneNumber = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer/${id}/customer-phone-number`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putUnverified = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer/${id}/unverified`,
        }, { apiName: this.apiName, ...config });
        this.putUpdateProfileByInput = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/customer/update-profile',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putVerified = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer/${id}/verified`,
        }, { apiName: this.apiName, ...config });
        this.signup = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customer/signup',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.statementReport = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customer/statement-report',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
CustomerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CustomerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var TransactionType;
(function (TransactionType) {
    TransactionType[TransactionType["Invoice"] = 0] = "Invoice";
    TransactionType[TransactionType["Payment"] = 1] = "Payment";
})(TransactionType || (TransactionType = {}));
const transactionTypeOptions = mapEnumToOptions$1(TransactionType);

var DeliveryMethodCoveredAreaLimitation;
(function (DeliveryMethodCoveredAreaLimitation) {
    DeliveryMethodCoveredAreaLimitation[DeliveryMethodCoveredAreaLimitation["NoLimitations"] = 0] = "NoLimitations";
    DeliveryMethodCoveredAreaLimitation[DeliveryMethodCoveredAreaLimitation["GeoAreas"] = 1] = "GeoAreas";
    DeliveryMethodCoveredAreaLimitation[DeliveryMethodCoveredAreaLimitation["MapZone"] = 2] = "MapZone";
})(DeliveryMethodCoveredAreaLimitation || (DeliveryMethodCoveredAreaLimitation = {}));
const deliveryMethodCoveredAreaLimitationOptions = mapEnumToOptions$1(DeliveryMethodCoveredAreaLimitation);

var DeliveryMethodProvider;
(function (DeliveryMethodProvider) {
    DeliveryMethodProvider[DeliveryMethodProvider["Manual"] = 0] = "Manual";
    DeliveryMethodProvider[DeliveryMethodProvider["Oto"] = 1] = "Oto";
    DeliveryMethodProvider[DeliveryMethodProvider["Kosmo"] = 2] = "Kosmo";
    DeliveryMethodProvider[DeliveryMethodProvider["DhlExpress"] = 3] = "DhlExpress";
})(DeliveryMethodProvider || (DeliveryMethodProvider = {}));
const deliveryMethodProviderOptions = mapEnumToOptions$1(DeliveryMethodProvider);

var DeliveryMethodRateType;
(function (DeliveryMethodRateType) {
    DeliveryMethodRateType[DeliveryMethodRateType["FixedPrice"] = 0] = "FixedPrice";
    DeliveryMethodRateType[DeliveryMethodRateType["ByWeight"] = 1] = "ByWeight";
})(DeliveryMethodRateType || (DeliveryMethodRateType = {}));
const deliveryMethodRateTypeOptions = mapEnumToOptions$1(DeliveryMethodRateType);

class DeliveryMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/delivery-method',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/delivery-method/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/delivery-method/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getDeliveryGateways = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/delivery-method/delivery-gateways',
        }, { apiName: this.apiName, ...config });
        this.getDeliveryMethodZoneRates = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/delivery-method/delivery-method-zone-rates',
            params: { tenantId: input.tenantId, outletId: input.outletId, currencyId: input.currencyId, countryId: input.countryId, stateProvinceId: input.stateProvinceId, stateProvinceName: input.stateProvinceName, cityId: input.cityId, cityName: input.cityName, districtId: input.districtId, zipPostalCode: input.zipPostalCode, longitude: input.longitude, latitude: input.latitude, accuracy: input.accuracy, deliveryMethodsIds: input.deliveryMethodsIds, totalWeights: input.totalWeights },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/delivery-method',
            params: { disabled: input.disabled, provider: input.provider, providerType: input.providerType, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMyArea = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/delivery-method/my-area',
        }, { apiName: this.apiName, ...config });
        this.getOptions = (provider, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/delivery-method/options/${provider}`,
        }, { apiName: this.apiName, ...config });
        this.getQuickCheckoutInfo = (outletId, currencyId, totalWeights, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/delivery-method/quick-checkout-info',
            params: { outletId, currencyId, totalWeights },
        }, { apiName: this.apiName, ...config });
        this.getQuotations = (provider, model, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/delivery-method/quotations/${provider}`,
            params: { packageId: model.packageId, deliveryMethodZoneRateId: model.deliveryMethodZoneRateId, plannedShippingDate: model.plannedShippingDate },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/delivery-method/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/delivery-method/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.shipment = (provider, model, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/delivery-method/shipment/${provider}`,
            body: model,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/delivery-method/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (provider, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/delivery-method/options/${provider}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
DeliveryMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DeliveryMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
DeliveryMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DeliveryMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DeliveryMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class DhlExpressDeliveryMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.createPickup = (payload, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/dhl-express-delivery-method/pickup',
            body: payload,
        }, { apiName: this.apiName, ...config });
        this.deletePickup = (payload, config) => this.restService.request({
            method: 'DELETE',
            url: '/api/app/dhl-express-delivery-method/pickup',
            params: { dispatchConfirmationNumber: payload.dispatchConfirmationNumber, requestorName: payload.requestorName, reason: payload.reason },
        }, { apiName: this.apiName, ...config });
        this.getAddressValidate = (payload, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/dhl-express-delivery-method/address-validate',
            params: { type: payload.type, countryCode: payload.countryCode, postalCode: payload.postalCode, cityName: payload.cityName, countyName: payload.countyName, strictValidation: payload.strictValidation, isOrigin: payload.isOrigin },
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/dhl-express-delivery-method/options',
        }, { apiName: this.apiName, ...config });
        this.getTracking = (payload, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/dhl-express-delivery-method/tracking',
            params: { shipmentTrackingNumber: payload.shipmentTrackingNumber, pieceTrackingNumber: payload.pieceTrackingNumber, trackingView: payload.trackingView, levelOfDetail: payload.levelOfDetail, requestControlledAccessDataCodes: payload.requestControlledAccessDataCodes, requestGMTOffsetPerEvent: payload.requestGMTOffsetPerEvent },
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (payload, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/dhl-express-delivery-method/options',
            body: payload,
        }, { apiName: this.apiName, ...config });
    }
}
DhlExpressDeliveryMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DhlExpressDeliveryMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
DhlExpressDeliveryMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DhlExpressDeliveryMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: DhlExpressDeliveryMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class KosmoDeliveryMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/kosmo-delivery-method/options',
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/kosmo-delivery-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
KosmoDeliveryMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: KosmoDeliveryMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
KosmoDeliveryMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: KosmoDeliveryMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: KosmoDeliveryMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var KosmoType;
(function (KosmoType) {
    KosmoType[KosmoType["ASAP"] = 0] = "ASAP";
    KosmoType[KosmoType["TwoHours"] = 1] = "TwoHours";
    KosmoType[KosmoType["FourHours"] = 2] = "FourHours";
    KosmoType[KosmoType["NextDay"] = 3] = "NextDay";
    KosmoType[KosmoType["NextThreeDays"] = 4] = "NextThreeDays";
})(KosmoType || (KosmoType = {}));
const kosmoTypeOptions = mapEnumToOptions$1(KosmoType);

class OtoDeliveryMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/oto-delivery-method/options',
        }, { apiName: this.apiName, ...config });
        this.getPrintAWBUrl = (model, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/oto-delivery-method/print-aWBUrl',
            params: { orderId: model.orderId },
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/oto-delivery-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
OtoDeliveryMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OtoDeliveryMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
OtoDeliveryMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OtoDeliveryMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OtoDeliveryMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var OtoType;
(function (OtoType) {
    OtoType[OtoType["Express"] = 0] = "Express";
    OtoType[OtoType["SameDay"] = 1] = "SameDay";
    OtoType[OtoType["FastDelivery"] = 2] = "FastDelivery";
    OtoType[OtoType["ColdDelivery"] = 3] = "ColdDelivery";
    OtoType[OtoType["HeavyAndBulky"] = 4] = "HeavyAndBulky";
    OtoType[OtoType["ElectronicAndHeavy"] = 5] = "ElectronicAndHeavy";
})(OtoType || (OtoType = {}));
const otoTypeOptions = mapEnumToOptions$1(OtoType);

class PackageScaleService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/package-scale',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/package-scale/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/package-scale/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getDimensions = (weight, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/package-scale/dimensions',
            params: { weight },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/package-scale',
            params: { sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/package-scale/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
PackageScaleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PackageScaleService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
PackageScaleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PackageScaleService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PackageScaleService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class PackageService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/package',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/package/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/package/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/package',
            params: { search: input.search, orderId: input.orderId, fromDate: input.fromDate, toDate: input.toDate, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/package/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
PackageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PackageService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
PackageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PackageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PackageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ShipmentService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.changeStatus = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/shipment/change-status',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/shipment',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/shipment/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/shipment/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getHistory = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/shipment/history',
            params: { shipmentId: input.shipmentId, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/shipment',
            params: { search: input.search, orderId: input.orderId, packageId: input.packageId, deliveryMethodId: input.deliveryMethodId, providerQuotationId: input.providerQuotationId, providerOrderId: input.providerOrderId, dispatcherId: input.dispatcherId, status: input.status, fromDate: input.fromDate, toDate: input.toDate, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/shipment/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ShipmentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ShipmentService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ShipmentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ShipmentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ShipmentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var ExternalServiceCategory;
(function (ExternalServiceCategory) {
    ExternalServiceCategory[ExternalServiceCategory["Marketing"] = 0] = "Marketing";
    ExternalServiceCategory[ExternalServiceCategory["Statistical"] = 1] = "Statistical";
    ExternalServiceCategory[ExternalServiceCategory["Chat"] = 2] = "Chat";
})(ExternalServiceCategory || (ExternalServiceCategory = {}));
const externalServiceCategoryOptions = mapEnumToOptions$1(ExternalServiceCategory);

class ExternalServiceService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/external-service/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getChatraIdOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/chatra-id-options',
        }, { apiName: this.apiName, ...config });
        this.getCrazyEggOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/crazy-egg-options',
        }, { apiName: this.apiName, ...config });
        this.getExpertOrderOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/expert-order-options',
        }, { apiName: this.apiName, ...config });
        this.getFacebookAppOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/facebook-app-options',
        }, { apiName: this.apiName, ...config });
        this.getFacebookCatalogFeedOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/facebook-catalog-feed-options',
        }, { apiName: this.apiName, ...config });
        this.getFacebookDVKOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/facebook-dVKOptions',
        }, { apiName: this.apiName, ...config });
        this.getFacebookMessengerOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/facebook-messenger-options',
        }, { apiName: this.apiName, ...config });
        this.getFacebookPixelOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/facebook-pixel-options',
        }, { apiName: this.apiName, ...config });
        this.getFreshworksOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/freshworks-options',
        }, { apiName: this.apiName, ...config });
        this.getGoogleAdWordsOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/google-ad-words-options',
        }, { apiName: this.apiName, ...config });
        this.getGoogleAnalyticOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/google-analytic-options',
        }, { apiName: this.apiName, ...config });
        this.getGoogleConsoleOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/google-console-options',
        }, { apiName: this.apiName, ...config });
        this.getGoogleMerchentOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/google-merchent-options',
        }, { apiName: this.apiName, ...config });
        this.getGoogleTagManagerOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/google-tag-manager-options',
        }, { apiName: this.apiName, ...config });
        this.getHotJarOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/hot-jar-options',
        }, { apiName: this.apiName, ...config });
        this.getList = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service',
        }, { apiName: this.apiName, ...config });
        this.getMailChimpOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/mail-chimp-options',
        }, { apiName: this.apiName, ...config });
        this.getSnapChatCatalogOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/snap-chat-catalog-options',
        }, { apiName: this.apiName, ...config });
        this.getSnapChatOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/snap-chat-options',
        }, { apiName: this.apiName, ...config });
        this.getSnapOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/snap-options',
        }, { apiName: this.apiName, ...config });
        this.getTawkOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/tawk-options',
        }, { apiName: this.apiName, ...config });
        this.getTidioOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/tidio-options',
        }, { apiName: this.apiName, ...config });
        this.getTiktookOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/external-service/tiktook-options',
        }, { apiName: this.apiName, ...config });
        this.updateChatraIdOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/chatra-id-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateCrazyEggOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/crazy-egg-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateExpertOrderOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/expert-order-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateFacebookAppOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/facebook-app-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateFacebookCatalogFeedOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/facebook-catalog-feed-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateFacebookDVKOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/facebook-dVKOptions',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateFacebookMessengerOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/facebook-messenger-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateFacebookPixelOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/facebook-pixel-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateFreshworksOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/freshworks-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateGoogleAdWordsOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/google-ad-words-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateGoogleAnalyticOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/google-analytic-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateGoogleConsoleOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/google-console-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateGoogleMerchentOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/google-merchent-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateGoogleTagManagerOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/google-tag-manager-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateHotJarOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/hot-jar-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateMailChimpOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/mail-chimp-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateSnapChatCatalogOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/snap-chat-catalog-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateSnapChatOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/snap-chat-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateSnapOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/snap-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateTawkOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/tawk-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateTidioOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/tidio-options',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateTiktookOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/external-service/tiktook-options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ExternalServiceService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExternalServiceService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ExternalServiceService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExternalServiceService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExternalServiceService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class FeedService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.getFacebook = (domain, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/feed/${domain}/facebook.xml`,
        }, { apiName: this.apiName, ...config });
        this.getGoogleMerchant = (domain, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/feed/${domain}/google-merchant.xml`,
        }, { apiName: this.apiName, ...config });
        this.getMainSitemap = (domain, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/feed/${domain}/main-sitemap.xml`,
        }, { apiName: this.apiName, ...config });
        this.getSitemap = (domain, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/feed/${domain}/sitemap.xml`,
        }, { apiName: this.apiName, ...config });
        this.getSnapchat = (domain, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/feed/${domain}/snapchat.xml`,
        }, { apiName: this.apiName, ...config });
        this.getSubSitemap = (domain, index, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/feed/${domain}/sitemap-${index}.xml`,
        }, { apiName: this.apiName, ...config });
        this.getTikTok = (domain, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/feed/${domain}/tiktok.xml`,
        }, { apiName: this.apiName, ...config });
    }
}
FeedService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FeedService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
FeedService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FeedService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FeedService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class InventoryService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.getItemStockQuantity = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/inventory/item-stock-quantity',
            params: { itemId: input.itemId, itemDetailId: input.itemDetailId, warehouseId: input.warehouseId, categoryId: input.categoryId, disable: input.disable, fromDate: input.fromDate, toDate: input.toDate, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getStockQuantityHistory = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/inventory/stock-quantity-history',
            params: { itemId: input.itemId, warehouseId: input.warehouseId, reference: input.reference, fromDate: input.fromDate, toDate: input.toDate, actionType: input.actionType, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putManageStock = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/inventory/manage-stock',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateItemStockQuantity = (stockQuantity, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/inventory/item-stock-quantity',
            body: stockQuantity,
        }, { apiName: this.apiName, ...config });
    }
}
InventoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InventoryService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
InventoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InventoryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InventoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var StockQuantityHistoryActionType;
(function (StockQuantityHistoryActionType) {
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["InitialStockQuantity"] = 0] = "InitialStockQuantity";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["ManualAdjustment"] = 10] = "ManualAdjustment";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["OrderPosted"] = 15] = "OrderPosted";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["BeginningInventoryInvoicePosted"] = 20] = "BeginningInventoryInvoicePosted";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["BeginningInventoryInvoiceCanceled"] = 21] = "BeginningInventoryInvoiceCanceled";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["PurchaseBillPosted"] = 30] = "PurchaseBillPosted";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["PurchaseBillCanceled"] = 31] = "PurchaseBillCanceled";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["SalesInvoicePosted"] = 40] = "SalesInvoicePosted";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["SalesInvoiceCanceled"] = 41] = "SalesInvoiceCanceled";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["PurchaseReturnBillPosted"] = 50] = "PurchaseReturnBillPosted";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["PurchaseReturnBillCanceled"] = 51] = "PurchaseReturnBillCanceled";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["SalesReturnInvoicePosted"] = 60] = "SalesReturnInvoicePosted";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["SalesReturnInvoiceCanceled"] = 61] = "SalesReturnInvoiceCanceled";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["TransferOrderInPosted"] = 70] = "TransferOrderInPosted";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["TransferOrderInCanceled"] = 71] = "TransferOrderInCanceled";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["TransferOrderOutPosted"] = 72] = "TransferOrderOutPosted";
    StockQuantityHistoryActionType[StockQuantityHistoryActionType["TransferOrderOutCanceled"] = 73] = "TransferOrderOutCanceled";
})(StockQuantityHistoryActionType || (StockQuantityHistoryActionType = {}));
const stockQuantityHistoryActionTypeOptions = mapEnumToOptions$1(StockQuantityHistoryActionType);

var TransferOrderStatus;
(function (TransferOrderStatus) {
    TransferOrderStatus[TransferOrderStatus["Initiated"] = 0] = "Initiated";
    TransferOrderStatus[TransferOrderStatus["Transferred"] = 1] = "Transferred";
    TransferOrderStatus[TransferOrderStatus["Canceled"] = 2] = "Canceled";
})(TransferOrderStatus || (TransferOrderStatus = {}));
const transferOrderStatusOptions = mapEnumToOptions$1(TransferOrderStatus);

class TransferOrderService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/transfer-order',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/transfer-order/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/transfer-order/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/transfer-order',
            params: { search: input.search, sourceWarehouseId: input.sourceWarehouseId, destinationWarehouseId: input.destinationWarehouseId, status: input.status, sourceBranchId: input.sourceBranchId, destinationBranchId: input.destinationBranchId, fromDate: input.fromDate, toDate: input.toDate, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putCancel = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/transfer-order/cancel/${id}`,
        }, { apiName: this.apiName, ...config });
        this.putTransfer = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/transfer-order/transfer/${id}`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/transfer-order/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
TransferOrderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TransferOrderService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
TransferOrderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TransferOrderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TransferOrderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class WarehouseService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/warehouse',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/warehouse/${id}`,
        }, { apiName: this.apiName, ...config });
        this.enableBranchInAllWarehouses = (branchId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/warehouse/enable-branch-in-all-warehouses/${branchId}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/warehouse/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/warehouse/all',
            params: { disabled: input.disabled },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/warehouse',
            params: { disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getListWarehousesToBranch = (branchId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/warehouse/warehouses-to-branch/${branchId}`,
        }, { apiName: this.apiName, ...config });
        this.putDefault = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/warehouse/${id}/default`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/warehouse/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/warehouse/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/warehouse/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
WarehouseService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WarehouseService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
WarehouseService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WarehouseService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: WarehouseService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class InvoiceFileService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/invoice-file',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/invoice-file/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/invoice-file/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice-file',
            params: { sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/invoice-file/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
InvoiceFileService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InvoiceFileService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
InvoiceFileService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InvoiceFileService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InvoiceFileService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var InvoicePaymentStatus;
(function (InvoicePaymentStatus) {
    InvoicePaymentStatus[InvoicePaymentStatus["Unpaid"] = 0] = "Unpaid";
    InvoicePaymentStatus[InvoicePaymentStatus["Pending"] = 5] = "Pending";
    InvoicePaymentStatus[InvoicePaymentStatus["FullyPaid"] = 10] = "FullyPaid";
    InvoicePaymentStatus[InvoicePaymentStatus["PartiallyPaid"] = 11] = "PartiallyPaid";
    InvoicePaymentStatus[InvoicePaymentStatus["OverPaid"] = 12] = "OverPaid";
    InvoicePaymentStatus[InvoicePaymentStatus["Refunded"] = 20] = "Refunded";
    InvoicePaymentStatus[InvoicePaymentStatus["PartiallyRefunded"] = 21] = "PartiallyRefunded";
    InvoicePaymentStatus[InvoicePaymentStatus["OverRefunded"] = 22] = "OverRefunded";
})(InvoicePaymentStatus || (InvoicePaymentStatus = {}));
const invoicePaymentStatusOptions = mapEnumToOptions$1(InvoicePaymentStatus);

var InvoiceStatus;
(function (InvoiceStatus) {
    InvoiceStatus[InvoiceStatus["Open"] = 0] = "Open";
    InvoiceStatus[InvoiceStatus["Canceled"] = 10] = "Canceled";
    InvoiceStatus[InvoiceStatus["Draft"] = 20] = "Draft";
})(InvoiceStatus || (InvoiceStatus = {}));
const invoiceStatusOptions = mapEnumToOptions$1(InvoiceStatus);

var InvoiceTypeMode;
(function (InvoiceTypeMode) {
    InvoiceTypeMode[InvoiceTypeMode["BeginningInventoryInvoice"] = 10] = "BeginningInventoryInvoice";
    InvoiceTypeMode[InvoiceTypeMode["PurchaseBill"] = 20] = "PurchaseBill";
    InvoiceTypeMode[InvoiceTypeMode["SalesInvoice"] = 30] = "SalesInvoice";
    InvoiceTypeMode[InvoiceTypeMode["PurchaseReturnBill"] = 40] = "PurchaseReturnBill";
    InvoiceTypeMode[InvoiceTypeMode["SalesReturnInvoice"] = 50] = "SalesReturnInvoice";
    InvoiceTypeMode[InvoiceTypeMode["TransferIn"] = 60] = "TransferIn";
    InvoiceTypeMode[InvoiceTypeMode["TransferOut"] = 70] = "TransferOut";
})(InvoiceTypeMode || (InvoiceTypeMode = {}));
const invoiceTypeModeOptions = mapEnumToOptions$1(InvoiceTypeMode);

class InvoiceTypeService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/invoice-type',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/invoice-type/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/invoice-type/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice-type',
            params: { search: input.search, disabled: input.disabled, withOptions: input.withOptions, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getOptions = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/invoice-type/${id}/options`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/invoice-type/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/invoice-type/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/invoice-type/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (options, id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/invoice-type/${id}/options`,
            body: options,
        }, { apiName: this.apiName, ...config });
    }
}
InvoiceTypeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InvoiceTypeService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
InvoiceTypeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InvoiceTypeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InvoiceTypeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class InvoiceService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, typeId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/invoice/${typeId}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createPaymentForInvoices = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/invoice/payment-for-invoices',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/invoice/${id}`,
        }, { apiName: this.apiName, ...config });
        this.exportSummaryReport = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/invoice/summary-report/export',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.get = (typeId, id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/invoice/${typeId}/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getByCategoriesReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice/by-categories-report',
            params: { warehouseId: input.warehouseId, branchId: input.branchId, categoryId: input.categoryId, mainCategoryId: input.mainCategoryId, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, mode: input.mode, typeIds: input.typeIds, salesChannels: input.salesChannels, excludedSalesChannels: input.excludedSalesChannels, status: input.status, onlyPostedToWarehouses: input.onlyPostedToWarehouses, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getByCustomersReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice/by-customers-report',
            params: { customerId: input.customerId, customerGroupId: input.customerGroupId, customerBusinessTypeId: input.customerBusinessTypeId, isClient: input.isClient, isSupplier: input.isSupplier, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, mode: input.mode, typeIds: input.typeIds, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getByDeliveryMethod = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice/by-delivery-methods',
            params: { outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, mode: input.mode, typeIds: input.typeIds, paymentModes: input.paymentModes },
        }, { apiName: this.apiName, ...config });
        this.getByItemsReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice/by-items-report',
            params: { itemId: input.itemId, itemDetailId: input.itemDetailId, warehouseId: input.warehouseId, branchId: input.branchId, categoryId: input.categoryId, mainCategoryId: input.mainCategoryId, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, mode: input.mode, typeIds: input.typeIds, salesChannels: input.salesChannels, excludedSalesChannels: input.excludedSalesChannels, onlyPostedToWarehouses: input.onlyPostedToWarehouses, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getByPaymentMethods = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice/by-payment-methods',
            params: { outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, mode: input.mode, typeIds: input.typeIds, paymentModes: input.paymentModes },
        }, { apiName: this.apiName, ...config });
        this.getDailySummaryReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice/daily-summary-report',
            params: { outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, mode: input.mode, typeIds: input.typeIds, paymentModes: input.paymentModes },
        }, { apiName: this.apiName, ...config });
        this.getHeader = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/invoice/${id}/header`,
        }, { apiName: this.apiName, ...config });
        this.getInvoiceFiles = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/invoice/${id}/invoice-files`,
        }, { apiName: this.apiName, ...config });
        this.getLines = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/invoice/${id}/lines`,
        }, { apiName: this.apiName, ...config });
        this.getListSummaryReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice/list-summary-report',
            params: { status: input.status, deliveryType: input.deliveryType, salesChannels: input.salesChannels, excludedSalesChannels: input.excludedSalesChannels, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, mode: input.mode, typeIds: input.typeIds, paymentModes: input.paymentModes },
        }, { apiName: this.apiName, ...config });
        this.getListV2 = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice/list',
            params: { search: input.search, marketplaceId: input.marketplaceId, deliveryType: input.deliveryType, number: input.number, referenceType: input.referenceType, status: input.status, referenceId: input.referenceId, salesChannels: input.salesChannels, excludedSalesChannels: input.excludedSalesChannels, customerId: input.customerId, itemDetailId: input.itemDetailId, couponId: input.couponId, taxId: input.taxId, hasNoTax: input.hasNoTax, fromDate: input.fromDate, toDate: input.toDate, outletId: input.outletId, typeIds: input.typeIds, mode: input.mode, paymentStatus: input.paymentStatus, outletTableId: input.outletTableId, paymentMethodId: input.paymentMethodId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getNewInvoiceNumber = (typeId, config) => this.restService.request({
            method: 'GET',
            responseType: 'text',
            url: `/api/app/invoice/${typeId}/next`,
        }, { apiName: this.apiName, ...config });
        this.getPaymentStatus = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/invoice/${id}/payment-status`,
        }, { apiName: this.apiName, ...config });
        this.getPaymentsReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice/payments-report',
            params: { outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, mode: input.mode, typeIds: input.typeIds, showOnlyZeroUnpaidAmount: input.showOnlyZeroUnpaidAmount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getPaymentsSummaryReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice/payments-summary-report',
            params: { outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, mode: input.mode, typeIds: input.typeIds, paymentModes: input.paymentModes },
        }, { apiName: this.apiName, ...config });
        this.getSummaryReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/invoice/summary-report',
            params: { timezoneOffset: input.timezoneOffset, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, mode: input.mode, typeIds: input.typeIds, paymentModes: input.paymentModes },
        }, { apiName: this.apiName, ...config });
        this.getTaxLines = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/invoice/${id}/tax-lines`,
        }, { apiName: this.apiName, ...config });
        this.postByIds = (ids, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/invoice/by-ids',
            body: ids,
        }, { apiName: this.apiName, ...config });
        this.putCancel = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/invoice/${id}/cancel`,
        }, { apiName: this.apiName, ...config });
        this.putPostToWarehouses = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/invoice/${id}/post-to-warehouses`,
        }, { apiName: this.apiName, ...config });
        this.putRollbackPostToWarehouses = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/invoice/${id}/rollback-post-to-warehouses`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, typeId, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/invoice/${typeId}/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateNote = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/invoice/note',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateTaxLines = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/invoice/${id}/tax-lines`,
        }, { apiName: this.apiName, ...config });
    }
}
InvoiceService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InvoiceService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
InvoiceService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InvoiceService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: InvoiceService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var ReferenceType;
(function (ReferenceType) {
    ReferenceType[ReferenceType["Invoice"] = 0] = "Invoice";
    ReferenceType[ReferenceType["Order"] = 10] = "Order";
    ReferenceType[ReferenceType["TransferOrderIn"] = 11] = "TransferOrderIn";
    ReferenceType[ReferenceType["TransferOrderOut"] = 12] = "TransferOrderOut";
})(ReferenceType || (ReferenceType = {}));
const referenceTypeOptions = mapEnumToOptions$1(ReferenceType);

var LoyaltyProgramActionType;
(function (LoyaltyProgramActionType) {
    LoyaltyProgramActionType[LoyaltyProgramActionType["Registration"] = 0] = "Registration";
    LoyaltyProgramActionType[LoyaltyProgramActionType["Invitation"] = 1] = "Invitation";
    LoyaltyProgramActionType[LoyaltyProgramActionType["ReviewItem"] = 2] = "ReviewItem";
    LoyaltyProgramActionType[LoyaltyProgramActionType["ReviewStore"] = 3] = "ReviewStore";
})(LoyaltyProgramActionType || (LoyaltyProgramActionType = {}));
const loyaltyProgramActionTypeOptions = mapEnumToOptions$1(LoyaltyProgramActionType);

var LoyaltyProgramEarnedPointsMethod;
(function (LoyaltyProgramEarnedPointsMethod) {
    LoyaltyProgramEarnedPointsMethod[LoyaltyProgramEarnedPointsMethod["SaleTotal"] = 0] = "SaleTotal";
    LoyaltyProgramEarnedPointsMethod[LoyaltyProgramEarnedPointsMethod["WeightsItemsTotal"] = 1] = "WeightsItemsTotal";
    LoyaltyProgramEarnedPointsMethod[LoyaltyProgramEarnedPointsMethod["QuantitiesTotal"] = 2] = "QuantitiesTotal";
})(LoyaltyProgramEarnedPointsMethod || (LoyaltyProgramEarnedPointsMethod = {}));
const loyaltyProgramEarnedPointsMethodOptions = mapEnumToOptions$1(LoyaltyProgramEarnedPointsMethod);

var LoyaltyProgramPointActionType;
(function (LoyaltyProgramPointActionType) {
    LoyaltyProgramPointActionType[LoyaltyProgramPointActionType["Registration"] = 0] = "Registration";
    LoyaltyProgramPointActionType[LoyaltyProgramPointActionType["Invitation"] = 1] = "Invitation";
    LoyaltyProgramPointActionType[LoyaltyProgramPointActionType["PlaceOrder"] = 2] = "PlaceOrder";
    LoyaltyProgramPointActionType[LoyaltyProgramPointActionType["OrderCompleted"] = 3] = "OrderCompleted";
    LoyaltyProgramPointActionType[LoyaltyProgramPointActionType["OrderCanceled"] = 4] = "OrderCanceled";
    LoyaltyProgramPointActionType[LoyaltyProgramPointActionType["CreateInvoice"] = 5] = "CreateInvoice";
    LoyaltyProgramPointActionType[LoyaltyProgramPointActionType["InvoiceCanceled"] = 6] = "InvoiceCanceled";
    LoyaltyProgramPointActionType[LoyaltyProgramPointActionType["ReviewItem"] = 7] = "ReviewItem";
    LoyaltyProgramPointActionType[LoyaltyProgramPointActionType["ReviewStore"] = 8] = "ReviewStore";
    LoyaltyProgramPointActionType[LoyaltyProgramPointActionType["Manual"] = 9] = "Manual";
})(LoyaltyProgramPointActionType || (LoyaltyProgramPointActionType = {}));
const loyaltyProgramPointActionTypeOptions = mapEnumToOptions$1(LoyaltyProgramPointActionType);

class LoyaltyProgramPointService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/loyalty-program-point',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/loyalty-program-point/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/loyalty-program-point/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getCustomerPoints = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/loyalty-program-point/${id}/customer-points`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/loyalty-program-point',
            params: { customerId: input.customerId, referenceId: input.referenceId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMyPoints = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/loyalty-program-point/my-points',
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/loyalty-program-point/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
LoyaltyProgramPointService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoyaltyProgramPointService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
LoyaltyProgramPointService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoyaltyProgramPointService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoyaltyProgramPointService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class LoyaltyProgramService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.configuration = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/loyalty-program/configuration',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.get = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/loyalty-program',
        }, { apiName: this.apiName, ...config });
        this.getStoreConfiguration = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/loyalty-program/store-configuration',
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/loyalty-program/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/loyalty-program/${id}/enable`,
        }, { apiName: this.apiName, ...config });
    }
}
LoyaltyProgramService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoyaltyProgramService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
LoyaltyProgramService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoyaltyProgramService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: LoyaltyProgramService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var FirebaseCloudMessagingClientStatus;
(function (FirebaseCloudMessagingClientStatus) {
    FirebaseCloudMessagingClientStatus[FirebaseCloudMessagingClientStatus["Accepted"] = 0] = "Accepted";
    FirebaseCloudMessagingClientStatus[FirebaseCloudMessagingClientStatus["Rejected"] = 1] = "Rejected";
    FirebaseCloudMessagingClientStatus[FirebaseCloudMessagingClientStatus["LoggedOut"] = 2] = "LoggedOut";
    FirebaseCloudMessagingClientStatus[FirebaseCloudMessagingClientStatus["NotFound"] = 3] = "NotFound";
})(FirebaseCloudMessagingClientStatus || (FirebaseCloudMessagingClientStatus = {}));
const firebaseCloudMessagingClientStatusOptions = mapEnumToOptions$1(FirebaseCloudMessagingClientStatus);

class FirebaseCloudMessagingService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/firebase-cloud-messaging/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getFcmClients = (customerId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/firebase-cloud-messaging/fcm-clients/${customerId}`,
        }, { apiName: this.apiName, ...config });
        this.update = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/firebase-cloud-messaging',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
FirebaseCloudMessagingService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FirebaseCloudMessagingService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
FirebaseCloudMessagingService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FirebaseCloudMessagingService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: FirebaseCloudMessagingService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var NotificationAudience;
(function (NotificationAudience) {
    NotificationAudience[NotificationAudience["AllStoreFrontClients"] = 0] = "AllStoreFrontClients";
    NotificationAudience[NotificationAudience["AllStaffUsers"] = 1] = "AllStaffUsers";
    NotificationAudience[NotificationAudience["StoreGuestsOnly"] = 2] = "StoreGuestsOnly";
    NotificationAudience[NotificationAudience["StoreCustomersOnly"] = 3] = "StoreCustomersOnly";
    NotificationAudience[NotificationAudience["StoreSpecificCustomers"] = 4] = "StoreSpecificCustomers";
    NotificationAudience[NotificationAudience["SpecificUsers"] = 5] = "SpecificUsers";
})(NotificationAudience || (NotificationAudience = {}));
const notificationAudienceOptions = mapEnumToOptions$1(NotificationAudience);

var NotificationCustomersFilter;
(function (NotificationCustomersFilter) {
    NotificationCustomersFilter[NotificationCustomersFilter["AllCustomers"] = 0] = "AllCustomers";
    NotificationCustomersFilter[NotificationCustomersFilter["CustomersWithNoAddressOnly"] = 1] = "CustomersWithNoAddressOnly";
    NotificationCustomersFilter[NotificationCustomersFilter["CustomersWithAddressOnly"] = 2] = "CustomersWithAddressOnly";
    NotificationCustomersFilter[NotificationCustomersFilter["CustomersOrderedBefore"] = 3] = "CustomersOrderedBefore";
    NotificationCustomersFilter[NotificationCustomersFilter["CustomersNeverOrderedBefore"] = 4] = "CustomersNeverOrderedBefore";
})(NotificationCustomersFilter || (NotificationCustomersFilter = {}));
const notificationCustomersFilterOptions = mapEnumToOptions$1(NotificationCustomersFilter);

var NotificationTarget;
(function (NotificationTarget) {
    NotificationTarget[NotificationTarget["None"] = 0] = "None";
    NotificationTarget[NotificationTarget["Item"] = 1] = "Item";
    NotificationTarget[NotificationTarget["Order"] = 2] = "Order";
    NotificationTarget[NotificationTarget["Coupon"] = 3] = "Coupon";
    NotificationTarget[NotificationTarget["RewardPoints"] = 4] = "RewardPoints";
    NotificationTarget[NotificationTarget["NewCustomer"] = 5] = "NewCustomer";
})(NotificationTarget || (NotificationTarget = {}));
const notificationTargetOptions = mapEnumToOptions$1(NotificationTarget);

var NotificationType;
(function (NotificationType) {
    NotificationType[NotificationType["Auto"] = 0] = "Auto";
    NotificationType[NotificationType["Manual"] = 1] = "Manual";
})(NotificationType || (NotificationType = {}));
const notificationTypeOptions = mapEnumToOptions$1(NotificationType);

class NotificationService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/notification',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/notification/${id}`,
        }, { apiName: this.apiName, ...config });
        this.deleteReceiver = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/notification/${id}/receiver`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/notification/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/notification',
            params: { search: input.search, type: input.type, target: input.target, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMy = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/notification/my',
            params: { token: input.token, seen: input.seen, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getRecipientsCount = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/notification/recipients-count',
            params: { prefixTitle: input.prefixTitle, title: input.title, title2: input.title2, title3: input.title3, message: input.message, message2: input.message2, message3: input.message3, image: input.image, allLanguages: input.allLanguages, targetId: input.targetId, target: input.target, audience: input.audience, customersFilter: input.customersFilter, specificCustomersIds: input.specificCustomersIds, specificUsersIds: input.specificUsersIds, type: input.type, salesChannel: input.salesChannel, tenantId: input.tenantId },
        }, { apiName: this.apiName, ...config });
        this.getUnseenCount = (token, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/notification/unseen-count',
            params: { token },
        }, { apiName: this.apiName, ...config });
        this.sendOrderTestNotification = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/notification/send-order-test-notification',
        }, { apiName: this.apiName, ...config });
        this.sendSignupTestNotification = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/notification/send-signup-test-notification',
        }, { apiName: this.apiName, ...config });
        this.updateActionTaken = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/notification/action-taken',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateSeen = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/notification/seen',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateSeenAll = (token, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/notification/seen-all',
            params: { token },
        }, { apiName: this.apiName, ...config });
    }
}
NotificationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: NotificationService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
NotificationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: NotificationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: NotificationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var CartLineModifierStatus;
(function (CartLineModifierStatus) {
    CartLineModifierStatus[CartLineModifierStatus["NotUpdated"] = 0] = "NotUpdated";
    CartLineModifierStatus[CartLineModifierStatus["ModifierPriceIncreased"] = 1] = "ModifierPriceIncreased";
    CartLineModifierStatus[CartLineModifierStatus["ModifierPriceDecreased"] = 2] = "ModifierPriceDecreased";
    CartLineModifierStatus[CartLineModifierStatus["ModifierNotAvailable"] = 3] = "ModifierNotAvailable";
})(CartLineModifierStatus || (CartLineModifierStatus = {}));
const cartLineModifierStatusOptions = mapEnumToOptions$1(CartLineModifierStatus);

var CartLineStatus;
(function (CartLineStatus) {
    CartLineStatus[CartLineStatus["NotUpdated"] = 0] = "NotUpdated";
    CartLineStatus[CartLineStatus["ItemPriceIncreased"] = 1] = "ItemPriceIncreased";
    CartLineStatus[CartLineStatus["ItemPriceDecreased"] = 2] = "ItemPriceDecreased";
    CartLineStatus[CartLineStatus["NotAvailable"] = 3] = "NotAvailable";
    CartLineStatus[CartLineStatus["NoStockAvailable"] = 4] = "NoStockAvailable";
})(CartLineStatus || (CartLineStatus = {}));
const cartLineStatusOptions = mapEnumToOptions$1(CartLineStatus);

var CartStatus;
(function (CartStatus) {
    CartStatus[CartStatus["Open"] = 0] = "Open";
    CartStatus[CartStatus["Reset"] = 1] = "Reset";
    CartStatus[CartStatus["Done"] = 2] = "Done";
})(CartStatus || (CartStatus = {}));
const cartStatusOptions = mapEnumToOptions$1(CartStatus);

class CartService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.addItem = (itemDetailId, input, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/cart/item/${itemDetailId}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.addMarketplace = (itemDetailId, input, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/cart/marketplace/add/${itemDetailId}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.deleteMarketplace = (input, config) => this.restService.request({
            method: 'DELETE',
            url: '/api/app/cart/marketplace/reset',
            params: { tenantId: input.tenantId, currencyId: input.currencyId, token: input.token },
        }, { apiName: this.apiName, ...config });
        this.deleteMarketplaceLine = (cartLineId, input, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/cart/marketplace/reset/${cartLineId}`,
            params: { tenantId: input.tenantId, currencyId: input.currencyId, token: input.token },
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/cart/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getCheckout = (deliveryType, outletId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/cart/checkout/${outletId}`,
            params: { deliveryType },
        }, { apiName: this.apiName, ...config });
        this.getEstimatedEarnedPoints = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/cart/estimated-earned-points',
            params: { token: input.token, currencyId: input.currencyId },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/cart',
            params: { status: input.status, isUser: input.isUser, outletId: input.outletId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMarketplace = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/cart/marketplace/my',
            params: { tenantId: input.tenantId, currencyId: input.currencyId, token: input.token },
        }, { apiName: this.apiName, ...config });
        this.getMy = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/cart/my',
            params: { token: input.token, currencyId: input.currencyId },
        }, { apiName: this.apiName, ...config });
        this.getRedeemablePoints = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/cart/redeemable-points',
            params: { token: input.token, currencyId: input.currencyId },
        }, { apiName: this.apiName, ...config });
        this.remove = (id, input, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/cart/${id}`,
            params: { token: input.token, currencyId: input.currencyId },
        }, { apiName: this.apiName, ...config });
        this.removeReset = (token, config) => this.restService.request({
            method: 'DELETE',
            url: '/api/app/cart/reset',
            params: { token },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/cart/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateMarketplace = (cartLineId, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/cart/marketplace/update/${cartLineId}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
CartService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CartService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CartService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CartService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CartService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var GroupByOptions;
(function (GroupByOptions) {
    GroupByOptions[GroupByOptions["Hours"] = 0] = "Hours";
    GroupByOptions[GroupByOptions["Days"] = 1] = "Days";
    GroupByOptions[GroupByOptions["Months"] = 2] = "Months";
    GroupByOptions[GroupByOptions["Years"] = 3] = "Years";
})(GroupByOptions || (GroupByOptions = {}));
const groupByOptionsOptions = mapEnumToOptions$1(GroupByOptions);

var OrderLineStatus;
(function (OrderLineStatus) {
    OrderLineStatus[OrderLineStatus["Accepted"] = 0] = "Accepted";
    OrderLineStatus[OrderLineStatus["PartiallyAccepted"] = 1] = "PartiallyAccepted";
    OrderLineStatus[OrderLineStatus["Canceled"] = 10] = "Canceled";
    OrderLineStatus[OrderLineStatus["Replaced"] = 11] = "Replaced";
})(OrderLineStatus || (OrderLineStatus = {}));
const orderLineStatusOptions = mapEnumToOptions$1(OrderLineStatus);

var OrderNumberGenerateMethod;
(function (OrderNumberGenerateMethod) {
    OrderNumberGenerateMethod[OrderNumberGenerateMethod["Incremental"] = 0] = "Incremental";
    OrderNumberGenerateMethod[OrderNumberGenerateMethod["Random"] = 1] = "Random";
})(OrderNumberGenerateMethod || (OrderNumberGenerateMethod = {}));
const orderNumberGenerateMethodOptions = mapEnumToOptions$1(OrderNumberGenerateMethod);

var OrderPaymentStatus;
(function (OrderPaymentStatus) {
    OrderPaymentStatus[OrderPaymentStatus["Unpaid"] = 0] = "Unpaid";
    OrderPaymentStatus[OrderPaymentStatus["Pending"] = 5] = "Pending";
    OrderPaymentStatus[OrderPaymentStatus["FullyPaid"] = 10] = "FullyPaid";
    OrderPaymentStatus[OrderPaymentStatus["PartiallyPaid"] = 11] = "PartiallyPaid";
    OrderPaymentStatus[OrderPaymentStatus["OverPaid"] = 12] = "OverPaid";
    OrderPaymentStatus[OrderPaymentStatus["Refunded"] = 20] = "Refunded";
    OrderPaymentStatus[OrderPaymentStatus["PartiallyRefunded"] = 21] = "PartiallyRefunded";
    OrderPaymentStatus[OrderPaymentStatus["OverRefunded"] = 22] = "OverRefunded";
})(OrderPaymentStatus || (OrderPaymentStatus = {}));
const orderPaymentStatusOptions = mapEnumToOptions$1(OrderPaymentStatus);

var OrderReviewStatus;
(function (OrderReviewStatus) {
    OrderReviewStatus[OrderReviewStatus["NotReviewed"] = 0] = "NotReviewed";
    OrderReviewStatus[OrderReviewStatus["PartiallyReviewed"] = 1] = "PartiallyReviewed";
    OrderReviewStatus[OrderReviewStatus["Reviewed"] = 2] = "Reviewed";
})(OrderReviewStatus || (OrderReviewStatus = {}));
const orderReviewStatusOptions = mapEnumToOptions$1(OrderReviewStatus);

var OrderStatus;
(function (OrderStatus) {
    OrderStatus[OrderStatus["Draft"] = 0] = "Draft";
    OrderStatus[OrderStatus["New"] = 10] = "New";
    OrderStatus[OrderStatus["Accepted"] = 11] = "Accepted";
    OrderStatus[OrderStatus["InProgress"] = 12] = "InProgress";
    OrderStatus[OrderStatus["Ready"] = 13] = "Ready";
    OrderStatus[OrderStatus["Completed"] = 14] = "Completed";
    OrderStatus[OrderStatus["Rejected"] = 20] = "Rejected";
    OrderStatus[OrderStatus["Canceled"] = 21] = "Canceled";
    OrderStatus[OrderStatus["Failed"] = 22] = "Failed";
})(OrderStatus || (OrderStatus = {}));
const orderStatusOptions = mapEnumToOptions$1(OrderStatus);

class OrderService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/order',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createInvoice = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/order/${id}/invoice`,
        }, { apiName: this.apiName, ...config });
        this.createMarketplaceStore = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/order/marketplace-store',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createPaymentForOrders = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/order/payment-for-orders',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/order/${id}`,
        }, { apiName: this.apiName, ...config });
        this.exportSummaryReport = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/order/summary-report/export',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/order/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getByCategoriesReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/by-categories-report',
            params: { warehouseId: input.warehouseId, branchId: input.branchId, categoryId: input.categoryId, mainCategoryId: input.mainCategoryId, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, salesChannels: input.salesChannels, excludedSalesChannels: input.excludedSalesChannels, onlyCompleted: input.onlyCompleted, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getByCustomersReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/by-customers-report',
            params: { customerId: input.customerId, customerGroupId: input.customerGroupId, customerBusinessTypeId: input.customerBusinessTypeId, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, status: input.status, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getByDeliveryMethod = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/by-delivery-method',
            params: { outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate },
        }, { apiName: this.apiName, ...config });
        this.getByItemsReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/by-items-report',
            params: { itemId: input.itemId, itemDetailId: input.itemDetailId, warehouseId: input.warehouseId, branchId: input.branchId, categoryId: input.categoryId, mainCategoryId: input.mainCategoryId, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, salesChannels: input.salesChannels, excludedSalesChannels: input.excludedSalesChannels, onlyCompleted: input.onlyCompleted, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getByPaymentMethods = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/by-payment-methods',
            params: { outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate },
        }, { apiName: this.apiName, ...config });
        this.getCompareNewCustomersReturningCustomersSummary = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/compare-new-customers-returning-customers-summary',
            params: { warehouseId: input.warehouseId, branchId: input.branchId, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate },
        }, { apiName: this.apiName, ...config });
        this.getDashboardSummary = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/dashboard-summary',
            params: { warehouseId: input.warehouseId, branchId: input.branchId, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate },
        }, { apiName: this.apiName, ...config });
        this.getHeader = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/order/${id}/header`,
        }, { apiName: this.apiName, ...config });
        this.getHistory = (id, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/order/${id}/history`,
            params: { skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getInvoices = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/order/${id}/invoices`,
        }, { apiName: this.apiName, ...config });
        this.getLines = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/order/${id}/lines`,
        }, { apiName: this.apiName, ...config });
        this.getListMarketplace = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/marketplace',
            params: { storesIds: input.storesIds, marketplaceId: input.marketplaceId, outletId: input.outletId, countryId: input.countryId, currencyId: input.currencyId, stateProvinceId: input.stateProvinceId, stateProvinceName: input.stateProvinceName, cityId: input.cityId, cityName: input.cityName, districtId: input.districtId, zipPostalCode: input.zipPostalCode, minSubTotal: input.minSubTotal, maxSubTotal: input.maxSubTotal, fromDate: input.fromDate, toDate: input.toDate, filter: input.filter, address: input.address, excludedStatus: input.excludedStatus, status: input.status, paymentStatus: input.paymentStatus, shippingStatus: input.shippingStatus, salesChannels: input.salesChannels, excludedSalesChannels: input.excludedSalesChannels, deliveryType: input.deliveryType, customerId: input.customerId, itemDetailId: input.itemDetailId, couponId: input.couponId, taxId: input.taxId, hasNoTax: input.hasNoTax, outletTableId: input.outletTableId, paymentMethodId: input.paymentMethodId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getListMarketplaceStore = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/marketplace-store',
            params: { storesIds: input.storesIds, marketplaceId: input.marketplaceId, outletId: input.outletId, countryId: input.countryId, currencyId: input.currencyId, stateProvinceId: input.stateProvinceId, stateProvinceName: input.stateProvinceName, cityId: input.cityId, cityName: input.cityName, districtId: input.districtId, zipPostalCode: input.zipPostalCode, minSubTotal: input.minSubTotal, maxSubTotal: input.maxSubTotal, fromDate: input.fromDate, toDate: input.toDate, filter: input.filter, address: input.address, excludedStatus: input.excludedStatus, status: input.status, paymentStatus: input.paymentStatus, shippingStatus: input.shippingStatus, salesChannels: input.salesChannels, excludedSalesChannels: input.excludedSalesChannels, deliveryType: input.deliveryType, customerId: input.customerId, itemDetailId: input.itemDetailId, couponId: input.couponId, taxId: input.taxId, hasNoTax: input.hasNoTax, outletTableId: input.outletTableId, paymentMethodId: input.paymentMethodId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getListOrderStore = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/order-store',
            params: { storesIds: input.storesIds, marketplaceId: input.marketplaceId, outletId: input.outletId, countryId: input.countryId, currencyId: input.currencyId, stateProvinceId: input.stateProvinceId, stateProvinceName: input.stateProvinceName, cityId: input.cityId, cityName: input.cityName, districtId: input.districtId, zipPostalCode: input.zipPostalCode, minSubTotal: input.minSubTotal, maxSubTotal: input.maxSubTotal, fromDate: input.fromDate, toDate: input.toDate, filter: input.filter, address: input.address, excludedStatus: input.excludedStatus, status: input.status, paymentStatus: input.paymentStatus, shippingStatus: input.shippingStatus, salesChannels: input.salesChannels, excludedSalesChannels: input.excludedSalesChannels, deliveryType: input.deliveryType, customerId: input.customerId, itemDetailId: input.itemDetailId, couponId: input.couponId, taxId: input.taxId, hasNoTax: input.hasNoTax, outletTableId: input.outletTableId, paymentMethodId: input.paymentMethodId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getListSummaryReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/list-summary-report',
            params: { status: input.status, deliveryType: input.deliveryType, salesChannels: input.salesChannels, excludedSalesChannels: input.excludedSalesChannels, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate },
        }, { apiName: this.apiName, ...config });
        this.getListV2 = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order',
            params: { storesIds: input.storesIds, marketplaceId: input.marketplaceId, outletId: input.outletId, countryId: input.countryId, currencyId: input.currencyId, stateProvinceId: input.stateProvinceId, stateProvinceName: input.stateProvinceName, cityId: input.cityId, cityName: input.cityName, districtId: input.districtId, zipPostalCode: input.zipPostalCode, minSubTotal: input.minSubTotal, maxSubTotal: input.maxSubTotal, fromDate: input.fromDate, toDate: input.toDate, filter: input.filter, address: input.address, excludedStatus: input.excludedStatus, status: input.status, paymentStatus: input.paymentStatus, shippingStatus: input.shippingStatus, salesChannels: input.salesChannels, excludedSalesChannels: input.excludedSalesChannels, deliveryType: input.deliveryType, customerId: input.customerId, itemDetailId: input.itemDetailId, couponId: input.couponId, taxId: input.taxId, hasNoTax: input.hasNoTax, outletTableId: input.outletTableId, paymentMethodId: input.paymentMethodId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMarketplaceStore = (id, tenantId, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/order/${id}/marketplace-store/${tenantId}`,
            params: { currencyId: input.currencyId },
        }, { apiName: this.apiName, ...config });
        this.getNewOrdersCount = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/new-orders-count',
        }, { apiName: this.apiName, ...config });
        this.getOrderStore = (id, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/order/${id}/order-store`,
            params: { currencyId: input.currencyId },
        }, { apiName: this.apiName, ...config });
        this.getOrderStoreByOrderNumberAndPhoneNumber = (orderNumber, phoneNumber, currencyId, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/order-store-by-order-number-and-phone-number',
            params: { orderNumber, phoneNumber, currencyId },
        }, { apiName: this.apiName, ...config });
        this.getPaymentStatus = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/order/${id}/payment-status`,
        }, { apiName: this.apiName, ...config });
        this.getPaymentsReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/payments-report',
            params: { outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate, showOnlyZeroUnpaidAmount: input.showOnlyZeroUnpaidAmount, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getPaymentsSummaryReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/payments-summary-report',
            params: { outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate },
        }, { apiName: this.apiName, ...config });
        this.getSummaryReport = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/order/summary-report',
            params: { timezoneOffset: input.timezoneOffset, outletId: input.outletId, fromDate: input.fromDate, toDate: input.toDate },
        }, { apiName: this.apiName, ...config });
        this.getTaxLines = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/order/${id}/tax-lines`,
        }, { apiName: this.apiName, ...config });
        this.postByIds = (ids, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/order/by-ids',
            body: ids,
        }, { apiName: this.apiName, ...config });
        this.pushExpertOrder = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/order/${id}/push-expert-order`,
        }, { apiName: this.apiName, ...config });
        this.putCancelOrder = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/order/${id}/cancel-order`,
        }, { apiName: this.apiName, ...config });
        this.putCoupon = (id, couponCode, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/order/${id}/coupon`,
            params: { couponCode },
        }, { apiName: this.apiName, ...config });
        this.sendOrderInvoiceEmail = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/order/${id}/send-order-invoice-email`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/order/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateNote = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/order/note',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updatePaymentMethod = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/order/payment-method',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateShippingStatus = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/order/shipping-status',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateStatus = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/order/status',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateTaxLines = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/order/${id}/tax-lines`,
        }, { apiName: this.apiName, ...config });
    }
}
OrderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OrderService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
OrderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OrderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OrderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var ShippingStatus;
(function (ShippingStatus) {
    ShippingStatus[ShippingStatus["NotYetShipped"] = 0] = "NotYetShipped";
    ShippingStatus[ShippingStatus["Submitted"] = 10] = "Submitted";
    ShippingStatus[ShippingStatus["AssigningDriver"] = 20] = "AssigningDriver";
    ShippingStatus[ShippingStatus["GoingToPickup"] = 30] = "GoingToPickup";
    ShippingStatus[ShippingStatus["ArrivedToPickup"] = 31] = "ArrivedToPickup";
    ShippingStatus[ShippingStatus["PickedUp"] = 32] = "PickedUp";
    ShippingStatus[ShippingStatus["InTransit"] = 33] = "InTransit";
    ShippingStatus[ShippingStatus["Shipped"] = 40] = "Shipped";
    ShippingStatus[ShippingStatus["Delivered"] = 41] = "Delivered";
    ShippingStatus[ShippingStatus["PartiallyShipped"] = 42] = "PartiallyShipped";
    ShippingStatus[ShippingStatus["ReturnProcessing"] = 70] = "ReturnProcessing";
    ShippingStatus[ShippingStatus["Returned"] = 71] = "Returned";
    ShippingStatus[ShippingStatus["Canceled"] = 90] = "Canceled";
    ShippingStatus[ShippingStatus["Failed"] = 91] = "Failed";
    ShippingStatus[ShippingStatus["Rejected"] = 92] = "Rejected";
    ShippingStatus[ShippingStatus["Expired"] = 93] = "Expired";
    ShippingStatus[ShippingStatus["ShippingNotRequired"] = 100] = "ShippingNotRequired";
    ShippingStatus[ShippingStatus["MultipleShipments"] = 200] = "MultipleShipments";
})(ShippingStatus || (ShippingStatus = {}));
const shippingStatusOptions = mapEnumToOptions$1(ShippingStatus);

var PointOfSaleStatus;
(function (PointOfSaleStatus) {
    PointOfSaleStatus[PointOfSaleStatus["New"] = 0] = "New";
    PointOfSaleStatus[PointOfSaleStatus["Opened"] = 1] = "Opened";
    PointOfSaleStatus[PointOfSaleStatus["Closed"] = 2] = "Closed";
})(PointOfSaleStatus || (PointOfSaleStatus = {}));
const pointOfSaleStatusOptions = mapEnumToOptions$1(PointOfSaleStatus);

class PointOfSaleService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.configPOS = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/point-of-sale/config-pOS',
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/point-of-sale',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/point-of-sale/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/point-of-sale/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/point-of-sale',
            params: { search: input.search, status: input.status, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMy = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/point-of-sale/my',
        }, { apiName: this.apiName, ...config });
        this.getOptions = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/point-of-sale/${id}/options`,
        }, { apiName: this.apiName, ...config });
        this.putChangeStatus = (id, status, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/point-of-sale/${id}/change-status`,
            params: { status },
        }, { apiName: this.apiName, ...config });
        this.putClose = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/point-of-sale/${id}/close`,
        }, { apiName: this.apiName, ...config });
        this.putOpen = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/point-of-sale/${id}/open`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/point-of-sale/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (options, id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/point-of-sale/${id}/options`,
            body: options,
        }, { apiName: this.apiName, ...config });
    }
}
PointOfSaleService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PointOfSaleService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
PointOfSaleService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PointOfSaleService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PointOfSaleService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var SaasActionBy;
(function (SaasActionBy) {
    SaasActionBy[SaasActionBy["TenantUser"] = 0] = "TenantUser";
    SaasActionBy[SaasActionBy["HostUser"] = 1] = "HostUser";
    SaasActionBy[SaasActionBy["System"] = 2] = "System";
})(SaasActionBy || (SaasActionBy = {}));
const saasActionByOptions = mapEnumToOptions$1(SaasActionBy);

var SaasActionReason;
(function (SaasActionReason) {
    SaasActionReason[SaasActionReason["NewAccount"] = 0] = "NewAccount";
    SaasActionReason[SaasActionReason["AssignDefaultEdition"] = 1] = "AssignDefaultEdition";
    SaasActionReason[SaasActionReason["AssignDefaultCurrency"] = 2] = "AssignDefaultCurrency";
    SaasActionReason[SaasActionReason["StartTrial"] = 3] = "StartTrial";
    SaasActionReason[SaasActionReason["EndTrial"] = 4] = "EndTrial";
    SaasActionReason[SaasActionReason["ChangePlan"] = 5] = "ChangePlan";
    SaasActionReason[SaasActionReason["UnpaidInvoices"] = 6] = "UnpaidInvoices";
    SaasActionReason[SaasActionReason["SettlePayment"] = 7] = "SettlePayment";
    SaasActionReason[SaasActionReason["InvoiceIssued"] = 8] = "InvoiceIssued";
    SaasActionReason[SaasActionReason["ShiftBillingCycle"] = 9] = "ShiftBillingCycle";
})(SaasActionReason || (SaasActionReason = {}));
const saasActionReasonOptions = mapEnumToOptions$1(SaasActionReason);

class SaasAddonService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-addon',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/saas-addon/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-addon/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-addon',
            params: { productId: input.productId, editionId: input.editionId, search: input.search, intervalUnit: input.intervalUnit, interval: input.interval, currencyId: input.currencyId, countryId: input.countryId, disabled: input.disabled, fromPrice: input.fromPrice, toPrice: input.toPrice, isRecurring: input.isRecurring, allEditionsSelected: input.allEditionsSelected, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getPrices = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-addon/prices',
            params: { editionId: input.editionId, currencyId: input.currencyId, quantity: input.quantity },
        }, { apiName: this.apiName, ...config });
        this.getStorePrices = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-addon/store-prices',
            params: { editionId: input.editionId, currencyId: input.currencyId, quantity: input.quantity },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-addon/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-addon/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-addon/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaasAddonService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasAddonService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaasAddonService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasAddonService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasAddonService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var SaasCouponType;
(function (SaasCouponType) {
    SaasCouponType[SaasCouponType["New"] = 0] = "New";
    SaasCouponType[SaasCouponType["Recurring"] = 1] = "Recurring";
    SaasCouponType[SaasCouponType["Both"] = 2] = "Both";
})(SaasCouponType || (SaasCouponType = {}));
const saasCouponTypeOptions = mapEnumToOptions$1(SaasCouponType);

class SaasCouponService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.checkEligibility = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-coupon/check-eligibility',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-coupon',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/saas-coupon/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-coupon/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-coupon',
            params: { productId: input.productId, currencyId: input.currencyId, countryId: input.countryId, search: input.search, disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-coupon/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-coupon/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-coupon/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaasCouponService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasCouponService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaasCouponService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasCouponService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasCouponService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var SaasDiscountType;
(function (SaasDiscountType) {
    SaasDiscountType[SaasDiscountType["Percentage"] = 0] = "Percentage";
    SaasDiscountType[SaasDiscountType["Flat"] = 1] = "Flat";
})(SaasDiscountType || (SaasDiscountType = {}));
const saasDiscountTypeOptions = mapEnumToOptions$1(SaasDiscountType);

class SaasEditionService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.copy = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/saas-edition/${id}/copy`,
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-edition',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/saas-edition/${id}`,
        }, { apiName: this.apiName, ...config });
        this.deleteTranslation = (id, language, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/saas-edition/${id}/translation`,
            params: { language },
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-edition/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getCompareFeatures = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-edition/compare-features',
            params: { productCode: input.productCode, withFeatures: input.withFeatures },
        }, { apiName: this.apiName, ...config });
        this.getComparison = (countryIso3Code, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-edition/comparison/${countryIso3Code}`,
            params: { currencyIsoCode: input.currencyIsoCode, productCode: input.productCode, withFeatures: input.withFeatures },
        }, { apiName: this.apiName, ...config });
        this.getFeatures = (editionId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-edition/features/${editionId}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-edition',
            params: { productId: input.productId, search: input.search, currencyId: input.currencyId, countryId: input.countryId, subscriptionExpiryAction: input.subscriptionExpiryAction, expirySubstituteId: input.expirySubstituteId, disabled: input.disabled, publicAvailability: input.publicAvailability, fromPrice: input.fromPrice, toPrice: input.toPrice, offersOnly: input.offersOnly, intervalUnit: input.intervalUnit, interval: input.interval, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getProductEditions = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-edition/product-editions',
            params: { productCode: input.productCode },
        }, { apiName: this.apiName, ...config });
        this.getStore = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-edition/store',
            params: { productId: input.productId, currencyId: input.currencyId, storeId: input.storeId, search: input.search, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getTranslations = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-edition/${id}/translations`,
        }, { apiName: this.apiName, ...config });
        this.putDefault = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-edition/${id}/default`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-edition/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-edition/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.putTranslations = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-edition/${id}/translations`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-edition/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaasEditionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasEditionService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaasEditionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasEditionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasEditionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var SaasIntervalUnit;
(function (SaasIntervalUnit) {
    SaasIntervalUnit[SaasIntervalUnit["Day"] = 0] = "Day";
    SaasIntervalUnit[SaasIntervalUnit["Week"] = 1] = "Week";
    SaasIntervalUnit[SaasIntervalUnit["Month"] = 2] = "Month";
    SaasIntervalUnit[SaasIntervalUnit["Year"] = 3] = "Year";
})(SaasIntervalUnit || (SaasIntervalUnit = {}));
const saasIntervalUnitOptions = mapEnumToOptions$1(SaasIntervalUnit);

var SaasInvoiceStatus;
(function (SaasInvoiceStatus) {
    SaasInvoiceStatus[SaasInvoiceStatus["NoPaymentNeeded"] = 0] = "NoPaymentNeeded";
    SaasInvoiceStatus[SaasInvoiceStatus["DueOn"] = 1] = "DueOn";
    SaasInvoiceStatus[SaasInvoiceStatus["Overdue"] = 2] = "Overdue";
    SaasInvoiceStatus[SaasInvoiceStatus["Paid"] = 3] = "Paid";
})(SaasInvoiceStatus || (SaasInvoiceStatus = {}));
const saasInvoiceStatusOptions = mapEnumToOptions$1(SaasInvoiceStatus);

class SaasInvoiceService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-invoice',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/saas-invoice/${id}`,
        }, { apiName: this.apiName, ...config });
        this.fixNumbers = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-invoice/fix-numbers',
        }, { apiName: this.apiName, ...config });
        this.generateNextInvoice = (subscriptionId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/saas-invoice/generate-next-invoice/${subscriptionId}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-invoice/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-invoice',
            params: { tenantId: input.tenantId, storeId: input.storeId, subscriptionId: input.subscriptionId, editionId: input.editionId, couponId: input.couponId, paymentMethodId: input.paymentMethodId, currencyId: input.currencyId, startBillingDate: input.startBillingDate, endBillingDate: input.endBillingDate, minEditionPrice: input.minEditionPrice, maxEditionPrice: input.maxEditionPrice, isReturn: input.isReturn, expiryActionApplied: input.expiryActionApplied, referenceId: input.referenceId, status: input.status, search: input.search, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getSubscriptionList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-invoice/subscription-list',
            params: { subscriptionId: input.subscriptionId, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getUnpaidInvoices = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-invoice/unpaid-invoices',
            params: { subscriptionId: input.subscriptionId, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-invoice/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateCoupon = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/saas-invoice/coupon',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaasInvoiceService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasInvoiceService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaasInvoiceService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasInvoiceService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasInvoiceService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var SaasOrderStatus;
(function (SaasOrderStatus) {
    SaasOrderStatus[SaasOrderStatus["Pending"] = 0] = "Pending";
    SaasOrderStatus[SaasOrderStatus["Accepted"] = 1] = "Accepted";
    SaasOrderStatus[SaasOrderStatus["Completed"] = 2] = "Completed";
    SaasOrderStatus[SaasOrderStatus["Canceled"] = 3] = "Canceled";
})(SaasOrderStatus || (SaasOrderStatus = {}));
const saasOrderStatusOptions = mapEnumToOptions$1(SaasOrderStatus);

var SaasOrderType;
(function (SaasOrderType) {
    SaasOrderType[SaasOrderType["NewSubscription"] = 0] = "NewSubscription";
    SaasOrderType[SaasOrderType["Downgrade"] = 1] = "Downgrade";
    SaasOrderType[SaasOrderType["Upgrade"] = 2] = "Upgrade";
})(SaasOrderType || (SaasOrderType = {}));
const saasOrderTypeOptions = mapEnumToOptions$1(SaasOrderType);

class SaasOrderService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-order',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/saas-order/${id}`,
        }, { apiName: this.apiName, ...config });
        this.fixNumbers = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-order/fix-numbers',
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-order/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-order',
            params: { tenantId: input.tenantId, storeId: input.storeId, subscriptionId: input.subscriptionId, editionId: input.editionId, couponId: input.couponId, paymentMethodId: input.paymentMethodId, currencyId: input.currencyId, status: input.status, type: input.type, startBillingDate: input.startBillingDate, endBillingDate: input.endBillingDate, minEditionPrice: input.minEditionPrice, maxEditionPrice: input.maxEditionPrice, search: input.search, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putChangeStatus = (id, status, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-order/${id}/change-status`,
            params: { status },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-order/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateCoupon = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/saas-order/coupon',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaasOrderService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasOrderService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaasOrderService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasOrderService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasOrderService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class SaasPaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-payment-method',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/saas-payment-method/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-payment-method/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-payment-method',
            params: { search: input.search, provider: input.provider, providerType: input.providerType, disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getOptions = (provider, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-payment-method/options/${provider}`,
        }, { apiName: this.apiName, ...config });
        this.getPaymentGateways = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-payment-method/payment-gateways',
        }, { apiName: this.apiName, ...config });
        this.getTenantPaymentMethods = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-payment-method/tenant-payment-methods',
            params: { skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDefault = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-payment-method/${id}/default`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-payment-method/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-payment-method/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-payment-method/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (provider, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-payment-method/options/${provider}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaasPaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasPaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaasPaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasPaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasPaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class SaasPaymentService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-payment',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createOutstanding = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-payment/outstanding',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/saas-payment/${id}`,
        }, { apiName: this.apiName, ...config });
        this.fixNumbers = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-payment/fix-numbers',
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-payment/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-payment',
            params: { search: input.search, tenantId: input.tenantId, storeId: input.storeId, subscriptionId: input.subscriptionId, paymentMethodId: input.paymentMethodId, fromDate: input.fromDate, toDate: input.toDate, status: input.status, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putChangeStatus = (id, status, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-payment/${id}/change-status`,
            params: { status },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-payment/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaasPaymentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasPaymentService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaasPaymentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasPaymentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasPaymentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class SaasPaypalPaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.capturePayment = (transactionId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/saas-paypal-payment-method/capture-payment/${transactionId}`,
        }, { apiName: this.apiName, ...config });
        this.createPaymentCheckout = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-paypal-payment-method/payment-checkout',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-paypal-payment-method/options',
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/saas-paypal-payment-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaasPaypalPaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasPaypalPaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaasPaypalPaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasPaypalPaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasPaypalPaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class SaasProductService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-product',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/saas-product/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-product/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-product/all',
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-product',
            params: { sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDefault = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-product/${id}/default`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-product/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaasProductService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasProductService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaasProductService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasProductService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasProductService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class SaasStripePaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.createPaymentCheckout = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-stripe-payment-method/payment-checkout',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-stripe-payment-method/options',
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/saas-stripe-payment-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaasStripePaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasStripePaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaasStripePaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasStripePaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasStripePaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var SaasSubscriptionStatus;
(function (SaasSubscriptionStatus) {
    SaasSubscriptionStatus[SaasSubscriptionStatus["Pending"] = 0] = "Pending";
    SaasSubscriptionStatus[SaasSubscriptionStatus["Trial"] = 1] = "Trial";
    SaasSubscriptionStatus[SaasSubscriptionStatus["Live"] = 2] = "Live";
    SaasSubscriptionStatus[SaasSubscriptionStatus["Dunning"] = 3] = "Dunning";
    SaasSubscriptionStatus[SaasSubscriptionStatus["Canceled"] = 4] = "Canceled";
    SaasSubscriptionStatus[SaasSubscriptionStatus["Disabled"] = 5] = "Disabled";
    SaasSubscriptionStatus[SaasSubscriptionStatus["Expired"] = 6] = "Expired";
})(SaasSubscriptionStatus || (SaasSubscriptionStatus = {}));
const saasSubscriptionStatusOptions = mapEnumToOptions$1(SaasSubscriptionStatus);

class SaasSubscriptionService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.applyCoupon = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-subscription/apply-coupon',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.cancelAllPendingOrdersAndPayments = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-subscription/cancel-all-pending-orders-and-payments',
        }, { apiName: this.apiName, ...config });
        this.convertSubscriptionsToDunning = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-subscription/convert-subscriptions-to-dunning',
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-subscription',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createCart = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-subscription/cart',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createStoreCart = (storeId, input, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/saas-subscription/store-cart/${storeId}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/saas-subscription/${id}`,
        }, { apiName: this.apiName, ...config });
        this.doActionsWhenEndTrialOfSubscriptions = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-subscription/do-actions-when-end-trial-of-subscriptions',
        }, { apiName: this.apiName, ...config });
        this.doExpiryAction = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-subscription/do-expiry-action',
        }, { apiName: this.apiName, ...config });
        this.fixNumbers = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-subscription/fix-numbers',
        }, { apiName: this.apiName, ...config });
        this.generateInvoiceForNextBillingCycle = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-subscription/generate-invoice-for-next-billing-cycle',
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/saas-subscription/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getHistory = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-subscription/history',
            params: { subscriptionId: input.subscriptionId, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-subscription',
            params: { productId: input.productId, tenantId: input.tenantId, storeId: input.storeId, editionId: input.editionId, couponId: input.couponId, currencyId: input.currencyId, status: input.status, startDate: input.startDate, endDate: input.endDate, startBillingDate: input.startBillingDate, endBillingDate: input.endBillingDate, minEditionPrice: input.minEditionPrice, maxEditionPrice: input.maxEditionPrice, isEditionFreeOnly: input.isEditionFreeOnly, isEditionPaidOnly: input.isEditionPaidOnly, search: input.search, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMy = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saas-subscription/my',
        }, { apiName: this.apiName, ...config });
        this.hideEndTrialAlert = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/saas-subscription/${id}/hide-end-trial-alert`,
        }, { apiName: this.apiName, ...config });
        this.hideExpiringAlert = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/saas-subscription/${id}/hide-expiring-alert`,
        }, { apiName: this.apiName, ...config });
        this.putChangeStatus = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/saas-subscription/change-status',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.resetTenantFeatures = (tenantId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/saas-subscription/reset-tenant-features/${tenantId}`,
        }, { apiName: this.apiName, ...config });
        this.shiftBillingCycleAfterInvoiceIssued = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-subscription/shift-billing-cycle-after-invoice-issued',
        }, { apiName: this.apiName, ...config });
        this.subscribe = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saas-subscription/subscribe',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.tryTrial = (editionId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/saas-subscription/try-trial/${editionId}`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/saas-subscription/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateBillingPeriod = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/saas-subscription/billing-period',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateCoupon = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/saas-subscription/coupon',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaasSubscriptionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasSubscriptionService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaasSubscriptionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasSubscriptionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaasSubscriptionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var SubscriptionExpiryAction;
(function (SubscriptionExpiryAction) {
    SubscriptionExpiryAction[SubscriptionExpiryAction["DeactiveTenant"] = 0] = "DeactiveTenant";
    SubscriptionExpiryAction[SubscriptionExpiryAction["AssignToAnotherEdition"] = 1] = "AssignToAnotherEdition";
})(SubscriptionExpiryAction || (SubscriptionExpiryAction = {}));
const subscriptionExpiryActionOptions = mapEnumToOptions$1(SubscriptionExpiryAction);

class CustomizedThemeComponentService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customized-theme-component',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/customized-theme-component/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/customized-theme-component/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getMarketplaceRelatedStoresFeatures = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customized-theme-component/marketplace-related-stores-features',
            params: { tenantId: input.tenantId, themeComponentCode: input.themeComponentCode },
        }, { apiName: this.apiName, ...config });
        this.getPage = (pageId, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/customized-theme-component/get-page/${pageId}`,
            params: { themeId: input.themeId },
        }, { apiName: this.apiName, ...config });
        this.putReorder = (pageId, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customized-theme-component/${pageId}/reorder`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/customized-theme-component',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
CustomizedThemeComponentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomizedThemeComponentService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CustomizedThemeComponentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomizedThemeComponentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomizedThemeComponentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class CustomizedThemeMenuItemService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.getOptions = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customized-theme-menu-item/options',
            params: { themeId: input.themeId, menuItemId: input.menuItemId },
        }, { apiName: this.apiName, ...config });
        this.patch = (input, config) => this.restService.request({
            method: 'PATCH',
            url: '/api/app/customized-theme-menu-item',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
CustomizedThemeMenuItemService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomizedThemeMenuItemService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CustomizedThemeMenuItemService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomizedThemeMenuItemService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomizedThemeMenuItemService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class CustomizedThemeService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.apply = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/customized-theme/${id}/apply`,
        }, { apiName: this.apiName, ...config });
        this.copy = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/customized-theme/${id}/copy`,
        }, { apiName: this.apiName, ...config });
        this.getStoreDesign = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customized-theme/store-design',
            params: { themeId: input.themeId, pageId: input.pageId },
        }, { apiName: this.apiName, ...config });
        this.reset = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/customized-theme/${id}/reset`,
        }, { apiName: this.apiName, ...config });
    }
}
CustomizedThemeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomizedThemeService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CustomizedThemeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomizedThemeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomizedThemeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ThemeComponentCategoryService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/theme-component-category',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/theme-component-category/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/theme-component-category/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getComponents = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/theme-component-category/components',
            params: { themeId: input.themeId, pageId: input.pageId },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/theme-component-category',
            params: { themeId: input.themeId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putReorder = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/theme-component-category/reorder',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-component-category/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ThemeComponentCategoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentCategoryService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeComponentCategoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentCategoryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentCategoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ThemeComponentDependencyService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/theme-component-dependency',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/theme-component-dependency/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/theme-component-dependency/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/theme-component-dependency',
            params: { componentId: input.componentId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-component-dependency/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ThemeComponentDependencyService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentDependencyService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeComponentDependencyService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentDependencyService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentDependencyService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ThemeComponentPropertyService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/theme-component-property',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/theme-component-property/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/theme-component-property/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/theme-component-property',
            params: { componentId: input.componentId, sectionId: input.sectionId, disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-component-property/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-component-property/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.putReorder = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/theme-component-property/reorder',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-component-property/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ThemeComponentPropertyService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentPropertyService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeComponentPropertyService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentPropertyService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentPropertyService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ThemeComponentSectionService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/theme-component-section',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/theme-component-section/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/theme-component-section/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/theme-component-section',
            params: { themeId: input.themeId, disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-component-section/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-component-section/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.putReorder = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/theme-component-section/reorder',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-component-section/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ThemeComponentSectionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentSectionService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeComponentSectionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentSectionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentSectionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class ThemeComponentService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/theme-component',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/theme-component/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/theme-component/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getComponents = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/theme-component/${id}/properties`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/theme-component',
            params: { themeId: input.themeId, categoryId: input.categoryId, menuItemId: input.menuItemId, disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-component/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-component/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.putReorder = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/theme-component/reorder',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-component/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ThemeComponentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeComponentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeComponentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var ThemeDataType;
(function (ThemeDataType) {
    ThemeDataType[ThemeDataType["Static"] = 0] = "Static";
    ThemeDataType[ThemeDataType["Dynamic"] = 1] = "Dynamic";
})(ThemeDataType || (ThemeDataType = {}));
const themeDataTypeOptions = mapEnumToOptions$1(ThemeDataType);

class ThemeMenuItemOptionService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/theme-menu-item-option',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/theme-menu-item-option/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/theme-menu-item-option/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/theme-menu-item-option',
            params: { menuItemId: input.menuItemId, disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-menu-item-option/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-menu-item-option/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.putReorder = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/theme-menu-item-option/reorder',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-menu-item-option/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ThemeMenuItemOptionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeMenuItemOptionService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeMenuItemOptionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeMenuItemOptionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeMenuItemOptionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var ThemeMenuItemType;
(function (ThemeMenuItemType) {
    ThemeMenuItemType[ThemeMenuItemType["Options"] = 0] = "Options";
    ThemeMenuItemType[ThemeMenuItemType["Page"] = 1] = "Page";
})(ThemeMenuItemType || (ThemeMenuItemType = {}));
const themeMenuItemTypeOptions = mapEnumToOptions$1(ThemeMenuItemType);

class ThemeMenuItemService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/theme-menu-item',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/theme-menu-item/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/theme-menu-item/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/theme-menu-item',
            params: { themeId: input.themeId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getOptions = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/theme-menu-item/${id}/options`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme-menu-item/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ThemeMenuItemService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeMenuItemService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeMenuItemService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeMenuItemService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeMenuItemService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var ThemeOptionType;
(function (ThemeOptionType) {
    ThemeOptionType[ThemeOptionType["Toogle"] = 0] = "Toogle";
    ThemeOptionType[ThemeOptionType["ColorPicker"] = 1] = "ColorPicker";
    ThemeOptionType[ThemeOptionType["Select"] = 2] = "Select";
    ThemeOptionType[ThemeOptionType["Number"] = 3] = "Number";
    ThemeOptionType[ThemeOptionType["UploadImage"] = 4] = "UploadImage";
    ThemeOptionType[ThemeOptionType["UploadListOfImages"] = 5] = "UploadListOfImages";
    ThemeOptionType[ThemeOptionType["Url"] = 6] = "Url";
    ThemeOptionType[ThemeOptionType["MultiSelect"] = 7] = "MultiSelect";
    ThemeOptionType[ThemeOptionType["Text"] = 8] = "Text";
    ThemeOptionType[ThemeOptionType["TranslatedText"] = 9] = "TranslatedText";
})(ThemeOptionType || (ThemeOptionType = {}));
const themeOptionTypeOptions = mapEnumToOptions$1(ThemeOptionType);

class ThemeService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.copy = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/theme/${id}/copy`,
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/theme',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/theme/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/theme/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/theme',
            params: { type: input.type, disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMenuItems = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/theme/${id}/menu-items`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/theme/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ThemeService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ThemeService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ThemeService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var index$1 = /*#__PURE__*/Object.freeze({
    __proto__: null,
    CustomizedThemeComponentService: CustomizedThemeComponentService,
    CustomizedThemeMenuItemService: CustomizedThemeMenuItemService,
    CustomizedThemeService: CustomizedThemeService,
    ThemeComponentCategoryService: ThemeComponentCategoryService,
    ThemeComponentDependencyService: ThemeComponentDependencyService,
    ThemeComponentPropertyService: ThemeComponentPropertyService,
    ThemeComponentSectionService: ThemeComponentSectionService,
    ThemeComponentService: ThemeComponentService,
    get ThemeDataType () { return ThemeDataType; },
    ThemeMenuItemOptionService: ThemeMenuItemOptionService,
    ThemeMenuItemService: ThemeMenuItemService,
    get ThemeMenuItemType () { return ThemeMenuItemType; },
    get ThemeOptionType () { return ThemeOptionType; },
    ThemeService: ThemeService,
    themeDataTypeOptions: themeDataTypeOptions,
    themeMenuItemTypeOptions: themeMenuItemTypeOptions,
    themeOptionTypeOptions: themeOptionTypeOptions
});

class VerificationStageService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/verification-stage',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/verification-stage/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/verification-stage/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAccount = (tenantId, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/verification-stage/account',
            params: { tenantId },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/verification-stage',
            params: { filter: input.filter, countryId: input.countryId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putReorderByInput = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/verification-stage/reorder',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putUnverified = (id, tenantId, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/verification-stage/${id}/unverified/${tenantId}`,
        }, { apiName: this.apiName, ...config });
        this.putVerified = (id, tenantId, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/verification-stage/${id}/verified/${tenantId}`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/verification-stage/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
VerificationStageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerificationStageService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
VerificationStageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerificationStageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerificationStageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var VerificationType;
(function (VerificationType) {
    VerificationType[VerificationType["Text"] = 0] = "Text";
    VerificationType[VerificationType["Number"] = 1] = "Number";
    VerificationType[VerificationType["Select"] = 2] = "Select";
    VerificationType[VerificationType["MultiSelect"] = 3] = "MultiSelect";
    VerificationType[VerificationType["Image"] = 4] = "Image";
    VerificationType[VerificationType["MultiImages"] = 5] = "MultiImages";
    VerificationType[VerificationType["Url"] = 6] = "Url";
    VerificationType[VerificationType["DateTime"] = 7] = "DateTime";
})(VerificationType || (VerificationType = {}));
const verificationTypeOptions = mapEnumToOptions$1(VerificationType);

class VerificationValueService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/verification-value',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/verification-value/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/verification-value/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/verification-value',
            params: { verificationId: input.verificationId, verificationStageId: input.verificationStageId, countryId: input.countryId, isRequired: input.isRequired, type: input.type, filter: input.filter },
        }, { apiName: this.apiName, ...config });
        this.putBulk = (model, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/verification-value/bulk',
            body: model,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/verification-value/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
VerificationValueService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerificationValueService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
VerificationValueService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerificationValueService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerificationValueService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class VerificationService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/verification',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/verification/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/verification/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/verification',
            params: { verificationStageId: input.verificationStageId, countryId: input.countryId, isRequired: input.isRequired, type: input.type, filter: input.filter, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/verification/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
VerificationService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerificationService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
VerificationService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerificationService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerificationService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var index = /*#__PURE__*/Object.freeze({
    __proto__: null,
    VerificationService: VerificationService,
    VerificationStageService: VerificationStageService,
    get VerificationType () { return VerificationType; },
    VerificationValueService: VerificationValueService,
    verificationTypeOptions: verificationTypeOptions
});

var AddressType;
(function (AddressType) {
    AddressType[AddressType["Manual"] = 0] = "Manual";
})(AddressType || (AddressType = {}));
const addressTypeOptions = mapEnumToOptions$1(AddressType);

var BannerPosition;
(function (BannerPosition) {
    BannerPosition[BannerPosition["TopFixedAd"] = 1] = "TopFixedAd";
    BannerPosition[BannerPosition["MainBanner"] = 11] = "MainBanner";
    BannerPosition[BannerPosition["AfterBanner"] = 12] = "AfterBanner";
    BannerPosition[BannerPosition["AfterFeaturedItems"] = 21] = "AfterFeaturedItems";
    BannerPosition[BannerPosition["AfterOffersItems"] = 22] = "AfterOffersItems";
    BannerPosition[BannerPosition["AfterBestsellerItems"] = 23] = "AfterBestsellerItems";
    BannerPosition[BannerPosition["AfterLatestItems"] = 24] = "AfterLatestItems";
})(BannerPosition || (BannerPosition = {}));
const bannerPositionOptions = mapEnumToOptions$1(BannerPosition);

var BannerTarget;
(function (BannerTarget) {
    BannerTarget[BannerTarget["None"] = 0] = "None";
    BannerTarget[BannerTarget["Item"] = 1] = "Item";
    BannerTarget[BannerTarget["Category"] = 2] = "Category";
    BannerTarget[BannerTarget["ExternalLink"] = 3] = "ExternalLink";
})(BannerTarget || (BannerTarget = {}));
const bannerTargetOptions = mapEnumToOptions$1(BannerTarget);

class BannerService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/banner',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/banner/${id}`,
        }, { apiName: this.apiName, ...config });
        this.deleteTranslation = (id, language, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/banner/${id}/translation`,
            params: { language },
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/banner/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/banner/all',
            params: { disabled: input.disabled },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/banner',
            params: { disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getTranslations = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/banner/${id}/translations`,
        }, { apiName: this.apiName, ...config });
        this.migrateData = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/banner/migrate-data',
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/banner/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/banner/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.putTranslations = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/banner/${id}/translations`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/banner/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
BannerService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: BannerService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
BannerService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: BannerService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: BannerService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var CartMoreItemsOption;
(function (CartMoreItemsOption) {
    CartMoreItemsOption[CartMoreItemsOption["AllItems"] = 0] = "AllItems";
    CartMoreItemsOption[CartMoreItemsOption["AllCategories"] = 1] = "AllCategories";
    CartMoreItemsOption[CartMoreItemsOption["Home"] = 2] = "Home";
})(CartMoreItemsOption || (CartMoreItemsOption = {}));
const cartMoreItemsOptionOptions = mapEnumToOptions$1(CartMoreItemsOption);

var CategoriesLayoutColumnSizeOnHome;
(function (CategoriesLayoutColumnSizeOnHome) {
    CategoriesLayoutColumnSizeOnHome[CategoriesLayoutColumnSizeOnHome["Small"] = 0] = "Small";
    CategoriesLayoutColumnSizeOnHome[CategoriesLayoutColumnSizeOnHome["Medium"] = 1] = "Medium";
    CategoriesLayoutColumnSizeOnHome[CategoriesLayoutColumnSizeOnHome["Large"] = 2] = "Large";
})(CategoriesLayoutColumnSizeOnHome || (CategoriesLayoutColumnSizeOnHome = {}));
const categoriesLayoutColumnSizeOnHomeOptions = mapEnumToOptions$1(CategoriesLayoutColumnSizeOnHome);

var CategoriesLayoutHeighSizeOnHome;
(function (CategoriesLayoutHeighSizeOnHome) {
    CategoriesLayoutHeighSizeOnHome[CategoriesLayoutHeighSizeOnHome["Auto"] = 0] = "Auto";
    CategoriesLayoutHeighSizeOnHome[CategoriesLayoutHeighSizeOnHome["Short"] = 1] = "Short";
    CategoriesLayoutHeighSizeOnHome[CategoriesLayoutHeighSizeOnHome["Medium"] = 2] = "Medium";
    CategoriesLayoutHeighSizeOnHome[CategoriesLayoutHeighSizeOnHome["Long"] = 3] = "Long";
})(CategoriesLayoutHeighSizeOnHome || (CategoriesLayoutHeighSizeOnHome = {}));
const categoriesLayoutHeighSizeOnHomeOptions = mapEnumToOptions$1(CategoriesLayoutHeighSizeOnHome);

var CategoryPadding;
(function (CategoryPadding) {
    CategoryPadding[CategoryPadding["NoPadding"] = 0] = "NoPadding";
    CategoryPadding[CategoryPadding["Padding1"] = 1] = "Padding1";
    CategoryPadding[CategoryPadding["Padding2"] = 2] = "Padding2";
})(CategoryPadding || (CategoryPadding = {}));
const categoryPaddingOptions = mapEnumToOptions$1(CategoryPadding);

var CommissionType;
(function (CommissionType) {
    CommissionType[CommissionType["Percentage"] = 0] = "Percentage";
    CommissionType[CommissionType["Flat"] = 1] = "Flat";
})(CommissionType || (CommissionType = {}));
const commissionTypeOptions = mapEnumToOptions$1(CommissionType);

class CustomerStoreReviewService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/customer-store-review',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/customer-store-review/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/customer-store-review/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/customer-store-review',
            params: { filter: input.filter, fromDate: input.fromDate, toDate: input.toDate, type: input.type, orderId: input.orderId, customerId: input.customerId, questionId: input.questionId, minRating: input.minRating, maxRating: input.maxRating, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/customer-store-review/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
CustomerStoreReviewService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerStoreReviewService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
CustomerStoreReviewService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerStoreReviewService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: CustomerStoreReviewService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var DeliveryType;
(function (DeliveryType) {
    DeliveryType[DeliveryType["Takeaway"] = 10] = "Takeaway";
    DeliveryType[DeliveryType["Delivery"] = 20] = "Delivery";
    DeliveryType[DeliveryType["SelfPickup"] = 30] = "SelfPickup";
    DeliveryType[DeliveryType["DineIn"] = 40] = "DineIn";
})(DeliveryType || (DeliveryType = {}));
const deliveryTypeOptions = mapEnumToOptions$1(DeliveryType);

var FeaturesHomePosition;
(function (FeaturesHomePosition) {
    FeaturesHomePosition[FeaturesHomePosition["Top"] = 0] = "Top";
    FeaturesHomePosition[FeaturesHomePosition["Bottom"] = 1] = "Bottom";
})(FeaturesHomePosition || (FeaturesHomePosition = {}));
const featuresHomePositionOptions = mapEnumToOptions$1(FeaturesHomePosition);

var FeaturesSectionDesign;
(function (FeaturesSectionDesign) {
    FeaturesSectionDesign[FeaturesSectionDesign["Card"] = 0] = "Card";
    FeaturesSectionDesign[FeaturesSectionDesign["List"] = 1] = "List";
})(FeaturesSectionDesign || (FeaturesSectionDesign = {}));
const featuresSectionDesignOptions = mapEnumToOptions$1(FeaturesSectionDesign);

var IconPosition;
(function (IconPosition) {
    IconPosition[IconPosition["Top"] = 0] = "Top";
    IconPosition[IconPosition["Before"] = 10] = "Before";
    IconPosition[IconPosition["After"] = 20] = "After";
})(IconPosition || (IconPosition = {}));
const iconPositionOptions = mapEnumToOptions$1(IconPosition);

var IconStyle;
(function (IconStyle) {
    IconStyle[IconStyle["Outline"] = 0] = "Outline";
    IconStyle[IconStyle["Filled"] = 1] = "Filled";
    IconStyle[IconStyle["Sharp"] = 2] = "Sharp";
})(IconStyle || (IconStyle = {}));
const iconStyleOptions = mapEnumToOptions$1(IconStyle);

var NewsletterHomePosition;
(function (NewsletterHomePosition) {
    NewsletterHomePosition[NewsletterHomePosition["BeforeFeatures"] = 0] = "BeforeFeatures";
    NewsletterHomePosition[NewsletterHomePosition["AfterFeatures"] = 1] = "AfterFeatures";
})(NewsletterHomePosition || (NewsletterHomePosition = {}));
const newsletterHomePositionOptions = mapEnumToOptions$1(NewsletterHomePosition);

var OpenSelectOutletDialogOptions;
(function (OpenSelectOutletDialogOptions) {
    OpenSelectOutletDialogOptions[OpenSelectOutletDialogOptions["WhenStoreIsOpenedForFirstTime"] = 1] = "WhenStoreIsOpenedForFirstTime";
    OpenSelectOutletDialogOptions[OpenSelectOutletDialogOptions["WhenAddingToCartForFirstTime"] = 2] = "WhenAddingToCartForFirstTime";
    OpenSelectOutletDialogOptions[OpenSelectOutletDialogOptions["Never"] = 3] = "Never";
})(OpenSelectOutletDialogOptions || (OpenSelectOutletDialogOptions = {}));
const openSelectOutletDialogOptionsOptions = mapEnumToOptions$1(OpenSelectOutletDialogOptions);

var OutletSectionStatus;
(function (OutletSectionStatus) {
    OutletSectionStatus[OutletSectionStatus["Open"] = 0] = "Open";
    OutletSectionStatus[OutletSectionStatus["Closed"] = 1] = "Closed";
})(OutletSectionStatus || (OutletSectionStatus = {}));
const outletSectionStatusOptions = mapEnumToOptions$1(OutletSectionStatus);

class OutletSectionService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/outlet-section',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/outlet-section/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/outlet-section/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/outlet-section/all',
            params: { outletId: input.outletId, status: input.status, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/outlet-section',
            params: { outletId: input.outletId, status: input.status, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putChangeStatus = (id, status, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/outlet-section/${id}/change-status`,
            params: { status },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/outlet-section/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
OutletSectionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OutletSectionService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
OutletSectionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OutletSectionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OutletSectionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var OutletStatus;
(function (OutletStatus) {
    OutletStatus[OutletStatus["Draft"] = 0] = "Draft";
    OutletStatus[OutletStatus["Active"] = 1] = "Active";
    OutletStatus[OutletStatus["Busy"] = 2] = "Busy";
    OutletStatus[OutletStatus["TemporarilyClosed"] = 3] = "TemporarilyClosed";
    OutletStatus[OutletStatus["Closed"] = 4] = "Closed";
    OutletStatus[OutletStatus["Disabled"] = 5] = "Disabled";
})(OutletStatus || (OutletStatus = {}));
const outletStatusOptions = mapEnumToOptions$1(OutletStatus);

var OutletTableStatus;
(function (OutletTableStatus) {
    OutletTableStatus[OutletTableStatus["Available"] = 0] = "Available";
    OutletTableStatus[OutletTableStatus["Reserved"] = 1] = "Reserved";
    OutletTableStatus[OutletTableStatus["Closed"] = 2] = "Closed";
})(OutletTableStatus || (OutletTableStatus = {}));
const outletTableStatusOptions = mapEnumToOptions$1(OutletTableStatus);

class OutletTableService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/outlet-table',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/outlet-table/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/outlet-table/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/outlet-table',
            params: { status: input.status, outletId: input.outletId, outletSectionId: input.outletSectionId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getOutletTableByIdById = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/outlet-table/${id}/store`,
        }, { apiName: this.apiName, ...config });
        this.putChangeStatus = (id, status, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/outlet-table/${id}/change-status`,
            params: { status },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/outlet-table/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
OutletTableService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OutletTableService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
OutletTableService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OutletTableService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OutletTableService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class OutletService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/outlet',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/outlet/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/outlet/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/outlet/all',
            params: { status: input.status },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/outlet',
            params: { sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getMarketplaceList = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/outlet/marketplace-list',
        }, { apiName: this.apiName, ...config });
        this.getOptions = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/outlet/${id}/options`,
        }, { apiName: this.apiName, ...config });
        this.getStoreList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/outlet/store-list',
            params: { status: input.status },
        }, { apiName: this.apiName, ...config });
        this.putChangeStatus = (id, status, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/outlet/${id}/change-status`,
            params: { status },
        }, { apiName: this.apiName, ...config });
        this.putDefault = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/outlet/${id}/default`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/outlet/disable',
            params: { id },
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/outlet/enable',
            params: { id },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/outlet/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (options, id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/outlet/${id}/options`,
            body: options,
        }, { apiName: this.apiName, ...config });
    }
}
OutletService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OutletService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
OutletService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OutletService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: OutletService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var PrintTemplateType;
(function (PrintTemplateType) {
    PrintTemplateType[PrintTemplateType["Invoice"] = 0] = "Invoice";
    PrintTemplateType[PrintTemplateType["Receipt"] = 1] = "Receipt";
})(PrintTemplateType || (PrintTemplateType = {}));
const printTemplateTypeOptions = mapEnumToOptions$1(PrintTemplateType);

class PrintTemplateService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/print-template',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/print-template/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/print-template/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getInvoiceOptions = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/print-template/${id}/invoice/options`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/print-template',
            params: { type: input.type, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getReceiptOptions = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/print-template/${id}/receipt/options`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/print-template/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateInvoiceOptions = (id, options, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/print-template/${id}/invoice-options`,
            body: options,
        }, { apiName: this.apiName, ...config });
        this.updateReceiptOptions = (id, options, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/print-template/${id}/receipt-options`,
            body: options,
        }, { apiName: this.apiName, ...config });
    }
}
PrintTemplateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PrintTemplateService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
PrintTemplateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PrintTemplateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PrintTemplateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var SaleType;
(function (SaleType) {
    SaleType[SaleType["None"] = 0] = "None";
    SaleType[SaleType["Wholesale"] = 1] = "Wholesale";
    SaleType[SaleType["Retail"] = 2] = "Retail";
})(SaleType || (SaleType = {}));
const saleTypeOptions = mapEnumToOptions$1(SaleType);

var SalesChannelStatus;
(function (SalesChannelStatus) {
    SalesChannelStatus[SalesChannelStatus["Enable"] = 0] = "Enable";
    SalesChannelStatus[SalesChannelStatus["Disable"] = 1] = "Disable";
})(SalesChannelStatus || (SalesChannelStatus = {}));
const salesChannelStatusOptions = mapEnumToOptions$1(SalesChannelStatus);

class SalesChannelService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.getList = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/sales-channel',
        }, { apiName: this.apiName, ...config });
        this.update = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/sales-channel/${id}`,
        }, { apiName: this.apiName, ...config });
    }
}
SalesChannelService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SalesChannelService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SalesChannelService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SalesChannelService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SalesChannelService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var StorePageStatus;
(function (StorePageStatus) {
    StorePageStatus[StorePageStatus["Draft"] = 0] = "Draft";
    StorePageStatus[StorePageStatus["Published"] = 1] = "Published";
})(StorePageStatus || (StorePageStatus = {}));
const storePageStatusOptions = mapEnumToOptions$1(StorePageStatus);

var StorePageType;
(function (StorePageType) {
    StorePageType[StorePageType["Basic"] = 0] = "Basic";
    StorePageType[StorePageType["Blog"] = 1] = "Blog";
})(StorePageType || (StorePageType = {}));
const storePageTypeOptions = mapEnumToOptions$1(StorePageType);

class StorePageService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store-page',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/store-page/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store-page/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store-page',
            params: { search: input.search, type: input.type, isPublish: input.isPublish, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getStoreList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store-page/store-list',
            params: { search: input.search, type: input.type, isPublish: input.isPublish, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getStorePageBySlug = (slug, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store-page/slug/${slug}`,
        }, { apiName: this.apiName, ...config });
        this.publish = (id, dateTime, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/store-page/${id}/publish`,
            params: { dateTime },
        }, { apiName: this.apiName, ...config });
        this.unpublish = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/store-page/${id}/unpublish`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/store-page/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
StorePageService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StorePageService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
StorePageService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StorePageService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StorePageService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var StoreReviewQuestionType;
(function (StoreReviewQuestionType) {
    StoreReviewQuestionType[StoreReviewQuestionType["Rating"] = 1] = "Rating";
    StoreReviewQuestionType[StoreReviewQuestionType["Textual"] = 2] = "Textual";
    StoreReviewQuestionType[StoreReviewQuestionType["Both"] = 3] = "Both";
})(StoreReviewQuestionType || (StoreReviewQuestionType = {}));
const storeReviewQuestionTypeOptions = mapEnumToOptions$1(StoreReviewQuestionType);

class StoreReviewQuestionService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store-review-question',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/store-review-question/${id}`,
        }, { apiName: this.apiName, ...config });
        this.disable = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/store-review-question/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.enable = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/store-review-question/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store-review-question/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store-review-question',
            params: { filter: input.filter, fromDate: input.fromDate, toDate: input.toDate, disabled: input.disabled, type: input.type, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.reorder = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store-review-question/reorder',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/store-review-question/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
StoreReviewQuestionService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreReviewQuestionService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
StoreReviewQuestionService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreReviewQuestionService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreReviewQuestionService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class StoreReviewService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/store-review/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store-review/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store-review',
            params: { isPublished: input.isPublished, replierId: input.replierId, filter: input.filter, fromDate: input.fromDate, toDate: input.toDate, type: input.type, orderId: input.orderId, customerId: input.customerId, questionId: input.questionId, minRating: input.minRating, maxRating: input.maxRating, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.publish = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store-review/publish',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.reply = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store-review/reply',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
StoreReviewService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreReviewService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
StoreReviewService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreReviewService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreReviewService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var StoreStatus;
(function (StoreStatus) {
    StoreStatus[StoreStatus["UnderMaintenance"] = 0] = "UnderMaintenance";
    StoreStatus[StoreStatus["Active"] = 1] = "Active";
})(StoreStatus || (StoreStatus = {}));
const storeStatusOptions = mapEnumToOptions$1(StoreStatus);

var StoreSupportedCountriesType;
(function (StoreSupportedCountriesType) {
    StoreSupportedCountriesType[StoreSupportedCountriesType["AllCountries"] = 0] = "AllCountries";
    StoreSupportedCountriesType[StoreSupportedCountriesType["StoreCountryOnly"] = 1] = "StoreCountryOnly";
    StoreSupportedCountriesType[StoreSupportedCountriesType["OutletCountryOnly"] = 2] = "OutletCountryOnly";
    StoreSupportedCountriesType[StoreSupportedCountriesType["SpecificCountries"] = 3] = "SpecificCountries";
})(StoreSupportedCountriesType || (StoreSupportedCountriesType = {}));
const storeSupportedCountriesTypeOptions = mapEnumToOptions$1(StoreSupportedCountriesType);

var StoreTemplateType;
(function (StoreTemplateType) {
    StoreTemplateType[StoreTemplateType["Web"] = 0] = "Web";
    StoreTemplateType[StoreTemplateType["Mobile"] = 1] = "Mobile";
})(StoreTemplateType || (StoreTemplateType = {}));
const storeTemplateTypeOptions = mapEnumToOptions$1(StoreTemplateType);

class StoreTemplateService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.copy = (Id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/store-template/copy/${Id}`,
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store-template',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/store-template/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store-template/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAll = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store-template/all',
            params: { type: input.type, parentId: input.parentId, disabled: input.disabled },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store-template',
            params: { disabled: input.disabled, type: input.type, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getOptions = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store-template/${id}/options`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/store-template/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/store-template/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.putReset = (Id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/store-template/reset/${Id}`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/store-template/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (id, options, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/store-template/${id}/options`,
            body: options,
        }, { apiName: this.apiName, ...config });
    }
}
StoreTemplateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreTemplateService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
StoreTemplateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreTemplateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreTemplateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class StoreService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.addDomain = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store/domain',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.applyStoreTemplate = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store/apply-store-template',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.applyStoreTheme = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store/apply-store-theme',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.checkCustomDomain = (domain, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store/check-custom-domain',
            params: { domain },
        }, { apiName: this.apiName, ...config });
        this.checkDomain = (domain, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store/check-domain',
            params: { domain },
        }, { apiName: this.apiName, ...config });
        this.clearCache = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/store/${id}/clear-cache`,
        }, { apiName: this.apiName, ...config });
        this.completeRegistration = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store/complete-registration',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.contactUs = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store/contact-us',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/store',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.deleteDomain = (input, config) => this.restService.request({
            method: 'DELETE',
            url: '/api/app/store/domain',
            params: { storeId: input.storeId, domain: input.domain },
        }, { apiName: this.apiName, ...config });
        this.deleteStore = (id, tenantId, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/store/${id}/${tenantId}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAppliedMarketplace = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store/applied-marketplace',
            params: { industryId: input.industryId, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getCommissionHistory = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store/commission-history',
            params: { relatedStoreId: input.relatedStoreId, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getDashboardSummary = (id, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store/${id}/dashboard-summary`,
            params: { fromDate: input.fromDate, toDate: input.toDate },
        }, { apiName: this.apiName, ...config });
        this.getDetails = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store/${id}/details`,
        }, { apiName: this.apiName, ...config });
        this.getFeatures = (storeId, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store/features',
            params: { storeId },
        }, { apiName: this.apiName, ...config });
        this.getIncomplete = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store/incomplete',
            params: { tenantId: input.tenantId, search: input.search, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store',
            params: { search: input.search, currencyId: input.currencyId, countryId: input.countryId, industryId: input.industryId, productId: input.productId, status: input.status, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getManifest = (domain, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store/manifest/${domain}`,
        }, { apiName: this.apiName, ...config });
        this.getMarketplaces = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store/marketplaces',
        }, { apiName: this.apiName, ...config });
        this.getMy = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store/my',
        }, { apiName: this.apiName, ...config });
        this.getOptions = (id, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store/options',
            params: { id },
        }, { apiName: this.apiName, ...config });
        this.getQuickCheckout = (outletId, currencyId, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store/quick-checkout',
            params: { outletId, currencyId },
        }, { apiName: this.apiName, ...config });
        this.getRelatedAppliedMarketplace = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store/${id}/related-applied-marketplace`,
        }, { apiName: this.apiName, ...config });
        this.getRelatedStoreDesign = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/store/related-store-design',
            params: { tenantId: input.tenantId, pageId: input.pageId },
        }, { apiName: this.apiName, ...config });
        this.getRelatedStoreStatistical = (id, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store/${id}/related-store-statistical`,
            params: { fromDate: input.fromDate, toDate: input.toDate, orderStatus: input.orderStatus, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getStoreByDomain = (domain, token, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store/domain/${domain}`,
            params: { token },
        }, { apiName: this.apiName, ...config });
        this.getStoreById = (id, token, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/store/id/${id}`,
            params: { token },
        }, { apiName: this.apiName, ...config });
        this.getStorefront = (domain, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/storefront/${domain}`,
            params: { token: input.token, themeId: input.themeId, outletId: input.outletId, pageId: input.pageId, version: input.version },
        }, { apiName: this.apiName, ...config });
        this.getStorefrontPage = (id, input, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/storefront/page/${id}`,
            params: { privacyPolicyPageId: input.privacyPolicyPageId, termsAndConditionsPageId: input.termsAndConditionsPageId },
        }, { apiName: this.apiName, ...config });
        this.putApplyMarketplace = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/store/apply-marketplace',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putChangeStatus = (id, status, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/store/${id}/change-status`,
            params: { status },
        }, { apiName: this.apiName, ...config });
        this.putCommissions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/store/commissions',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putDefaultDomain = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/store/default-domain',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putFeatures = (input, storeId, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/store/features',
            params: { storeId },
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putMarketplace = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/store/${id}/marketplace`,
        }, { apiName: this.apiName, ...config });
        this.putSeo = (input, storeId, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/store/seo/${storeId}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/store/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateDomain = (domain, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/store/domain',
            params: { domain },
        }, { apiName: this.apiName, ...config });
        this.updateLanguages = (languages, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/store/languages',
            body: languages,
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (options, storeId, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/store/options',
            params: { storeId },
            body: options,
        }, { apiName: this.apiName, ...config });
    }
}
StoreService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
StoreService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StoreService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class StorefrontService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.geThemeComponent = (pageId, type, themeId, version, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/storefront/theme/components/${pageId}`,
            params: { type, themeId, version },
        }, { apiName: this.apiName, ...config });
        this.geThemeOptions = (type, themeId, version, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/storefront/theme/options',
            params: { type, themeId, version },
        }, { apiName: this.apiName, ...config });
        this.getBasic = (domain, token, version, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/storefront/basic/${domain}`,
            params: { token, version },
        }, { apiName: this.apiName, ...config });
        this.getInfo = (outletId, isMarketplace, version, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/storefront/info',
            params: { outletId, isMarketplace, version },
        }, { apiName: this.apiName, ...config });
        this.getMobile = (id, token, version, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/storefront/mobile/${id}`,
            params: { token, version },
        }, { apiName: this.apiName, ...config });
    }
}
StorefrontService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StorefrontService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
StorefrontService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StorefrontService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StorefrontService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var SubtotalType;
(function (SubtotalType) {
    SubtotalType[SubtotalType["WithTax"] = 0] = "WithTax";
    SubtotalType[SubtotalType["WithoutTax"] = 1] = "WithoutTax";
})(SubtotalType || (SubtotalType = {}));
const subtotalTypeOptions = mapEnumToOptions$1(SubtotalType);

var Gender;
(function (Gender) {
    Gender[Gender["Unknown"] = 0] = "Unknown";
    Gender[Gender["Male"] = 1] = "Male";
    Gender[Gender["Female"] = 2] = "Female";
})(Gender || (Gender = {}));
const genderOptions = mapEnumToOptions$1(Gender);

var TwilioVerificationChannels;
(function (TwilioVerificationChannels) {
    TwilioVerificationChannels[TwilioVerificationChannels["Sms"] = 0] = "Sms";
    TwilioVerificationChannels[TwilioVerificationChannels["WhatsApp"] = 1] = "WhatsApp";
    TwilioVerificationChannels[TwilioVerificationChannels["Call"] = 2] = "Call";
    TwilioVerificationChannels[TwilioVerificationChannels["SNA"] = 3] = "SNA";
})(TwilioVerificationChannels || (TwilioVerificationChannels = {}));
const twilioVerificationChannelsOptions = mapEnumToOptions$1(TwilioVerificationChannels);

var UserType;
(function (UserType) {
    UserType[UserType["Owner"] = 0] = "Owner";
    UserType[UserType["User"] = 10] = "User";
    UserType[UserType["Vendor"] = 20] = "Vendor";
    UserType[UserType["Customer"] = 30] = "Customer";
})(UserType || (UserType = {}));
const userTypeOptions = mapEnumToOptions$1(UserType);

class UserService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.block = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/user/${id}/block`,
        }, { apiName: this.apiName, ...config });
        this.blockUserTenantByHost = (id, tenantId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/user/${id}/block-user-tenant-by-host/${tenantId}`,
        }, { apiName: this.apiName, ...config });
        this.checkEmail = (email, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/user/${email}/check-email`,
        }, { apiName: this.apiName, ...config });
        this.checkIsActiveUser = (config) => this.restService.request({
            method: 'POST',
            url: '/api/app/user/check-is-active',
        }, { apiName: this.apiName, ...config });
        this.checkPhoneNumber = (phoneNumber, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/user/check-phone-number',
            params: { phoneNumber },
        }, { apiName: this.apiName, ...config });
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/user',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createUserTenantByHost = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/user/user-tenant-by-host',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/user/${id}`,
        }, { apiName: this.apiName, ...config });
        this.deleteUserTenantByHost = (id, tenantId, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/user/${id}/user-tenant-by-host/${tenantId}`,
        }, { apiName: this.apiName, ...config });
        this.findByEmail = (email, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/user/find-by-email',
            params: { email },
        }, { apiName: this.apiName, ...config });
        this.findByUsername = (userName, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/user/find-by-username',
            params: { userName },
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/user/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getAccountManagerStores = (userId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/user/account-manager-stores/${userId}`,
        }, { apiName: this.apiName, ...config });
        this.getAssignableRoles = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/user/assignable-roles',
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/user',
            params: { filter: input.filter, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getRoles = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/user/${id}/roles`,
        }, { apiName: this.apiName, ...config });
        this.getUserTenantByHost = (id, tenantId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/user/${id}/user-tenant-by-host/${tenantId}`,
        }, { apiName: this.apiName, ...config });
        this.getUserTenantByHostList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/user/user-tenant-by-host-list',
            params: { tenantId: input.tenantId, filter: input.filter, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putAccountManager = (model, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/user/account-manager',
            body: model,
        }, { apiName: this.apiName, ...config });
        this.resetPassword = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/user/${id}/reset-password`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.resetPasswordUserTenantByHost = (id, input, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/user/${id}/reset-password-user-tenant-by-host`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.unblock = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/user/${id}/unblock`,
        }, { apiName: this.apiName, ...config });
        this.unblockUserTenantByHost = (id, tenantId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/user/${id}/unblock-user-tenant-by-host/${tenantId}`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/user/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateRoles = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/user/${id}/roles`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateUserTenantByHost = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/user/${id}/user-tenant-by-host`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
UserService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: UserService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
UserService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: UserService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: UserService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class VerifyService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.sendVerification = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/verify/send-verification',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
VerifyService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerifyService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
VerifyService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerifyService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: VerifyService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var IyzicoCardBrand;
(function (IyzicoCardBrand) {
    IyzicoCardBrand[IyzicoCardBrand["Mastercard"] = 0] = "Mastercard";
    IyzicoCardBrand[IyzicoCardBrand["Visa"] = 1] = "Visa";
    IyzicoCardBrand[IyzicoCardBrand["Troy"] = 2] = "Troy";
    IyzicoCardBrand[IyzicoCardBrand["AmericanExpress"] = 3] = "AmericanExpress";
})(IyzicoCardBrand || (IyzicoCardBrand = {}));
const iyzicoCardBrandOptions = mapEnumToOptions$1(IyzicoCardBrand);

class IyzicoPaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.createPayment = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/iyzico-payment-method/payment',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/iyzico-payment-method/options',
        }, { apiName: this.apiName, ...config });
        this.getPayment = (token, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/iyzico-payment-method/payment',
            params: { token },
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/iyzico-payment-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
IyzicoPaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: IyzicoPaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
IyzicoPaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: IyzicoPaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: IyzicoPaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var IyzicoType;
(function (IyzicoType) {
    IyzicoType[IyzicoType["Card"] = 10] = "Card";
})(IyzicoType || (IyzicoType = {}));
const iyzicoTypeOptions = mapEnumToOptions$1(IyzicoType);

var MontypayCardBrand;
(function (MontypayCardBrand) {
    MontypayCardBrand[MontypayCardBrand["Visa"] = 0] = "Visa";
    MontypayCardBrand[MontypayCardBrand["Mastercard"] = 1] = "Mastercard";
    MontypayCardBrand[MontypayCardBrand["AmericanExpress"] = 2] = "AmericanExpress";
})(MontypayCardBrand || (MontypayCardBrand = {}));
const montypayCardBrandOptions = mapEnumToOptions$1(MontypayCardBrand);

class MontypayPaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.createPayment = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/montypay-payment-method/payment',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createRefund = (payload, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/montypay-payment-method/refund',
            body: payload,
        }, { apiName: this.apiName, ...config });
        this.deleteCard = (customerId, token, card, cardExpirationDate, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/montypay-payment-method/card/${customerId}`,
            params: { token, card, cardExpirationDate },
        }, { apiName: this.apiName, ...config });
        this.getCards = (customerId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/montypay-payment-method/cards/${customerId}`,
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/montypay-payment-method/options',
        }, { apiName: this.apiName, ...config });
        this.getOrder = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/montypay-payment-method/${id}/order`,
        }, { apiName: this.apiName, ...config });
        this.getTransaction = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/montypay-payment-method/${id}/transaction`,
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/montypay-payment-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
MontypayPaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MontypayPaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
MontypayPaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MontypayPaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MontypayPaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var MontypayType;
(function (MontypayType) {
    MontypayType[MontypayType["Card"] = 0] = "Card";
})(MontypayType || (MontypayType = {}));
const montypayTypeOptions = mapEnumToOptions$1(MontypayType);

class MyFatoorahPaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.createPayment = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/my-fatoorah-payment-method/payment',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/my-fatoorah-payment-method/options',
        }, { apiName: this.apiName, ...config });
        this.getPayment = (invoiceOrPaymentId, forInvoice, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/my-fatoorah-payment-method/payment/${invoiceOrPaymentId}`,
            params: { forInvoice },
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/my-fatoorah-payment-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
MyFatoorahPaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MyFatoorahPaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
MyFatoorahPaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MyFatoorahPaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: MyFatoorahPaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var MyFatoorahType;
(function (MyFatoorahType) {
    MyFatoorahType[MyFatoorahType["KNet"] = 0] = "KNet";
    MyFatoorahType[MyFatoorahType["Amex"] = 1] = "Amex";
    MyFatoorahType[MyFatoorahType["Sadad"] = 2] = "Sadad";
    MyFatoorahType[MyFatoorahType["Benefit"] = 3] = "Benefit";
    MyFatoorahType[MyFatoorahType["Mada"] = 4] = "Mada";
    MyFatoorahType[MyFatoorahType["ApplePay"] = 5] = "ApplePay";
    MyFatoorahType[MyFatoorahType["StcPay"] = 6] = "StcPay";
    MyFatoorahType[MyFatoorahType["OmanNet"] = 7] = "OmanNet";
    MyFatoorahType[MyFatoorahType["Meeza"] = 8] = "Meeza";
    MyFatoorahType[MyFatoorahType["Card"] = 9] = "Card";
})(MyFatoorahType || (MyFatoorahType = {}));
const myFatoorahTypeOptions = mapEnumToOptions$1(MyFatoorahType);

var MyfatoorahCardBrand;
(function (MyfatoorahCardBrand) {
    MyfatoorahCardBrand[MyfatoorahCardBrand["Visa"] = 0] = "Visa";
    MyfatoorahCardBrand[MyfatoorahCardBrand["Mastercard"] = 1] = "Mastercard";
    MyfatoorahCardBrand[MyfatoorahCardBrand["KNetCard"] = 2] = "KNetCard";
    MyfatoorahCardBrand[MyfatoorahCardBrand["BenefitCard"] = 3] = "BenefitCard";
    MyfatoorahCardBrand[MyfatoorahCardBrand["AmericanExpress"] = 4] = "AmericanExpress";
    MyfatoorahCardBrand[MyfatoorahCardBrand["QPay"] = 5] = "QPay";
    MyfatoorahCardBrand[MyfatoorahCardBrand["ApplePayCard"] = 6] = "ApplePayCard";
})(MyfatoorahCardBrand || (MyfatoorahCardBrand = {}));
const myfatoorahCardBrandOptions = mapEnumToOptions$1(MyfatoorahCardBrand);

var PayPalPaymentIntentType;
(function (PayPalPaymentIntentType) {
    PayPalPaymentIntentType[PayPalPaymentIntentType["CAPTURE"] = 0] = "CAPTURE";
    PayPalPaymentIntentType[PayPalPaymentIntentType["AUTHORIZE"] = 1] = "AUTHORIZE";
})(PayPalPaymentIntentType || (PayPalPaymentIntentType = {}));
const payPalPaymentIntentTypeOptions = mapEnumToOptions$1(PayPalPaymentIntentType);

class PayPalPaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.capture = (payPalOrderId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/pay-pal-payment-method/capture/${payPalOrderId}`,
        }, { apiName: this.apiName, ...config });
        this.create = (orderId, intentType, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/pay-pal-payment-method',
            params: { orderId, intentType },
        }, { apiName: this.apiName, ...config });
        this.createRefund = (payload, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/pay-pal-payment-method/refund',
            body: payload,
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/pay-pal-payment-method/options',
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/pay-pal-payment-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
PayPalPaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PayPalPaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
PayPalPaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PayPalPaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PayPalPaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var PayPalType;
(function (PayPalType) {
    PayPalType[PayPalType["Checkout"] = 10] = "Checkout";
})(PayPalType || (PayPalType = {}));
const payPalTypeOptions = mapEnumToOptions$1(PayPalType);

class PaymentCategoryService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/payment-category',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/payment-category/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/payment-category/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/payment-category',
            params: { skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/payment-category/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
PaymentCategoryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentCategoryService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
PaymentCategoryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentCategoryService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentCategoryService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var PaymentMethodProvider;
(function (PaymentMethodProvider) {
    PaymentMethodProvider[PaymentMethodProvider["Manual"] = 0] = "Manual";
    PaymentMethodProvider[PaymentMethodProvider["BankTransfer"] = 5] = "BankTransfer";
    PaymentMethodProvider[PaymentMethodProvider["PayPal"] = 10] = "PayPal";
    PaymentMethodProvider[PaymentMethodProvider["Stripe"] = 20] = "Stripe";
    PaymentMethodProvider[PaymentMethodProvider["QiCard"] = 30] = "QiCard";
    PaymentMethodProvider[PaymentMethodProvider["Tap"] = 40] = "Tap";
    PaymentMethodProvider[PaymentMethodProvider["Iyzico"] = 50] = "Iyzico";
    PaymentMethodProvider[PaymentMethodProvider["Tdsp"] = 60] = "Tdsp";
    PaymentMethodProvider[PaymentMethodProvider["MyFatoorah"] = 70] = "MyFatoorah";
    PaymentMethodProvider[PaymentMethodProvider["Saifalo"] = 80] = "Saifalo";
    PaymentMethodProvider[PaymentMethodProvider["Montypay"] = 90] = "Montypay";
    PaymentMethodProvider[PaymentMethodProvider["PayTabs"] = 100] = "PayTabs";
})(PaymentMethodProvider || (PaymentMethodProvider = {}));
const paymentMethodProviderOptions = mapEnumToOptions$1(PaymentMethodProvider);

class PaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/payment-method',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/payment-method/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/payment-method/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/payment-method',
            params: { tenantId: input.tenantId, currencyId: input.currencyId, disabled: input.disabled, providerType: input.providerType, provider: input.provider, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getOptions = (provider, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/payment-method/options/${provider}`,
        }, { apiName: this.apiName, ...config });
        this.getPaymentGateways = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/payment-method/payment-gateways',
        }, { apiName: this.apiName, ...config });
        this.putCash = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/payment-method/${id}/cash`,
        }, { apiName: this.apiName, ...config });
        this.putDefault = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/payment-method/${id}/default`,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/payment-method/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/payment-method/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/payment-method/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (provider, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/payment-method/options/${provider}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
PaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
PaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var PaymentMode;
(function (PaymentMode) {
    PaymentMode[PaymentMode["Received"] = 0] = "Received";
    PaymentMode[PaymentMode["ReceivedRefund"] = 1] = "ReceivedRefund";
    PaymentMode[PaymentMode["Made"] = 2] = "Made";
    PaymentMode[PaymentMode["RefundMade"] = 3] = "RefundMade";
})(PaymentMode || (PaymentMode = {}));
const paymentModeOptions = mapEnumToOptions$1(PaymentMode);

var PaymentStatus;
(function (PaymentStatus) {
    PaymentStatus[PaymentStatus["Pending"] = 0] = "Pending";
    PaymentStatus[PaymentStatus["Received"] = 10] = "Received";
    PaymentStatus[PaymentStatus["Canceled"] = 20] = "Canceled";
    PaymentStatus[PaymentStatus["Failed"] = 21] = "Failed";
    PaymentStatus[PaymentStatus["Voided"] = 22] = "Voided";
})(PaymentStatus || (PaymentStatus = {}));
const paymentStatusOptions = mapEnumToOptions$1(PaymentStatus);

var PaymentTermType;
(function (PaymentTermType) {
    PaymentTermType[PaymentTermType["DueOnReceipt"] = 0] = "DueOnReceipt";
    PaymentTermType[PaymentTermType["EndOfThisMonth"] = 10] = "EndOfThisMonth";
    PaymentTermType[PaymentTermType["EndNextMonth"] = 20] = "EndNextMonth";
    PaymentTermType[PaymentTermType["Custom"] = 30] = "Custom";
})(PaymentTermType || (PaymentTermType = {}));
const paymentTermTypeOptions = mapEnumToOptions$1(PaymentTermType);

class PaymentTermService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/payment-term',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/payment-term/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/payment-term/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/payment-term',
            params: { sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/payment-term/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
PaymentTermService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentTermService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
PaymentTermService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentTermService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentTermService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class PaymentService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/payment',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createMarketplace = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/payment/marketplace',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createStore = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/payment/store',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/payment/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/payment/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getHistory = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/payment/history',
            params: { paymentId: input.paymentId, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/payment',
            params: { search: input.search, mode: input.mode, status: input.status, marketplaceId: input.marketplaceId, paymentMethodId: input.paymentMethodId, customerId: input.customerId, currencyId: input.currencyId, branchId: input.branchId, referenceId: input.referenceId, orderId: input.orderId, invoiceId: input.invoiceId, fromDate: input.fromDate, toDate: input.toDate, isReceived: input.isReceived, isMade: input.isMade, paymentCategoryIds: input.paymentCategoryIds, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getSimpleList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/payment/simple-list',
            params: { search: input.search, mode: input.mode, status: input.status, marketplaceId: input.marketplaceId, paymentMethodId: input.paymentMethodId, customerId: input.customerId, currencyId: input.currencyId, branchId: input.branchId, referenceId: input.referenceId, orderId: input.orderId, invoiceId: input.invoiceId, fromDate: input.fromDate, toDate: input.toDate, isReceived: input.isReceived, isMade: input.isMade, paymentCategoryIds: input.paymentCategoryIds, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putChangeStatus = (id, status, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/payment/${id}/change-status`,
            params: { status },
        }, { apiName: this.apiName, ...config });
        this.resyncStatus = (id, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/payment/${id}/resync-status`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/payment/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
        this.updateMarketplace = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/payment/${id}/marketplace`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
PaymentService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
PaymentService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: PaymentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var QiCardBrand;
(function (QiCardBrand) {
    QiCardBrand[QiCardBrand["Mastercard"] = 0] = "Mastercard";
    QiCardBrand[QiCardBrand["Visa"] = 1] = "Visa";
})(QiCardBrand || (QiCardBrand = {}));
const qiCardBrandOptions = mapEnumToOptions$1(QiCardBrand);

class QiCardPaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/qi-card-payment-method',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/qi-card-payment-method/options',
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/qi-card-payment-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
QiCardPaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: QiCardPaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
QiCardPaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: QiCardPaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: QiCardPaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var QiCardType;
(function (QiCardType) {
    QiCardType[QiCardType["Card"] = 0] = "Card";
})(QiCardType || (QiCardType = {}));
const qiCardTypeOptions = mapEnumToOptions$1(QiCardType);

class SaifaloPaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.createPayment = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/saifalo-payment-method/payment',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/saifalo-payment-method/options',
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/saifalo-payment-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
SaifaloPaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaifaloPaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
SaifaloPaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaifaloPaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: SaifaloPaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var SaifaloType;
(function (SaifaloType) {
    SaifaloType[SaifaloType["Zaad"] = 0] = "Zaad";
    SaifaloType[SaifaloType["Edahab"] = 1] = "Edahab";
    SaifaloType[SaifaloType["Pbwallet"] = 2] = "Pbwallet";
})(SaifaloType || (SaifaloType = {}));
const saifaloTypeOptions = mapEnumToOptions$1(SaifaloType);

var StripeCardBrand;
(function (StripeCardBrand) {
    StripeCardBrand[StripeCardBrand["Visa"] = 0] = "Visa";
    StripeCardBrand[StripeCardBrand["Mastercard"] = 1] = "Mastercard";
    StripeCardBrand[StripeCardBrand["AmericanExpress"] = 2] = "AmericanExpress";
})(StripeCardBrand || (StripeCardBrand = {}));
const stripeCardBrandOptions = mapEnumToOptions$1(StripeCardBrand);

class StripePaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.confirmCardRequiresAuthentication = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/stripe-payment-method/confirm-card-requires-authentication',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createPaymentCheckout = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/stripe-payment-method/payment-checkout',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createPaymentIntent = (orderId, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/stripe-payment-method/payment-intent/${orderId}`,
        }, { apiName: this.apiName, ...config });
        this.deleteCard = (cardId, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/stripe-payment-method/card/${cardId}`,
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/stripe-payment-method/options',
        }, { apiName: this.apiName, ...config });
        this.getSavedCardList = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/stripe-payment-method/saved-card-list',
        }, { apiName: this.apiName, ...config });
        this.payWithExistingCard = (orderId, stripePaymentMethodId, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/stripe-payment-method/pay-with-existing-card',
            params: { orderId, stripePaymentMethodId },
        }, { apiName: this.apiName, ...config });
        this.payWithNewCard = (stripePaymentIntentId, remember, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/stripe-payment-method/pay-with-new-card/${stripePaymentIntentId}`,
            params: { remember },
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/stripe-payment-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
StripePaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StripePaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
StripePaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StripePaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: StripePaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var StripeType;
(function (StripeType) {
    StripeType[StripeType["Card"] = 0] = "Card";
    StripeType[StripeType["Fpx"] = 1] = "Fpx";
    StripeType[StripeType["Alipay"] = 2] = "Alipay";
    StripeType[StripeType["Grabpay"] = 3] = "Grabpay";
    StripeType[StripeType["Sofort"] = 4] = "Sofort";
    StripeType[StripeType["Klarna"] = 5] = "Klarna";
    StripeType[StripeType["Giropay"] = 6] = "Giropay";
    StripeType[StripeType["Sepa_debit"] = 7] = "Sepa_debit";
})(StripeType || (StripeType = {}));
const stripeTypeOptions = mapEnumToOptions$1(StripeType);

var TapCardBrand;
(function (TapCardBrand) {
    TapCardBrand[TapCardBrand["Visa"] = 0] = "Visa";
    TapCardBrand[TapCardBrand["Mastercard"] = 1] = "Mastercard";
    TapCardBrand[TapCardBrand["AmericanExpress"] = 2] = "AmericanExpress";
    TapCardBrand[TapCardBrand["Mada"] = 3] = "Mada";
})(TapCardBrand || (TapCardBrand = {}));
const tapCardBrandOptions = mapEnumToOptions$1(TapCardBrand);

class TapPaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.createCardToken = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/tap-payment-method/card-token',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createCharge = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/tap-payment-method/charge',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createCustomerCard = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/tap-payment-method/customer-card',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.createOrGetTapCustomer = (customerId, lastName, email, config) => this.restService.request({
            method: 'POST',
            url: `/api/app/tap-payment-method/or-get-tap-customer/${customerId}`,
            params: { lastName, email },
        }, { apiName: this.apiName, ...config });
        this.createSavedCardToken = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/tap-payment-method/saved-card-token',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.deleteCustomerCard = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/tap-payment-method/customer-card/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getCard = (customerId, cardId, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/tap-payment-method/card/${customerId}/${cardId}`,
        }, { apiName: this.apiName, ...config });
        this.getCharge = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/tap-payment-method/charge/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getListCustomerCards = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/tap-payment-method/customer-cards',
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/tap-payment-method/options',
        }, { apiName: this.apiName, ...config });
        this.getRefund = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/tap-payment-method/refund/${id}`,
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/tap-payment-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
TapPaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TapPaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
TapPaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TapPaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TapPaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var TapType;
(function (TapType) {
    TapType[TapType["KNet"] = 1] = "KNet";
    TapType[TapType["Card"] = 2] = "Card";
})(TapType || (TapType = {}));
const tapTypeOptions = mapEnumToOptions$1(TapType);

var TdspCardBrand;
(function (TdspCardBrand) {
    TdspCardBrand[TdspCardBrand["Visa"] = 0] = "Visa";
    TdspCardBrand[TdspCardBrand["Mastercard"] = 1] = "Mastercard";
    TdspCardBrand[TdspCardBrand["Numo"] = 2] = "Numo";
    TdspCardBrand[TdspCardBrand["Watani"] = 3] = "Watani";
    TdspCardBrand[TdspCardBrand["Travel"] = 4] = "Travel";
    TdspCardBrand[TdspCardBrand["TadawulCard"] = 5] = "TadawulCard";
    TdspCardBrand[TdspCardBrand["MoamalatCard"] = 6] = "MoamalatCard";
    TdspCardBrand[TdspCardBrand["Sadad"] = 7] = "Sadad";
    TdspCardBrand[TdspCardBrand["EdfaLCard"] = 8] = "EdfaLCard";
})(TdspCardBrand || (TdspCardBrand = {}));
const tdspCardBrandOptions = mapEnumToOptions$1(TdspCardBrand);

class TdspPaymentMethodService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.createPayment = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/tdsp-payment-method/payment',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.getOptions = (config) => this.restService.request({
            method: 'GET',
            url: '/api/app/tdsp-payment-method/options',
        }, { apiName: this.apiName, ...config });
        this.getPayment = (customReference, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/tdsp-payment-method/payment',
            params: { customReference },
        }, { apiName: this.apiName, ...config });
        this.updateOptions = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/tdsp-payment-method/options',
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
TdspPaymentMethodService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TdspPaymentMethodService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
TdspPaymentMethodService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TdspPaymentMethodService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: TdspPaymentMethodService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

var TdspType;
(function (TdspType) {
    TdspType[TdspType["Moamalat"] = 0] = "Moamalat";
    TdspType[TdspType["Tadauwel"] = 1] = "Tadauwel";
    TdspType[TdspType["Almadar"] = 2] = "Almadar";
    TdspType[TdspType["EdfaL"] = 3] = "EdfaL";
    TdspType[TdspType["MobiCash"] = 4] = "MobiCash";
    TdspType[TdspType["Card"] = 5] = "Card";
})(TdspType || (TdspType = {}));
const tdspTypeOptions = mapEnumToOptions$1(TdspType);

var ExchangeRateType;
(function (ExchangeRateType) {
    ExchangeRateType[ExchangeRateType["Manual"] = 0] = "Manual";
    ExchangeRateType[ExchangeRateType["Provider"] = 1] = "Provider";
})(ExchangeRateType || (ExchangeRateType = {}));
const exchangeRateTypeOptions = mapEnumToOptions$1(ExchangeRateType);

class ExchangeRateService {
    constructor(restService) {
        this.restService = restService;
        this.apiName = 'Default';
        this.create = (input, config) => this.restService.request({
            method: 'POST',
            url: '/api/app/exchange-rate',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.delete = (id, config) => this.restService.request({
            method: 'DELETE',
            url: `/api/app/exchange-rate/${id}`,
        }, { apiName: this.apiName, ...config });
        this.get = (id, config) => this.restService.request({
            method: 'GET',
            url: `/api/app/exchange-rate/${id}`,
        }, { apiName: this.apiName, ...config });
        this.getHistory = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/exchange-rate/history',
            params: { exchangeRateId: input.exchangeRateId, fromDate: input.fromDate, toDate: input.toDate, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.getList = (input, config) => this.restService.request({
            method: 'GET',
            url: '/api/app/exchange-rate',
            params: { disabled: input.disabled, sorting: input.sorting, skipCount: input.skipCount, maxResultCount: input.maxResultCount },
        }, { apiName: this.apiName, ...config });
        this.putBulk = (input, config) => this.restService.request({
            method: 'PUT',
            url: '/api/app/exchange-rate/bulk',
            body: input,
        }, { apiName: this.apiName, ...config });
        this.putDisable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/exchange-rate/${id}/disable`,
        }, { apiName: this.apiName, ...config });
        this.putEnable = (id, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/exchange-rate/${id}/enable`,
        }, { apiName: this.apiName, ...config });
        this.update = (id, input, config) => this.restService.request({
            method: 'PUT',
            url: `/api/app/exchange-rate/${id}`,
            body: input,
        }, { apiName: this.apiName, ...config });
    }
}
ExchangeRateService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExchangeRateService, deps: [{ token: i1.RestService }], target: i0.ɵɵFactoryTarget.Injectable });
ExchangeRateService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExchangeRateService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: ExchangeRateService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i1.RestService }]; } });

class RestService {
    constructor(environment, http) {
        this.environment = environment;
        this.http = http;
    }
    handleError(err) {
        return throwError(err);
    }
    getApiFromStore(apiName) {
        return this.environment.getApiUrl(apiName);
    }
    request(request, config, api) {
        config = config || {};
        api = api || this.getApiFromStore(config.apiName);
        const { method, params } = request;
        const { skipHandleError } = config;
        return this.http
            .request(method, api + request.url, {
            body: request.body,
            headers: request.headers,
            ...(params && {
                params: Object.keys(params).reduce((acc, key) => {
                    const value = params[key];
                    if (isUndefinedOrEmptyString(value))
                        return acc;
                    if (value === null && !this.sendNullsAsQueryParam)
                        return acc;
                    acc[key] = value;
                    return acc;
                }, {}),
            }),
        })
            .pipe(catchError(err => (skipHandleError ? throwError(err) : this.handleError(err))));
    }
}
RestService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RestService, deps: [{ token: EnvironmentService }, { token: i2.HttpClient }], target: i0.ɵɵFactoryTarget.Injectable });
RestService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RestService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: RestService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: EnvironmentService }, { type: i2.HttpClient }]; } });

function getPathName(url) {
    const { pathname } = new URL(url, window.location.origin);
    return pathname;
}
class WebHttpUrlEncodingCodec {
    encodeKey(k) {
        return encodeURIComponent(k);
    }
    encodeValue(v) {
        return encodeURIComponent(v);
    }
    decodeKey(k) {
        return decodeURIComponent(k);
    }
    decodeValue(v) {
        return decodeURIComponent(v);
    }
}

class HttpWaitService {
    constructor(injector) {
        this.store = new InternalStore({
            requests: [],
            filteredRequests: [],
        });
        this.destroy$ = new Subject();
        this.delay = injector.get(LOADER_DELAY, 500);
    }
    getLoading() {
        return !!this.applyFilter(this.store.state.requests).length;
    }
    getLoading$() {
        return this.store
            .sliceState(({ requests }) => requests)
            .pipe(map(requests => !!this.applyFilter(requests).length), switchMap(condition => condition
            ? this.delay === 0
                ? of(true)
                : timer(this.delay).pipe(mapTo(true), takeUntil(this.destroy$))
            : of(false)), tap(() => this.destroy$.next()));
    }
    updateLoading$() {
        return this.store.sliceUpdate(({ requests }) => !!this.applyFilter(requests).length);
    }
    clearLoading() {
        this.store.patch({ requests: [] });
    }
    addRequest(request) {
        this.store.patch({ requests: [...this.store.state.requests, request] });
    }
    deleteRequest(request) {
        const requests = this.store.state.requests.filter(r => r !== request);
        this.store.patch({ requests });
    }
    addFilter(request) {
        const requests = Array.isArray(request) ? request : [request];
        const filteredRequests = [
            ...this.store.state.filteredRequests.filter(f => !requests.some(r => this.isSameRequest(f, r))),
            ...requests,
        ];
        this.store.patch({ filteredRequests });
    }
    removeFilter(request) {
        const requests = Array.isArray(request) ? request : [request];
        const filteredRequests = this.store.state.filteredRequests.filter(f => !requests.some(r => this.isSameRequest(f, r)));
        this.store.patch({ filteredRequests });
    }
    applyFilter(requests) {
        if (!requests) {
            return [];
        }
        const { filteredRequests } = this.store.state;
        return requests.filter(({ method, url }) => !filteredRequests.find(filteredRequest => this.isSameRequest(filteredRequest, { method, endpoint: getPathName(url) })));
    }
    isSameRequest(filteredRequest, request) {
        const { method, endpoint } = filteredRequest;
        return endpoint === request.endpoint && method === request.method;
    }
}
HttpWaitService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: HttpWaitService, deps: [{ token: i0.Injector }], target: i0.ɵɵFactoryTarget.Injectable });
HttpWaitService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: HttpWaitService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "15.2.10", ngImport: i0, type: HttpWaitService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: i0.Injector }]; } });

function isNumber(value) {
    return value == Number(value);
}

function mapEnumToOptions(_enum) {
    const options = [];
    for (const member in _enum)
        if (!isNumber(member))
            options.push({
                key: member,
                value: _enum[member],
            });
    return options;
}

/*
 * Public API Surface of api
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AccountService, AddressService, AddressTarget, AddressType, AuditLogService, BannerPosition, BannerService, BannerTarget, BranchService, BrandService, CORE_OPTIONS, CartLineModifierStatus, CartLineStatus, CartMoreItemsOption, CartService, CartStatus, CashRoundingMethod, CategoriesLayoutColumnSizeOnHome, CategoriesLayoutHeighSizeOnHome, CategoryPadding, CategoryService, CommissionType, ConfigService, CountryIso2Code, CountryIso3Code, CountryService, CouponIncludedDetailType, CouponService, CouponType, CurrencyIsoCode, CurrencyPosition, CurrencyService, CustomerBusinessTypeService, CustomerFavoriteItemService, CustomerGroupService, CustomerService, CustomerStoreReviewService, CustomerType, CustomizedThemeComponentService, CustomizedThemeMenuItemService, CustomizedThemeService, DeliveryMethodCoveredAreaLimitation, DeliveryMethodProvider, DeliveryMethodRateType, DeliveryMethodService, DeliveryType, DhlExpressDeliveryMethodService, EnvironmentService, ExchangeRateService, ExchangeRateType, ExternalServiceCategory, ExternalServiceService, FeaturesHomePosition, FeaturesSectionDesign, FeedService, FirebaseCloudMessagingClientStatus, FirebaseCloudMessagingService, Gender, GeoAreaFieldType, GeoAreaService, GeoAreaType, GroupByOptions, HttpWaitService, IconPosition, IconStyle, ImageService, ImageTarget, IndustryService, InternalStore, InventoryService, InventoryType, InvoiceFileService, InvoicePaymentStatus, InvoiceService, InvoiceStatus, InvoiceTypeMode, InvoiceTypeService, ItemPosService, ItemRedisService, ItemReviewHelpfulnessService, ItemReviewService, ItemService, ItemStoreReviewService, ItemStoreService, IyzicoCardBrand, IyzicoPaymentMethodService, IyzicoType, KosmoDeliveryMethodService, KosmoType, LOADER_DELAY, LoyaltyProgramActionType, LoyaltyProgramEarnedPointsMethod, LoyaltyProgramPointActionType, LoyaltyProgramPointService, LoyaltyProgramService, ModifierListService, ModifierService, MontypayCardBrand, MontypayPaymentMethodService, MontypayType, MyFatoorahPaymentMethodService, MyFatoorahType, MyfatoorahCardBrand, NewsletterHomePosition, NoStockStatus, NotificationAudience, NotificationCustomersFilter, NotificationService, NotificationTarget, NotificationType, OpenSelectOutletDialogOptions, OrderLineStatus, OrderNumberGenerateMethod, OrderPaymentStatus, OrderReviewStatus, OrderService, OrderStatus, OtoDeliveryMethodService, OtoType, OutletSectionService, OutletSectionStatus, OutletService, OutletStatus, OutletTableService, OutletTableStatus, PackageScaleService, PackageService, PayPalPaymentIntentType, PayPalPaymentMethodService, PayPalType, PaymentCategoryService, PaymentMethodProvider, PaymentMethodService, PaymentMode, PaymentService, PaymentStatus, PaymentTermService, PaymentTermType, PointOfSaleService, PointOfSaleStatus, PriceListService, PrintTemplateService, PrintTemplateType, QiCardBrand, QiCardPaymentMethodService, QiCardType, ReferenceType, RestService, SaasActionBy, SaasActionReason, SaasAddonService, SaasCouponService, SaasCouponType, SaasDiscountType, SaasEditionService, SaasIntervalUnit, SaasInvoiceService, SaasInvoiceStatus, SaasOrderService, SaasOrderStatus, SaasOrderType, SaasPaymentMethodService, SaasPaymentService, SaasPaypalPaymentMethodService, SaasProductService, SaasStripePaymentMethodService, SaasSubscriptionService, SaasSubscriptionStatus, SaifaloPaymentMethodService, SaifaloType, SaleType, SalesChannelService, SalesChannelStatus, SalesChannels, SettingsService, ShipmentService, ShippingStatus, StockQuantityHistoryActionType, StorePageService, StorePageStatus, StorePageType, StoreReviewQuestionService, StoreReviewQuestionType, StoreReviewService, StoreService, StoreStatus, StoreSupportedCountriesType, StoreTemplateService, StoreTemplateType, StorefrontService, StripeCardBrand, StripePaymentMethodService, StripeType, SubscriptionExpiryAction, SubtotalType, TajerApiModule, TapCardBrand, TapPaymentMethodService, TapType, TaxAddOption, TaxService, TaxType, TdspCardBrand, TdspPaymentMethodService, TdspType, ThemeComponentCategoryService, ThemeComponentDependencyService, ThemeComponentPropertyService, ThemeComponentSectionService, ThemeComponentService, ThemeDataType, ThemeMenuItemOptionService, ThemeMenuItemService, ThemeMenuItemType, ThemeOptionType, ThemeService, index$1 as Themes, TransactionType, TransferOrderService, TransferOrderStatus, TwilioVerificationChannels, UnitService, UserService, UserType, VerificationService, VerificationStageService, VerificationType, VerificationValueService, index as Verifications, VerifyService, WarehouseService, WebHttpUrlEncodingCodec, addressTargetOptions, addressTypeOptions, bannerPositionOptions, bannerTargetOptions, cartLineModifierStatusOptions, cartLineStatusOptions, cartMoreItemsOptionOptions, cartStatusOptions, cashRoundingMethodOptions, categoriesLayoutColumnSizeOnHomeOptions, categoriesLayoutHeighSizeOnHomeOptions, categoryPaddingOptions, commissionTypeOptions, coreOptionsFactory, countryIso2CodeOptions, countryIso3CodeOptions, couponIncludedDetailTypeOptions, couponTypeOptions, currencyIsoCodeOptions, currencyPositionOptions, customerTypeOptions, deepMerge, deliveryMethodCoveredAreaLimitationOptions, deliveryMethodProviderOptions, deliveryMethodRateTypeOptions, deliveryTypeOptions, exchangeRateTypeOptions, exists, externalServiceCategoryOptions, featuresHomePositionOptions, featuresSectionDesignOptions, firebaseCloudMessagingClientStatusOptions, genderOptions, geoAreaFieldTypeOptions, geoAreaTypeOptions, getPathName, groupByOptionsOptions, iconPositionOptions, iconStyleOptions, imageTargetOptions, inventoryTypeOptions, invoicePaymentStatusOptions, invoiceStatusOptions, invoiceTypeModeOptions, isArray, isNode, isNullOrUndefined, isNumber, isObject, isObjectAndNotArray, isObjectAndNotArrayNotNode, isUndefinedOrEmptyString, iyzicoCardBrandOptions, iyzicoTypeOptions, kosmoTypeOptions, loyaltyProgramActionTypeOptions, loyaltyProgramEarnedPointsMethodOptions, loyaltyProgramPointActionTypeOptions, mapEnumToOptions, montypayCardBrandOptions, montypayTypeOptions, myFatoorahTypeOptions, myfatoorahCardBrandOptions, newsletterHomePositionOptions, noStockStatusOptions, noop, notificationAudienceOptions, notificationCustomersFilterOptions, notificationTargetOptions, notificationTypeOptions, openSelectOutletDialogOptionsOptions, orderLineStatusOptions, orderNumberGenerateMethodOptions, orderPaymentStatusOptions, orderReviewStatusOptions, orderStatusOptions, otoTypeOptions, outletSectionStatusOptions, outletStatusOptions, outletTableStatusOptions, payPalPaymentIntentTypeOptions, payPalTypeOptions, paymentMethodProviderOptions, paymentModeOptions, paymentStatusOptions, paymentTermTypeOptions, pointOfSaleStatusOptions, printTemplateTypeOptions, qiCardBrandOptions, qiCardTypeOptions, referenceTypeOptions, saasActionByOptions, saasActionReasonOptions, saasCouponTypeOptions, saasDiscountTypeOptions, saasIntervalUnitOptions, saasInvoiceStatusOptions, saasOrderStatusOptions, saasOrderTypeOptions, saasSubscriptionStatusOptions, saifaloTypeOptions, saleTypeOptions, salesChannelStatusOptions, salesChannelsOptions, shippingStatusOptions, stockQuantityHistoryActionTypeOptions, storePageStatusOptions, storePageTypeOptions, storeReviewQuestionTypeOptions, storeStatusOptions, storeSupportedCountriesTypeOptions, storeTemplateTypeOptions, stripeCardBrandOptions, stripeTypeOptions, subscriptionExpiryActionOptions, subtotalTypeOptions, tapCardBrandOptions, tapTypeOptions, taxAddOptionOptions, taxTypeOptions, tdspCardBrandOptions, tdspTypeOptions, themeDataTypeOptions, themeMenuItemTypeOptions, themeOptionTypeOptions, transactionTypeOptions, transferOrderStatusOptions, twilioVerificationChannelsOptions, userTypeOptions, verificationTypeOptions };
